import PropTypes from 'prop-types';
import React, { memo, useState, useEffect, useCallback, useReducer, useRef, useMemo } from 'react';
import { useTranslation, I18nextProvider } from 'react-i18next';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import take from 'lodash/take';
import isString$1 from 'lodash/isString';
import orderBy from 'lodash/orderBy';
import uniqWith from 'lodash/uniqWith';
import get from 'lodash/get';
import { useCombobox } from 'downshift';
import Icon, { defaultColors } from '@digitransit-component/digitransit-component-icon';
import { createInstance as createInstance$1 } from 'i18next';
import 'lodash/isEqual';
import memoize from 'lodash/memoize';
import escapeRegExp from 'lodash/escapeRegExp';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import ReactModal from 'react-modal';
import DialogModal from '@digitransit-component/digitransit-component-dialog-modal';
import SuggestionItem from '@digitransit-component/digitransit-component-suggestion-item';

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r);
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function toPropertyKey(t) {
  var i = toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: true,
    configurable: true,
    writable: true
  }) : e[r] = t, e;
}

/**
 *  accept equality of non nullish values
 *
 * @name truEq
 * @param {Boolean|Number|BigInt|String|Symbol|Object} val1 First value to be compared
 * @param {Boolean|Number|BigInt|String|Symbol|Object} param2 Second value to be compared
 * @returns {Boolean} true/false
 * @example
 * digitransit-util.truEq('2', '2');
 * //=true
 */
function truEq(val1, val2) {
  return val1 && val2 && val1 === val2;
}

/**
 * Checks that is items duplicate
 *
 * @name isDuplicate
 * @param {Object} item1 Object containing following attributes:
 * {
 *  properties {
 *      gtfsId: String
 *      gid: String from geocoder
 *      name: String
 *      label: String
 *       address: String
 *      geometry {
 *         coordinates [Number lat, Number lon]
 *      }
 *  }
 * }
 * @param {Object} item2 Object with same attributes as item1
 * @returns {Boolean} true/false
 * @example
 * digitransit-util.isDuplicate(param1, param2);
 * //=true
 */

const gtfsStopLayers = ['stop', 'station', 'favouriteStop', 'favouriteStation'];
const vehicleRentalLayers = ['favouriteVehicleRentalStation', 'bikestation'];
function labelIdToGid(id) {
  if (!id) {
    return '';
  }
  const parts = id.split(':');
  if (parts.length === 0) {
    return '';
  }
  return `citybikes${parts[0]}:bikestation:${parts[1]}`;
}
function isDuplicate(item1, item2) {
  const props1 = item1.properties;
  const props2 = item2.properties;
  if (item1.type === 'FutureRoute' && item2.type === 'FutureRoute') {
    const o1 = props1.origin;
    const d1 = props1.destination;
    const o2 = props2.origin;
    const d2 = props2.destination;
    const name1 = `${o1.name}//${o1.localadmin}//${d1.name}//${d1.localadmin}`;
    const name2 = `${o2.name}//${o2.localadmin}//${d2.name}//${d2.localadmin}`;
    return name1 === name2;
  }
  if (item1.type === 'FutureRoute' || item2.type === 'FutureRoute') {
    return false;
  }
  if (vehicleRentalLayers.includes(props1.layer) && vehicleRentalLayers.includes(props2.layer)) {
    const id1 = props1.gid || labelIdToGid(props1.labelId);
    const id2 = props2.gid || labelIdToGid(props2.labelId);
    return id1 === id2;
  }
  if (props1.gtfsId && props2.gtfsId) {
    return props1.gtfsId === props2.gtfsId;
  }
  if (gtfsStopLayers.includes(props1.layer) && gtfsStopLayers.includes(props2.layer) && props1.gid && props2.gid) {
    return props1.gid === props2.gid;
  }
  const p1 = item1 && item1.geometry ? item1.geometry.coordinates : undefined;
  const p2 = item2 && item2.geometry ? item2.geometry.coordinates : undefined;
  if (p1 && p2) {
    // both have geometry
    if (Math.abs(p1[0] - p2[0]) < 1e-6 && Math.abs(p1[1] - p2[1]) < 1e-6) {
      // location match is not enough. Require a common property
      if (truEq(props1.name, props2.name) || truEq(props1.label, props2.label) || truEq(props1.address, props2.address) || truEq(props1.address, props2.label) || truEq(props1.label, props2.address)) {
        return true;
      }
    }
  }
  return false;
}

const normalize = str => {
  if (!isString$1(str)) {
    return '';
  }
  return str.toLowerCase();
};
const splitShortNameAtLetters = str => {
  return str.split(/(\p{L}+)/u);
};
const testShortNameMatchesLineWithNumbersAndLetters = str => {
  return /^[0-9]+\p{L}+$/u.test(str);
};

/**
 * LayerType depicts the type of the point-of-interest.
 */
const LayerType = {
  Back: 'back',
  CurrentPosition: 'currentPosition',
  FavouriteStop: 'favouriteStop',
  FavouriteStation: 'favouriteStation',
  FavouritePlace: 'favouritePlace',
  FavouriteRoute: 'favouriteRoute',
  FavouriteVehicleRentalStation: 'favouriteVehicleRentalStation',
  FutureRoute: 'futureRoute',
  Station: 'station',
  SelectFromMap: 'selectFromMap',
  SelectFromOwnLocations: 'ownLocations',
  Stop: 'stop',
  VehicleRentalStation: 'bikestation',
  CarPark: 'carpark',
  BikePark: 'bikepark'
};
const isStop = ({
  layer,
  type
}) => layer === 'stop' || layer === 'favouriteStop' || type === 'stop' || type === 'favouriteStop';

/**
 * Tries to match the given search term agains the collection of properties
 * for a geocoding result (min: 0, max: 1.5). The best match will return a number of 1.5.
 * Worse matches will return 0 or 0.5.
 *
 * @param {string} normalizedTerm the normalized search term.
 * @param {*} resultProperties the geocoding result's property collection.
 */
const match = (normalizedTerm, resultProperties) => {
  if (!isString$1(normalizedTerm) || normalizedTerm.length === 0) {
    return 0;
  }
  const matchProps = ['name', 'label', 'address', 'shortName'];
  return matchProps.map(name => {
    return {
      name,
      value: resultProperties[name]
    };
  }).filter(element => isString$1(element.value) && element.value.length > 0).map(element => {
    let normalizedValue = normalize(element.value);
    if (normalizedValue.indexOf(normalizedTerm) === 0) {
      if (element.name === 'shortName' && testShortNameMatchesLineWithNumbersAndLetters(normalizedValue)) {
        // If the shortName is of a certain format (e.g. 89A), it is treated here
        // as if it had the length of the numbers with the letters removed.
        // So the length of 89A would be treated as 2.
        [normalizedValue] = splitShortNameAtLetters(normalizedValue);
      }
      // full match at start. Return max result when match is full, not only partial
      return 0.5 + normalizedTerm.length / normalizedValue.length;
    }
    // because of filtermatchingtoinput, we know that match occurred somewhere
    // don't run filtermatching again but estimate roughly:
    // the longer the matching string, the better confidence, max being 0.5
    return 0.5 * normalizedTerm.length / (normalizedTerm.length + 1);
  }).reduce((previous, current) => current > previous ? current : previous, 0);
};

/**
 * Ranks the result based on its layer property.
 *
 * @param {string} layer the layer property.
 * @param {string} source the source property.
 */
const getLayerRank = (layer, source) => {
  switch (layer) {
    case LayerType.CurrentPosition:
      return 1;
    case LayerType.Back:
      return 0.9;
    case LayerType.SelectFromMap:
      return 0.8;
    case LayerType.SelectFromOwnLocations:
      return 0.79;
    case LayerType.FavouriteStation:
    case LayerType.FavouritePlace:
    case LayerType.FavouriteStop:
    case LayerType.FavouriteRoute:
    case LayerType.FavouriteVehicleRentalStation:
      return 0.45;
    case LayerType.FutureRoute:
      return 0.44;
    case LayerType.Station:
      {
        if (isString$1(source) && source.indexOf('gtfs') === 0) {
          return 0.43;
        }
        return 0.42;
      }
    case LayerType.CarPark:
      return 0.38;
    case LayerType.BikePark:
      return 0.38;
    case LayerType.VehicleRentalStation:
      return 0.38;
    case LayerType.Stop:
      return 0.36;
    default:
      // venue, address, street, route-xxx
      return 0.41;
  }
};

/**
 * Helper function to sort the results. Orders as follows:
 *  - current position first for an empty search
 *  - matching routes first
 *  - otherwise by confidence, except that:
 *    - boost well matching stations (especially from GTFS)
 *    - rank stops lower as they tend to occupy most of the search results
 *  - items with no confidence (old searches and favorites):
 *    - rank favourites better than ordinary old searches
 *    - rank full match better than partial match
 *    - rank match at middle word lower than match at the beginning
 *    - rank bike rental stations lower
 * @param {*[]} results The search results that were received
 * @param {String} term The search term that was used
 */
const sortSearchResults = (lineRegexp, results, term = '') => {
  if (!Array.isArray(results) || !results.length) {
    return results;
  }
  const isLineIdentifier = value => isString$1(value) && lineRegexp && lineRegexp.test(value);
  const normalizedTerm = normalize(term);
  const isLineSearch = isLineIdentifier(normalizedTerm);
  const orderedResults = orderBy(results.filter(r => r), [
  // rank matching routes best
  result => isLineSearch && isLineIdentifier(normalize(result.properties.shortName)) && normalize(result.properties.shortName).indexOf(normalizedTerm) === 0 ? 1 : 0, result => {
    const {
      confidence,
      layer,
      source
    } = result.properties;
    if (normalizedTerm.length === 0) {
      // Doing search with empty string.
      // No confidence to match, so use ranked old searches and favourites
      return getLayerRank(layer, source);
    }

    // must handle a mixup of geocoder searches and items above
    // Normal confidence range from geocoder is about 0.3 .. 1
    if (!confidence) {
      // not from geocoder, estimate confidence ourselves
      return getLayerRank(layer, source) + match(normalizedTerm, result.properties);
    }
    // geocoded items with confidence, just adjust a little
    switch (layer) {
      case LayerType.Station:
        {
          const boost = source.indexOf('gtfs') === 0 ? 0.02 : 0.01;
          return confidence + boost;
        }
      case LayerType.Stop:
        return confidence - 0.05;
      case LayerType.CarPark:
        return confidence - 0.05;
      case LayerType.BikePark:
        return confidence - 0.05;
      case LayerType.VehicleRentalStation:
        return confidence - 0.04;
      default:
        return confidence;
    }
  }, result => {
    if (result.properties.shortName) {
      return testShortNameMatchesLineWithNumbersAndLetters(result.properties.shortName) ? splitShortNameAtLetters(result.properties.shortName)[0] : result.properties.shortName;
    }
    return undefined;
  }, result => {
    if (result.properties.shortName) {
      return testShortNameMatchesLineWithNumbersAndLetters(result.properties.shortName) ? splitShortNameAtLetters(result.properties.shortName)[1] : result.properties.shortName;
    }
    return undefined;
  }, 'properties.longName'], ['desc', 'desc', 'asc', 'asc', 'asc']);
  return uniqWith(orderedResults, isDuplicate);
};

/**
 * Parses stop's name without stop code from a stop name from geocoding results
 *
 * @param {string} label stop's name from geocoding results.
 * @param {string} stopCode stop code.
 */
const getStopName = (name, stopCode) => {
  if (stopCode !== undefined && stopCode !== null && name.lastIndexOf(stopCode) !== -1) {
    return name.substring(0, name.lastIndexOf(stopCode) - 1);
  }
  return name;
};

/**
 * Function that filters list matching to input.
 *
 * @name filterMatchingToInput
 * @param {Array} list List containin objects e.g(  {
 *         type: 'Feature'
 *         properties: {
 *           label: 'testaddress4',
 *           layer: 'address',
 *           name: 'testaddress4',
 *         },
 *         geometry: { coordinates: [lon, lat] },
 *       },)
 * @param {String} Input Input given for filtering the list
 * @param {Array} fields List of fields that list is filtered by e.g ([properties.label, properties.name])
 * @returns {Array} Filtered Array.
 * @example
 * digitransit-util.filterMatchingToInput([{type: feature, properties { ... name: testaddress4 } ... }], 'testaddress4', 'properties.name');
 * //= List containing object with given name.
 */
function filterMatchingToInput(list, Input, fields) {
  if (typeof Input === 'string' && Input.length > 0) {
    const input = Input.toLowerCase().trim();
    const multiWord = input.includes(' ') || input.includes(',');
    return list.filter(item => {
      let parts = [];
      fields.forEach(pName => {
        let value = get(item, pName);
        if (!multiWord && (pName === 'properties.label' || pName === 'address') && value) {
          // special case: drop last parts i.e. city and neighbourhood
          value = value.split(',');
          if (value.length > 2) {
            value.splice(value.length - 2, 2);
          } else if (value.length > 1) {
            value.splice(value.length - 1, 1);
          }
          value = value.join(',');
        }
        if (value) {
          if (multiWord) {
            parts = parts.concat(value.toLowerCase());
          } else {
            parts = parts.concat(value.toLowerCase().replace(/,/g, ' ').split(' '));
          }
        }
      });
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(input) === 0) {
          // accept match only at word start
          return true;
        }
      }
      return false;
    });
  }
  return list;
}

/**
 * Create a bound version of a function with a specified `this` context
 *
 * @param {Function} fn - The function to bind
 * @param {*} thisArg - The value to be passed as the `this` parameter
 * @returns {Function} A new function that will call the original function with the specified `this` context
 */
function bind(fn, thisArg) {
  return function wrap() {
    return fn.apply(thisArg, arguments);
  };
}

// utils is a library of generic helper functions non-specific to axios

const { toString } = Object.prototype;
const { getPrototypeOf } = Object;
const { iterator, toStringTag } = Symbol;

const kindOf = ((cache) => (thing) => {
  const str = toString.call(thing);
  return cache[str] || (cache[str] = str.slice(8, -1).toLowerCase());
})(Object.create(null));

const kindOfTest = (type) => {
  type = type.toLowerCase();
  return (thing) => kindOf(thing) === type;
};

const typeOfTest = (type) => (thing) => typeof thing === type;

/**
 * Determine if a value is a non-null object
 *
 * @param {Object} val The value to test
 *
 * @returns {boolean} True if value is an Array, otherwise false
 */
const { isArray } = Array;

/**
 * Determine if a value is undefined
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if the value is undefined, otherwise false
 */
const isUndefined = typeOfTest('undefined');

/**
 * Determine if a value is a Buffer
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a Buffer, otherwise false
 */
function isBuffer(val) {
  return (
    val !== null &&
    !isUndefined(val) &&
    val.constructor !== null &&
    !isUndefined(val.constructor) &&
    isFunction$1(val.constructor.isBuffer) &&
    val.constructor.isBuffer(val)
  );
}

/**
 * Determine if a value is an ArrayBuffer
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is an ArrayBuffer, otherwise false
 */
const isArrayBuffer = kindOfTest('ArrayBuffer');

/**
 * Determine if a value is a view on an ArrayBuffer
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a view on an ArrayBuffer, otherwise false
 */
function isArrayBufferView(val) {
  let result;
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView) {
    result = ArrayBuffer.isView(val);
  } else {
    result = val && val.buffer && isArrayBuffer(val.buffer);
  }
  return result;
}

/**
 * Determine if a value is a String
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a String, otherwise false
 */
const isString = typeOfTest('string');

/**
 * Determine if a value is a Function
 *
 * @param {*} val The value to test
 * @returns {boolean} True if value is a Function, otherwise false
 */
const isFunction$1 = typeOfTest('function');

/**
 * Determine if a value is a Number
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a Number, otherwise false
 */
const isNumber = typeOfTest('number');

/**
 * Determine if a value is an Object
 *
 * @param {*} thing The value to test
 *
 * @returns {boolean} True if value is an Object, otherwise false
 */
const isObject = (thing) => thing !== null && typeof thing === 'object';

/**
 * Determine if a value is a Boolean
 *
 * @param {*} thing The value to test
 * @returns {boolean} True if value is a Boolean, otherwise false
 */
const isBoolean = (thing) => thing === true || thing === false;

/**
 * Determine if a value is a plain Object
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a plain Object, otherwise false
 */
const isPlainObject = (val) => {
  if (kindOf(val) !== 'object') {
    return false;
  }

  const prototype = getPrototypeOf(val);
  return (
    (prototype === null ||
      prototype === Object.prototype ||
      Object.getPrototypeOf(prototype) === null) &&
    !(toStringTag in val) &&
    !(iterator in val)
  );
};

/**
 * Determine if a value is an empty object (safely handles Buffers)
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is an empty object, otherwise false
 */
const isEmptyObject = (val) => {
  // Early return for non-objects or Buffers to prevent RangeError
  if (!isObject(val) || isBuffer(val)) {
    return false;
  }

  try {
    return Object.keys(val).length === 0 && Object.getPrototypeOf(val) === Object.prototype;
  } catch (e) {
    // Fallback for any other objects that might cause RangeError with Object.keys()
    return false;
  }
};

/**
 * Determine if a value is a Date
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a Date, otherwise false
 */
const isDate = kindOfTest('Date');

/**
 * Determine if a value is a File
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a File, otherwise false
 */
const isFile = kindOfTest('File');

/**
 * Determine if a value is a React Native Blob
 * React Native "blob": an object with a `uri` attribute. Optionally, it can
 * also have a `name` and `type` attribute to specify filename and content type
 *
 * @see https://github.com/facebook/react-native/blob/26684cf3adf4094eb6c405d345a75bf8c7c0bf88/Libraries/Network/FormData.js#L68-L71
 *
 * @param {*} value The value to test
 *
 * @returns {boolean} True if value is a React Native Blob, otherwise false
 */
const isReactNativeBlob = (value) => {
  return !!(value && typeof value.uri !== 'undefined');
};

/**
 * Determine if environment is React Native
 * ReactNative `FormData` has a non-standard `getParts()` method
 *
 * @param {*} formData The formData to test
 *
 * @returns {boolean} True if environment is React Native, otherwise false
 */
const isReactNative = (formData) => formData && typeof formData.getParts !== 'undefined';

/**
 * Determine if a value is a Blob
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a Blob, otherwise false
 */
const isBlob = kindOfTest('Blob');

/**
 * Determine if a value is a FileList
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a FileList, otherwise false
 */
const isFileList = kindOfTest('FileList');

/**
 * Determine if a value is a Stream
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a Stream, otherwise false
 */
const isStream = (val) => isObject(val) && isFunction$1(val.pipe);

/**
 * Determine if a value is a FormData
 *
 * @param {*} thing The value to test
 *
 * @returns {boolean} True if value is an FormData, otherwise false
 */
function getGlobal() {
  if (typeof globalThis !== 'undefined') return globalThis;
  if (typeof self !== 'undefined') return self;
  if (typeof window !== 'undefined') return window;
  if (typeof global !== 'undefined') return global;
  return {};
}

const G = getGlobal();
const FormDataCtor = typeof G.FormData !== 'undefined' ? G.FormData : undefined;

const isFormData = (thing) => {
  if (!thing) return false;
  if (FormDataCtor && thing instanceof FormDataCtor) return true;
  // Reject plain objects inheriting directly from Object.prototype so prototype-pollution gadgets can't spoof FormData.
  const proto = getPrototypeOf(thing);
  if (!proto || proto === Object.prototype) return false;
  if (!isFunction$1(thing.append)) return false;
  const kind = kindOf(thing);
  return (
    kind === 'formdata' ||
    // detect form-data instance
    (kind === 'object' && isFunction$1(thing.toString) && thing.toString() === '[object FormData]')
  );
};

/**
 * Determine if a value is a URLSearchParams object
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a URLSearchParams object, otherwise false
 */
const isURLSearchParams = kindOfTest('URLSearchParams');

const [isReadableStream, isRequest, isResponse, isHeaders] = [
  'ReadableStream',
  'Request',
  'Response',
  'Headers',
].map(kindOfTest);

/**
 * Trim excess whitespace off the beginning and end of a string
 *
 * @param {String} str The String to trim
 *
 * @returns {String} The String freed of excess whitespace
 */
const trim = (str) => {
  return str.trim ? str.trim() : str.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '');
};
/**
 * Iterate over an Array or an Object invoking a function for each item.
 *
 * If `obj` is an Array callback will be called passing
 * the value, index, and complete array for each item.
 *
 * If 'obj' is an Object callback will be called passing
 * the value, key, and complete object for each property.
 *
 * @param {Object|Array<unknown>} obj The object to iterate
 * @param {Function} fn The callback to invoke for each item
 *
 * @param {Object} [options]
 * @param {Boolean} [options.allOwnKeys = false]
 * @returns {any}
 */
function forEach(obj, fn, { allOwnKeys = false } = {}) {
  // Don't bother if no value provided
  if (obj === null || typeof obj === 'undefined') {
    return;
  }

  let i;
  let l;

  // Force an array if not already something iterable
  if (typeof obj !== 'object') {
    /*eslint no-param-reassign:0*/
    obj = [obj];
  }

  if (isArray(obj)) {
    // Iterate over array values
    for (i = 0, l = obj.length; i < l; i++) {
      fn.call(null, obj[i], i, obj);
    }
  } else {
    // Buffer check
    if (isBuffer(obj)) {
      return;
    }

    // Iterate over object keys
    const keys = allOwnKeys ? Object.getOwnPropertyNames(obj) : Object.keys(obj);
    const len = keys.length;
    let key;

    for (i = 0; i < len; i++) {
      key = keys[i];
      fn.call(null, obj[key], key, obj);
    }
  }
}

/**
 * Finds a key in an object, case-insensitive, returning the actual key name.
 * Returns null if the object is a Buffer or if no match is found.
 *
 * @param {Object} obj - The object to search.
 * @param {string} key - The key to find (case-insensitive).
 * @returns {?string} The actual key name if found, otherwise null.
 */
function findKey(obj, key) {
  if (isBuffer(obj)) {
    return null;
  }

  key = key.toLowerCase();
  const keys = Object.keys(obj);
  let i = keys.length;
  let _key;
  while (i-- > 0) {
    _key = keys[i];
    if (key === _key.toLowerCase()) {
      return _key;
    }
  }
  return null;
}

const _global = (() => {
  /*eslint no-undef:0*/
  if (typeof globalThis !== 'undefined') return globalThis;
  return typeof self !== 'undefined' ? self : typeof window !== 'undefined' ? window : global;
})();

const isContextDefined = (context) => !isUndefined(context) && context !== _global;

/**
 * Accepts varargs expecting each argument to be an object, then
 * immutably merges the properties of each object and returns result.
 *
 * When multiple objects contain the same key the later object in
 * the arguments list will take precedence.
 *
 * Example:
 *
 * ```js
 * const result = merge({foo: 123}, {foo: 456});
 * console.log(result.foo); // outputs 456
 * ```
 *
 * @param {Object} obj1 Object to merge
 *
 * @returns {Object} Result of all merge properties
 */
function merge(...objs) {
  const { caseless, skipUndefined } = (isContextDefined(this) && this) || {};
  const result = {};
  const assignValue = (val, key) => {
    // Skip dangerous property names to prevent prototype pollution
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return;
    }

    // findKey lowercases the key, so caseless lookup only applies to strings —
    // symbol keys are identity-matched.
    const targetKey = (caseless && typeof key === 'string' && findKey(result, key)) || key;
    // Read via own-prop only — a bare `result[targetKey]` walks the prototype
    // chain, so a polluted Object.prototype value could surface here and get
    // copied into the merged result.
    const existing = hasOwnProperty(result, targetKey) ? result[targetKey] : undefined;
    if (isPlainObject(existing) && isPlainObject(val)) {
      result[targetKey] = merge(existing, val);
    } else if (isPlainObject(val)) {
      result[targetKey] = merge({}, val);
    } else if (isArray(val)) {
      result[targetKey] = val.slice();
    } else if (!skipUndefined || !isUndefined(val)) {
      result[targetKey] = val;
    }
  };

  for (let i = 0, l = objs.length; i < l; i++) {
    const source = objs[i];
    if (!source || isBuffer(source)) {
      continue;
    }

    forEach(source, assignValue);

    if (typeof source !== 'object' || isArray(source)) {
      continue;
    }

    const symbols = Object.getOwnPropertySymbols(source);
    for (let j = 0; j < symbols.length; j++) {
      const symbol = symbols[j];
      if (propertyIsEnumerable.call(source, symbol)) {
        assignValue(source[symbol], symbol);
      }
    }
  }
  return result;
}

/**
 * Extends object a by mutably adding to it the properties of object b.
 *
 * @param {Object} a The object to be extended
 * @param {Object} b The object to copy properties from
 * @param {Object} thisArg The object to bind function to
 *
 * @param {Object} [options]
 * @param {Boolean} [options.allOwnKeys]
 * @returns {Object} The resulting value of object a
 */
const extend = (a, b, thisArg, { allOwnKeys } = {}) => {
  forEach(
    b,
    (val, key) => {
      if (thisArg && isFunction$1(val)) {
        Object.defineProperty(a, key, {
          // Null-proto descriptor so a polluted Object.prototype.get cannot
          // hijack defineProperty's accessor-vs-data resolution.
          __proto__: null,
          value: bind(val, thisArg),
          writable: true,
          enumerable: true,
          configurable: true,
        });
      } else {
        Object.defineProperty(a, key, {
          __proto__: null,
          value: val,
          writable: true,
          enumerable: true,
          configurable: true,
        });
      }
    },
    { allOwnKeys }
  );
  return a;
};

/**
 * Remove byte order marker. This catches EF BB BF (the UTF-8 BOM)
 *
 * @param {string} content with BOM
 *
 * @returns {string} content value without BOM
 */
const stripBOM = (content) => {
  if (content.charCodeAt(0) === 0xfeff) {
    content = content.slice(1);
  }
  return content;
};

/**
 * Inherit the prototype methods from one constructor into another
 * @param {function} constructor
 * @param {function} superConstructor
 * @param {object} [props]
 * @param {object} [descriptors]
 *
 * @returns {void}
 */
const inherits = (constructor, superConstructor, props, descriptors) => {
  constructor.prototype = Object.create(superConstructor.prototype, descriptors);
  Object.defineProperty(constructor.prototype, 'constructor', {
    __proto__: null,
    value: constructor,
    writable: true,
    enumerable: false,
    configurable: true,
  });
  Object.defineProperty(constructor, 'super', {
    __proto__: null,
    value: superConstructor.prototype,
  });
  props && Object.assign(constructor.prototype, props);
};

/**
 * Resolve object with deep prototype chain to a flat object
 * @param {Object} sourceObj source object
 * @param {Object} [destObj]
 * @param {Function|Boolean} [filter]
 * @param {Function} [propFilter]
 *
 * @returns {Object}
 */
const toFlatObject = (sourceObj, destObj, filter, propFilter) => {
  let props;
  let i;
  let prop;
  const merged = {};

  destObj = destObj || {};
  // eslint-disable-next-line no-eq-null,eqeqeq
  if (sourceObj == null) return destObj;

  do {
    props = Object.getOwnPropertyNames(sourceObj);
    i = props.length;
    while (i-- > 0) {
      prop = props[i];
      if ((!propFilter || propFilter(prop, sourceObj, destObj)) && !merged[prop]) {
        destObj[prop] = sourceObj[prop];
        merged[prop] = true;
      }
    }
    sourceObj = filter !== false && getPrototypeOf(sourceObj);
  } while (sourceObj && (!filter || filter(sourceObj, destObj)) && sourceObj !== Object.prototype);

  return destObj;
};

/**
 * Determines whether a string ends with the characters of a specified string
 *
 * @param {String} str
 * @param {String} searchString
 * @param {Number} [position= 0]
 *
 * @returns {boolean}
 */
const endsWith = (str, searchString, position) => {
  str = String(str);
  if (position === undefined || position > str.length) {
    position = str.length;
  }
  position -= searchString.length;
  const lastIndex = str.indexOf(searchString, position);
  return lastIndex !== -1 && lastIndex === position;
};

/**
 * Returns new array from array like object or null if failed
 *
 * @param {*} [thing]
 *
 * @returns {?Array}
 */
const toArray = (thing) => {
  if (!thing) return null;
  if (isArray(thing)) return thing;
  let i = thing.length;
  if (!isNumber(i)) return null;
  const arr = new Array(i);
  while (i-- > 0) {
    arr[i] = thing[i];
  }
  return arr;
};

/**
 * Checking if the Uint8Array exists and if it does, it returns a function that checks if the
 * thing passed in is an instance of Uint8Array
 *
 * @param {TypedArray}
 *
 * @returns {Array}
 */
// eslint-disable-next-line func-names
const isTypedArray = ((TypedArray) => {
  // eslint-disable-next-line func-names
  return (thing) => {
    return TypedArray && thing instanceof TypedArray;
  };
})(typeof Uint8Array !== 'undefined' && getPrototypeOf(Uint8Array));

/**
 * For each entry in the object, call the function with the key and value.
 *
 * @param {Object<any, any>} obj - The object to iterate over.
 * @param {Function} fn - The function to call for each entry.
 *
 * @returns {void}
 */
const forEachEntry = (obj, fn) => {
  const generator = obj && obj[iterator];

  const _iterator = generator.call(obj);

  let result;

  while ((result = _iterator.next()) && !result.done) {
    const pair = result.value;
    fn.call(obj, pair[0], pair[1]);
  }
};

/**
 * It takes a regular expression and a string, and returns an array of all the matches
 *
 * @param {string} regExp - The regular expression to match against.
 * @param {string} str - The string to search.
 *
 * @returns {Array<boolean>}
 */
const matchAll = (regExp, str) => {
  let matches;
  const arr = [];

  while ((matches = regExp.exec(str)) !== null) {
    arr.push(matches);
  }

  return arr;
};

/* Checking if the kindOfTest function returns true when passed an HTMLFormElement. */
const isHTMLForm = kindOfTest('HTMLFormElement');

const toCamelCase = (str) => {
  return str.toLowerCase().replace(/[-_\s]([a-z\d])(\w*)/g, function replacer(m, p1, p2) {
    return p1.toUpperCase() + p2;
  });
};

/* Creating a function that will check if an object has a property. */
const hasOwnProperty = (
  ({ hasOwnProperty }) =>
  (obj, prop) =>
    hasOwnProperty.call(obj, prop)
)(Object.prototype);

const { propertyIsEnumerable } = Object.prototype;

/**
 * Determine if a value is a RegExp object
 *
 * @param {*} val The value to test
 *
 * @returns {boolean} True if value is a RegExp object, otherwise false
 */
const isRegExp = kindOfTest('RegExp');

const reduceDescriptors = (obj, reducer) => {
  const descriptors = Object.getOwnPropertyDescriptors(obj);
  const reducedDescriptors = {};

  forEach(descriptors, (descriptor, name) => {
    let ret;
    if ((ret = reducer(descriptor, name, obj)) !== false) {
      reducedDescriptors[name] = ret || descriptor;
    }
  });

  Object.defineProperties(obj, reducedDescriptors);
};

/**
 * Makes all methods read-only
 * @param {Object} obj
 */

const freezeMethods = (obj) => {
  reduceDescriptors(obj, (descriptor, name) => {
    // skip restricted props in strict mode
    if (isFunction$1(obj) && ['arguments', 'caller', 'callee'].includes(name)) {
      return false;
    }

    const value = obj[name];

    if (!isFunction$1(value)) return;

    descriptor.enumerable = false;

    if ('writable' in descriptor) {
      descriptor.writable = false;
      return;
    }

    if (!descriptor.set) {
      descriptor.set = () => {
        throw Error("Can not rewrite read-only method '" + name + "'");
      };
    }
  });
};

/**
 * Converts an array or a delimited string into an object set with values as keys and true as values.
 * Useful for fast membership checks.
 *
 * @param {Array|string} arrayOrString - The array or string to convert.
 * @param {string} delimiter - The delimiter to use if input is a string.
 * @returns {Object} An object with keys from the array or string, values set to true.
 */
const toObjectSet = (arrayOrString, delimiter) => {
  const obj = {};

  const define = (arr) => {
    arr.forEach((value) => {
      obj[value] = true;
    });
  };

  isArray(arrayOrString) ? define(arrayOrString) : define(String(arrayOrString).split(delimiter));

  return obj;
};

const noop = () => {};

const toFiniteNumber = (value, defaultValue) => {
  return value != null && Number.isFinite((value = +value)) ? value : defaultValue;
};

/**
 * If the thing is a FormData object, return true, otherwise return false.
 *
 * @param {unknown} thing - The thing to check.
 *
 * @returns {boolean}
 */
function isSpecCompliantForm(thing) {
  return !!(
    thing &&
    isFunction$1(thing.append) &&
    thing[toStringTag] === 'FormData' &&
    thing[iterator]
  );
}

/**
 * Recursively converts an object to a JSON-compatible object, handling circular references and Buffers.
 *
 * @param {Object} obj - The object to convert.
 * @returns {Object} The JSON-compatible object.
 */
const toJSONObject = (obj) => {
  const visited = new WeakSet();

  const visit = (source) => {
    if (isObject(source)) {
      if (visited.has(source)) {
        return;
      }

      //Buffer check
      if (isBuffer(source)) {
        return source;
      }

      if (!('toJSON' in source)) {
        // add-on descent / delete-on-ascent: preserves path semantics, so DAG nodes serialise at every occurrence (see #7230).
        visited.add(source);
        const target = isArray(source) ? [] : {};

        forEach(source, (value, key) => {
          const reducedValue = visit(value);
          !isUndefined(reducedValue) && (target[key] = reducedValue);
        });

        visited.delete(source);

        return target;
      }
    }

    return source;
  };

  return visit(obj);
};

/**
 * Determines if a value is an async function.
 *
 * @param {*} thing - The value to test.
 * @returns {boolean} True if value is an async function, otherwise false.
 */
const isAsyncFn = kindOfTest('AsyncFunction');

/**
 * Determines if a value is thenable (has then and catch methods).
 *
 * @param {*} thing - The value to test.
 * @returns {boolean} True if value is thenable, otherwise false.
 */
const isThenable = (thing) =>
  thing &&
  (isObject(thing) || isFunction$1(thing)) &&
  isFunction$1(thing.then) &&
  isFunction$1(thing.catch);

// original code
// https://github.com/DigitalBrainJS/AxiosPromise/blob/16deab13710ec09779922131f3fa5954320f83ab/lib/utils.js#L11-L34

/**
 * Provides a cross-platform setImmediate implementation.
 * Uses native setImmediate if available, otherwise falls back to postMessage or setTimeout.
 *
 * @param {boolean} setImmediateSupported - Whether setImmediate is supported.
 * @param {boolean} postMessageSupported - Whether postMessage is supported.
 * @returns {Function} A function to schedule a callback asynchronously.
 */
const _setImmediate = ((setImmediateSupported, postMessageSupported) => {
  if (setImmediateSupported) {
    return setImmediate;
  }

  return postMessageSupported
    ? ((token, callbacks) => {
        _global.addEventListener(
          'message',
          ({ source, data }) => {
            if (source === _global && data === token) {
              callbacks.length && callbacks.shift()();
            }
          },
          false
        );

        return (cb) => {
          callbacks.push(cb);
          _global.postMessage(token, '*');
        };
      })(`axios@${Math.random()}`, [])
    : (cb) => setTimeout(cb);
})(typeof setImmediate === 'function', isFunction$1(_global.postMessage));

/**
 * Schedules a microtask or asynchronous callback as soon as possible.
 * Uses queueMicrotask if available, otherwise falls back to process.nextTick or _setImmediate.
 *
 * @type {Function}
 */
const asap =
  typeof queueMicrotask !== 'undefined'
    ? queueMicrotask.bind(_global)
    : (typeof process !== 'undefined' && process.nextTick) || _setImmediate;

// *********************

const isIterable = (thing) => thing != null && isFunction$1(thing[iterator]);

var utils$1 = {
  isArray,
  isArrayBuffer,
  isBuffer,
  isFormData,
  isArrayBufferView,
  isString,
  isNumber,
  isBoolean,
  isObject,
  isPlainObject,
  isEmptyObject,
  isReadableStream,
  isRequest,
  isResponse,
  isHeaders,
  isUndefined,
  isDate,
  isFile,
  isReactNativeBlob,
  isReactNative,
  isBlob,
  isRegExp,
  isFunction: isFunction$1,
  isStream,
  isURLSearchParams,
  isTypedArray,
  isFileList,
  forEach,
  merge,
  extend,
  trim,
  stripBOM,
  inherits,
  toFlatObject,
  kindOf,
  kindOfTest,
  endsWith,
  toArray,
  forEachEntry,
  matchAll,
  isHTMLForm,
  hasOwnProperty,
  hasOwnProp: hasOwnProperty, // an alias to avoid ESLint no-prototype-builtins detection
  reduceDescriptors,
  freezeMethods,
  toObjectSet,
  toCamelCase,
  noop,
  toFiniteNumber,
  findKey,
  global: _global,
  isContextDefined,
  isSpecCompliantForm,
  toJSONObject,
  isAsyncFn,
  isThenable,
  setImmediate: _setImmediate,
  asap,
  isIterable,
};

// RawAxiosHeaders whose duplicates are ignored by node
// c.f. https://nodejs.org/api/http.html#http_message_headers
const ignoreDuplicateOf = utils$1.toObjectSet([
  'age',
  'authorization',
  'content-length',
  'content-type',
  'etag',
  'expires',
  'from',
  'host',
  'if-modified-since',
  'if-unmodified-since',
  'last-modified',
  'location',
  'max-forwards',
  'proxy-authorization',
  'referer',
  'retry-after',
  'user-agent',
]);

/**
 * Parse headers into an object
 *
 * ```
 * Date: Wed, 27 Aug 2014 08:58:49 GMT
 * Content-Type: application/json
 * Connection: keep-alive
 * Transfer-Encoding: chunked
 * ```
 *
 * @param {String} rawHeaders Headers needing to be parsed
 *
 * @returns {Object} Headers parsed into an object
 */
var parseHeaders = (rawHeaders) => {
  const parsed = {};
  let key;
  let val;
  let i;

  rawHeaders &&
    rawHeaders.split('\n').forEach(function parser(line) {
      i = line.indexOf(':');
      key = line.substring(0, i).trim().toLowerCase();
      val = line.substring(i + 1).trim();

      if (!key || (parsed[key] && ignoreDuplicateOf[key])) {
        return;
      }

      if (key === 'set-cookie') {
        if (parsed[key]) {
          parsed[key].push(val);
        } else {
          parsed[key] = [val];
        }
      } else {
        parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
      }
    });

  return parsed;
};

function trimSPorHTAB(str) {
  let start = 0;
  let end = str.length;

  while (start < end) {
    const code = str.charCodeAt(start);

    if (code !== 0x09 && code !== 0x20) {
      break;
    }

    start += 1;
  }

  while (end > start) {
    const code = str.charCodeAt(end - 1);

    if (code !== 0x09 && code !== 0x20) {
      break;
    }

    end -= 1;
  }

  return start === 0 && end === str.length ? str : str.slice(start, end);
}

// The control-code ranges are intentional: header sanitization strips C0/DEL bytes.
// eslint-disable-next-line no-control-regex
const INVALID_UNICODE_HEADER_VALUE_CHARS = new RegExp('[\\u0000-\\u0008\\u000a-\\u001f\\u007f]+', 'g');
// eslint-disable-next-line no-control-regex
const INVALID_BYTE_STRING_HEADER_VALUE_CHARS = new RegExp('[^\\u0009\\u0020-\\u007e\\u0080-\\u00ff]+', 'g');

function sanitizeValue(value, invalidChars) {
  if (utils$1.isArray(value)) {
    return value.map((item) => sanitizeValue(item, invalidChars));
  }

  return trimSPorHTAB(String(value).replace(invalidChars, ''));
}

const sanitizeHeaderValue = (value) =>
  sanitizeValue(value, INVALID_UNICODE_HEADER_VALUE_CHARS);

const sanitizeByteStringHeaderValue = (value) =>
  sanitizeValue(value, INVALID_BYTE_STRING_HEADER_VALUE_CHARS);

function toByteStringHeaderObject(headers) {
  const byteStringHeaders = Object.create(null);

  utils$1.forEach(headers.toJSON(), (value, header) => {
    byteStringHeaders[header] = sanitizeByteStringHeaderValue(value);
  });

  return byteStringHeaders;
}

const $internals = Symbol('internals');

function normalizeHeader(header) {
  return header && String(header).trim().toLowerCase();
}

function normalizeValue(value) {
  if (value === false || value == null) {
    return value;
  }

  return utils$1.isArray(value) ? value.map(normalizeValue) : sanitizeHeaderValue(String(value));
}

function parseTokens(str) {
  const tokens = Object.create(null);
  const tokensRE = /([^\s,;=]+)\s*(?:=\s*([^,;]+))?/g;
  let match;

  while ((match = tokensRE.exec(str))) {
    tokens[match[1]] = match[2];
  }

  return tokens;
}

const isValidHeaderName = (str) => /^[-_a-zA-Z0-9^`|~,!#$%&'*+.]+$/.test(str.trim());

function matchHeaderValue(context, value, header, filter, isHeaderNameFilter) {
  if (utils$1.isFunction(filter)) {
    return filter.call(this, value, header);
  }

  if (isHeaderNameFilter) {
    value = header;
  }

  if (!utils$1.isString(value)) return;

  if (utils$1.isString(filter)) {
    return value.indexOf(filter) !== -1;
  }

  if (utils$1.isRegExp(filter)) {
    return filter.test(value);
  }
}

function formatHeader(header) {
  return header
    .trim()
    .toLowerCase()
    .replace(/([a-z\d])(\w*)/g, (w, char, str) => {
      return char.toUpperCase() + str;
    });
}

function buildAccessors(obj, header) {
  const accessorName = utils$1.toCamelCase(' ' + header);

  ['get', 'set', 'has'].forEach((methodName) => {
    Object.defineProperty(obj, methodName + accessorName, {
      // Null-proto descriptor so a polluted Object.prototype.get cannot turn
      // this data descriptor into an accessor descriptor on the way in.
      __proto__: null,
      value: function (arg1, arg2, arg3) {
        return this[methodName].call(this, header, arg1, arg2, arg3);
      },
      configurable: true,
    });
  });
}

let AxiosHeaders$1 = class AxiosHeaders {
  constructor(headers) {
    headers && this.set(headers);
  }

  set(header, valueOrRewrite, rewrite) {
    const self = this;

    function setHeader(_value, _header, _rewrite) {
      const lHeader = normalizeHeader(_header);

      if (!lHeader) {
        return;
      }

      const key = utils$1.findKey(self, lHeader);

      if (
        !key ||
        self[key] === undefined ||
        _rewrite === true ||
        (_rewrite === undefined && self[key] !== false)
      ) {
        self[key || _header] = normalizeValue(_value);
      }
    }

    const setHeaders = (headers, _rewrite) =>
      utils$1.forEach(headers, (_value, _header) => setHeader(_value, _header, _rewrite));

    if (utils$1.isPlainObject(header) || header instanceof this.constructor) {
      setHeaders(header, valueOrRewrite);
    } else if (utils$1.isString(header) && (header = header.trim()) && !isValidHeaderName(header)) {
      setHeaders(parseHeaders(header), valueOrRewrite);
    } else if (utils$1.isObject(header) && utils$1.isIterable(header)) {
      let obj = {},
        dest,
        key;
      for (const entry of header) {
        if (!utils$1.isArray(entry)) {
          throw new TypeError('Object iterator must return a key-value pair');
        }

        obj[(key = entry[0])] = (dest = obj[key])
          ? utils$1.isArray(dest)
            ? [...dest, entry[1]]
            : [dest, entry[1]]
          : entry[1];
      }

      setHeaders(obj, valueOrRewrite);
    } else {
      header != null && setHeader(valueOrRewrite, header, rewrite);
    }

    return this;
  }

  get(header, parser) {
    header = normalizeHeader(header);

    if (header) {
      const key = utils$1.findKey(this, header);

      if (key) {
        const value = this[key];

        if (!parser) {
          return value;
        }

        if (parser === true) {
          return parseTokens(value);
        }

        if (utils$1.isFunction(parser)) {
          return parser.call(this, value, key);
        }

        if (utils$1.isRegExp(parser)) {
          return parser.exec(value);
        }

        throw new TypeError('parser must be boolean|regexp|function');
      }
    }
  }

  has(header, matcher) {
    header = normalizeHeader(header);

    if (header) {
      const key = utils$1.findKey(this, header);

      return !!(
        key &&
        this[key] !== undefined &&
        (!matcher || matchHeaderValue(this, this[key], key, matcher))
      );
    }

    return false;
  }

  delete(header, matcher) {
    const self = this;
    let deleted = false;

    function deleteHeader(_header) {
      _header = normalizeHeader(_header);

      if (_header) {
        const key = utils$1.findKey(self, _header);

        if (key && (!matcher || matchHeaderValue(self, self[key], key, matcher))) {
          delete self[key];

          deleted = true;
        }
      }
    }

    if (utils$1.isArray(header)) {
      header.forEach(deleteHeader);
    } else {
      deleteHeader(header);
    }

    return deleted;
  }

  clear(matcher) {
    const keys = Object.keys(this);
    let i = keys.length;
    let deleted = false;

    while (i--) {
      const key = keys[i];
      if (!matcher || matchHeaderValue(this, this[key], key, matcher, true)) {
        delete this[key];
        deleted = true;
      }
    }

    return deleted;
  }

  normalize(format) {
    const self = this;
    const headers = {};

    utils$1.forEach(this, (value, header) => {
      const key = utils$1.findKey(headers, header);

      if (key) {
        self[key] = normalizeValue(value);
        delete self[header];
        return;
      }

      const normalized = format ? formatHeader(header) : String(header).trim();

      if (normalized !== header) {
        delete self[header];
      }

      self[normalized] = normalizeValue(value);

      headers[normalized] = true;
    });

    return this;
  }

  concat(...targets) {
    return this.constructor.concat(this, ...targets);
  }

  toJSON(asStrings) {
    const obj = Object.create(null);

    utils$1.forEach(this, (value, header) => {
      value != null &&
        value !== false &&
        (obj[header] = asStrings && utils$1.isArray(value) ? value.join(', ') : value);
    });

    return obj;
  }

  [Symbol.iterator]() {
    return Object.entries(this.toJSON())[Symbol.iterator]();
  }

  toString() {
    return Object.entries(this.toJSON())
      .map(([header, value]) => header + ': ' + value)
      .join('\n');
  }

  getSetCookie() {
    return this.get('set-cookie') || [];
  }

  get [Symbol.toStringTag]() {
    return 'AxiosHeaders';
  }

  static from(thing) {
    return thing instanceof this ? thing : new this(thing);
  }

  static concat(first, ...targets) {
    const computed = new this(first);

    targets.forEach((target) => computed.set(target));

    return computed;
  }

  static accessor(header) {
    const internals =
      (this[$internals] =
      this[$internals] =
        {
          accessors: {},
        });

    const accessors = internals.accessors;
    const prototype = this.prototype;

    function defineAccessor(_header) {
      const lHeader = normalizeHeader(_header);

      if (!accessors[lHeader]) {
        buildAccessors(prototype, _header);
        accessors[lHeader] = true;
      }
    }

    utils$1.isArray(header) ? header.forEach(defineAccessor) : defineAccessor(header);

    return this;
  }
};

AxiosHeaders$1.accessor([
  'Content-Type',
  'Content-Length',
  'Accept',
  'Accept-Encoding',
  'User-Agent',
  'Authorization',
]);

// reserved names hotfix
utils$1.reduceDescriptors(AxiosHeaders$1.prototype, ({ value }, key) => {
  let mapped = key[0].toUpperCase() + key.slice(1); // map `set` => `Set`
  return {
    get: () => value,
    set(headerValue) {
      this[mapped] = headerValue;
    },
  };
});

utils$1.freezeMethods(AxiosHeaders$1);

const REDACTED = '[REDACTED ****]';

function hasOwnOrPrototypeToJSON(source) {
  if (utils$1.hasOwnProp(source, 'toJSON')) {
    return true;
  }

  let prototype = Object.getPrototypeOf(source);

  while (prototype && prototype !== Object.prototype) {
    if (utils$1.hasOwnProp(prototype, 'toJSON')) {
      return true;
    }

    prototype = Object.getPrototypeOf(prototype);
  }

  return false;
}

// Build a plain-object snapshot of `config` and replace the value of any key
// (case-insensitive) listed in `redactKeys` with REDACTED. Walks through arrays
// and AxiosHeaders, and short-circuits on circular references.
function redactConfig(config, redactKeys) {
  const lowerKeys = new Set(redactKeys.map((k) => String(k).toLowerCase()));
  const seen = [];

  const visit = (source) => {
    if (source === null || typeof source !== 'object') return source;
    if (utils$1.isBuffer(source)) return source;
    if (seen.indexOf(source) !== -1) return undefined;

    if (source instanceof AxiosHeaders$1) {
      source = source.toJSON();
    }

    seen.push(source);

    let result;
    if (utils$1.isArray(source)) {
      result = [];
      source.forEach((v, i) => {
        const reducedValue = visit(v);
        if (!utils$1.isUndefined(reducedValue)) {
          result[i] = reducedValue;
        }
      });
    } else {
      if (!utils$1.isPlainObject(source) && hasOwnOrPrototypeToJSON(source)) {
        seen.pop();
        return source;
      }

      result = Object.create(null);
      for (const [key, value] of Object.entries(source)) {
        const reducedValue = lowerKeys.has(key.toLowerCase()) ? REDACTED : visit(value);
        if (!utils$1.isUndefined(reducedValue)) {
          result[key] = reducedValue;
        }
      }
    }

    seen.pop();
    return result;
  };

  return visit(config);
}

let AxiosError$1 = class AxiosError extends Error {
  static from(error, code, config, request, response, customProps) {
    const axiosError = new AxiosError(error.message, code || error.code, config, request, response);
    axiosError.cause = error;
    axiosError.name = error.name;

    // Preserve status from the original error if not already set from response
    if (error.status != null && axiosError.status == null) {
      axiosError.status = error.status;
    }

    customProps && Object.assign(axiosError, customProps);
    return axiosError;
  }

  /**
   * Create an Error with the specified message, config, error code, request and response.
   *
   * @param {string} message The error message.
   * @param {string} [code] The error code (for example, 'ECONNABORTED').
   * @param {Object} [config] The config.
   * @param {Object} [request] The request.
   * @param {Object} [response] The response.
   *
   * @returns {Error} The created error.
   */
  constructor(message, code, config, request, response) {
    super(message);

    // Make message enumerable to maintain backward compatibility
    // The native Error constructor sets message as non-enumerable,
    // but axios < v1.13.3 had it as enumerable
    Object.defineProperty(this, 'message', {
      // Null-proto descriptor so a polluted Object.prototype.get cannot turn
      // this data descriptor into an accessor descriptor on the way in.
      __proto__: null,
      value: message,
      enumerable: true,
      writable: true,
      configurable: true,
    });

    this.name = 'AxiosError';
    this.isAxiosError = true;
    code && (this.code = code);
    config && (this.config = config);
    request && (this.request = request);
    if (response) {
      this.response = response;
      this.status = response.status;
    }
  }

  toJSON() {
    // Opt-in redaction: when the request config carries a `redact` array, the
    // value of any matching key (case-insensitive, at any depth) is replaced
    // with REDACTED in the serialized snapshot. Undefined or empty leaves the
    // existing serialization behavior unchanged.
    const config = this.config;
    const redactKeys = config && utils$1.hasOwnProp(config, 'redact') ? config.redact : undefined;
    const serializedConfig =
      utils$1.isArray(redactKeys) && redactKeys.length > 0
        ? redactConfig(config, redactKeys)
        : utils$1.toJSONObject(config);

    return {
      // Standard
      message: this.message,
      name: this.name,
      // Microsoft
      description: this.description,
      number: this.number,
      // Mozilla
      fileName: this.fileName,
      lineNumber: this.lineNumber,
      columnNumber: this.columnNumber,
      stack: this.stack,
      // Axios
      config: serializedConfig,
      code: this.code,
      status: this.status,
    };
  }
};

// This can be changed to static properties as soon as the parser options in .eslint.cjs are updated.
AxiosError$1.ERR_BAD_OPTION_VALUE = 'ERR_BAD_OPTION_VALUE';
AxiosError$1.ERR_BAD_OPTION = 'ERR_BAD_OPTION';
AxiosError$1.ECONNABORTED = 'ECONNABORTED';
AxiosError$1.ETIMEDOUT = 'ETIMEDOUT';
AxiosError$1.ECONNREFUSED = 'ECONNREFUSED';
AxiosError$1.ERR_NETWORK = 'ERR_NETWORK';
AxiosError$1.ERR_FR_TOO_MANY_REDIRECTS = 'ERR_FR_TOO_MANY_REDIRECTS';
AxiosError$1.ERR_DEPRECATED = 'ERR_DEPRECATED';
AxiosError$1.ERR_BAD_RESPONSE = 'ERR_BAD_RESPONSE';
AxiosError$1.ERR_BAD_REQUEST = 'ERR_BAD_REQUEST';
AxiosError$1.ERR_CANCELED = 'ERR_CANCELED';
AxiosError$1.ERR_NOT_SUPPORT = 'ERR_NOT_SUPPORT';
AxiosError$1.ERR_INVALID_URL = 'ERR_INVALID_URL';
AxiosError$1.ERR_FORM_DATA_DEPTH_EXCEEDED = 'ERR_FORM_DATA_DEPTH_EXCEEDED';

// eslint-disable-next-line strict
var httpAdapter = null;

/**
 * Determines if the given thing is a array or js object.
 *
 * @param {string} thing - The object or array to be visited.
 *
 * @returns {boolean}
 */
function isVisitable(thing) {
  return utils$1.isPlainObject(thing) || utils$1.isArray(thing);
}

/**
 * It removes the brackets from the end of a string
 *
 * @param {string} key - The key of the parameter.
 *
 * @returns {string} the key without the brackets.
 */
function removeBrackets(key) {
  return utils$1.endsWith(key, '[]') ? key.slice(0, -2) : key;
}

/**
 * It takes a path, a key, and a boolean, and returns a string
 *
 * @param {string} path - The path to the current key.
 * @param {string} key - The key of the current object being iterated over.
 * @param {string} dots - If true, the key will be rendered with dots instead of brackets.
 *
 * @returns {string} The path to the current key.
 */
function renderKey(path, key, dots) {
  if (!path) return key;
  return path
    .concat(key)
    .map(function each(token, i) {
      // eslint-disable-next-line no-param-reassign
      token = removeBrackets(token);
      return !dots && i ? '[' + token + ']' : token;
    })
    .join(dots ? '.' : '');
}

/**
 * If the array is an array and none of its elements are visitable, then it's a flat array.
 *
 * @param {Array<any>} arr - The array to check
 *
 * @returns {boolean}
 */
function isFlatArray(arr) {
  return utils$1.isArray(arr) && !arr.some(isVisitable);
}

const predicates = utils$1.toFlatObject(utils$1, {}, null, function filter(prop) {
  return /^is[A-Z]/.test(prop);
});

/**
 * Convert a data object to FormData
 *
 * @param {Object} obj
 * @param {?Object} [formData]
 * @param {?Object} [options]
 * @param {Function} [options.visitor]
 * @param {Boolean} [options.metaTokens = true]
 * @param {Boolean} [options.dots = false]
 * @param {?Boolean} [options.indexes = false]
 *
 * @returns {Object}
 **/

/**
 * It converts an object into a FormData object
 *
 * @param {Object<any, any>} obj - The object to convert to form data.
 * @param {string} formData - The FormData object to append to.
 * @param {Object<string, any>} options
 *
 * @returns
 */
function toFormData$1(obj, formData, options) {
  if (!utils$1.isObject(obj)) {
    throw new TypeError('target must be an object');
  }

  // eslint-disable-next-line no-param-reassign
  formData = formData || new (FormData)();

  // eslint-disable-next-line no-param-reassign
  options = utils$1.toFlatObject(
    options,
    {
      metaTokens: true,
      dots: false,
      indexes: false,
    },
    false,
    function defined(option, source) {
      // eslint-disable-next-line no-eq-null,eqeqeq
      return !utils$1.isUndefined(source[option]);
    }
  );

  const metaTokens = options.metaTokens;
  // eslint-disable-next-line no-use-before-define
  const visitor = options.visitor || defaultVisitor;
  const dots = options.dots;
  const indexes = options.indexes;
  const _Blob = options.Blob || (typeof Blob !== 'undefined' && Blob);
  const maxDepth = options.maxDepth === undefined ? 100 : options.maxDepth;
  const useBlob = _Blob && utils$1.isSpecCompliantForm(formData);

  if (!utils$1.isFunction(visitor)) {
    throw new TypeError('visitor must be a function');
  }

  function convertValue(value) {
    if (value === null) return '';

    if (utils$1.isDate(value)) {
      return value.toISOString();
    }

    if (utils$1.isBoolean(value)) {
      return value.toString();
    }

    if (!useBlob && utils$1.isBlob(value)) {
      throw new AxiosError$1('Blob is not supported. Use a Buffer instead.');
    }

    if (utils$1.isArrayBuffer(value) || utils$1.isTypedArray(value)) {
      return useBlob && typeof Blob === 'function' ? new Blob([value]) : Buffer.from(value);
    }

    return value;
  }

  /**
   * Default visitor.
   *
   * @param {*} value
   * @param {String|Number} key
   * @param {Array<String|Number>} path
   * @this {FormData}
   *
   * @returns {boolean} return true to visit the each prop of the value recursively
   */
  function defaultVisitor(value, key, path) {
    let arr = value;

    if (utils$1.isReactNative(formData) && utils$1.isReactNativeBlob(value)) {
      formData.append(renderKey(path, key, dots), convertValue(value));
      return false;
    }

    if (value && !path && typeof value === 'object') {
      if (utils$1.endsWith(key, '{}')) {
        // eslint-disable-next-line no-param-reassign
        key = metaTokens ? key : key.slice(0, -2);
        // eslint-disable-next-line no-param-reassign
        value = JSON.stringify(value);
      } else if (
        (utils$1.isArray(value) && isFlatArray(value)) ||
        ((utils$1.isFileList(value) || utils$1.endsWith(key, '[]')) && (arr = utils$1.toArray(value)))
      ) {
        // eslint-disable-next-line no-param-reassign
        key = removeBrackets(key);

        arr.forEach(function each(el, index) {
          !(utils$1.isUndefined(el) || el === null) &&
            formData.append(
              // eslint-disable-next-line no-nested-ternary
              indexes === true
                ? renderKey([key], index, dots)
                : indexes === null
                  ? key
                  : key + '[]',
              convertValue(el)
            );
        });
        return false;
      }
    }

    if (isVisitable(value)) {
      return true;
    }

    formData.append(renderKey(path, key, dots), convertValue(value));

    return false;
  }

  const stack = [];

  const exposedHelpers = Object.assign(predicates, {
    defaultVisitor,
    convertValue,
    isVisitable,
  });

  function build(value, path, depth = 0) {
    if (utils$1.isUndefined(value)) return;

    if (depth > maxDepth) {
      throw new AxiosError$1(
        'Object is too deeply nested (' + depth + ' levels). Max depth: ' + maxDepth,
        AxiosError$1.ERR_FORM_DATA_DEPTH_EXCEEDED
      );
    }

    if (stack.indexOf(value) !== -1) {
      throw new Error('Circular reference detected in ' + path.join('.'));
    }

    stack.push(value);

    utils$1.forEach(value, function each(el, key) {
      const result =
        !(utils$1.isUndefined(el) || el === null) &&
        visitor.call(formData, el, utils$1.isString(key) ? key.trim() : key, path, exposedHelpers);

      if (result === true) {
        build(el, path ? path.concat(key) : [key], depth + 1);
      }
    });

    stack.pop();
  }

  if (!utils$1.isObject(obj)) {
    throw new TypeError('data must be an object');
  }

  build(obj);

  return formData;
}

/**
 * It encodes a string by replacing all characters that are not in the unreserved set with
 * their percent-encoded equivalents
 *
 * @param {string} str - The string to encode.
 *
 * @returns {string} The encoded string.
 */
function encode$1(str) {
  const charMap = {
    '!': '%21',
    "'": '%27',
    '(': '%28',
    ')': '%29',
    '~': '%7E',
    '%20': '+',
  };
  return encodeURIComponent(str).replace(/[!'()~]|%20/g, function replacer(match) {
    return charMap[match];
  });
}

/**
 * It takes a params object and converts it to a FormData object
 *
 * @param {Object<string, any>} params - The parameters to be converted to a FormData object.
 * @param {Object<string, any>} options - The options object passed to the Axios constructor.
 *
 * @returns {void}
 */
function AxiosURLSearchParams(params, options) {
  this._pairs = [];

  params && toFormData$1(params, this, options);
}

const prototype = AxiosURLSearchParams.prototype;

prototype.append = function append(name, value) {
  this._pairs.push([name, value]);
};

prototype.toString = function toString(encoder) {
  const _encode = encoder
    ? function (value) {
        return encoder.call(this, value, encode$1);
      }
    : encode$1;

  return this._pairs
    .map(function each(pair) {
      return _encode(pair[0]) + '=' + _encode(pair[1]);
    }, '')
    .join('&');
};

/**
 * It replaces URL-encoded forms of `:`, `$`, `,`, and spaces with
 * their plain counterparts (`:`, `$`, `,`, `+`).
 *
 * @param {string} val The value to be encoded.
 *
 * @returns {string} The encoded value.
 */
function encode(val) {
  return encodeURIComponent(val)
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+');
}

/**
 * Build a URL by appending params to the end
 *
 * @param {string} url The base of the url (e.g., http://www.google.com)
 * @param {object} [params] The params to be appended
 * @param {?(object|Function)} options
 *
 * @returns {string} The formatted url
 */
function buildURL(url, params, options) {
  if (!params) {
    return url;
  }

  const _encode = (options && options.encode) || encode;

  const _options = utils$1.isFunction(options)
    ? {
        serialize: options,
      }
    : options;

  const serializeFn = _options && _options.serialize;

  let serializedParams;

  if (serializeFn) {
    serializedParams = serializeFn(params, _options);
  } else {
    serializedParams = utils$1.isURLSearchParams(params)
      ? params.toString()
      : new AxiosURLSearchParams(params, _options).toString(_encode);
  }

  if (serializedParams) {
    const hashmarkIndex = url.indexOf('#');

    if (hashmarkIndex !== -1) {
      url = url.slice(0, hashmarkIndex);
    }
    url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams;
  }

  return url;
}

class InterceptorManager {
  constructor() {
    this.handlers = [];
  }

  /**
   * Add a new interceptor to the stack
   *
   * @param {Function} fulfilled The function to handle `then` for a `Promise`
   * @param {Function} rejected The function to handle `reject` for a `Promise`
   * @param {Object} options The options for the interceptor, synchronous and runWhen
   *
   * @return {Number} An ID used to remove interceptor later
   */
  use(fulfilled, rejected, options) {
    this.handlers.push({
      fulfilled,
      rejected,
      synchronous: options ? options.synchronous : false,
      runWhen: options ? options.runWhen : null,
    });
    return this.handlers.length - 1;
  }

  /**
   * Remove an interceptor from the stack
   *
   * @param {Number} id The ID that was returned by `use`
   *
   * @returns {void}
   */
  eject(id) {
    if (this.handlers[id]) {
      this.handlers[id] = null;
    }
  }

  /**
   * Clear all interceptors from the stack
   *
   * @returns {void}
   */
  clear() {
    if (this.handlers) {
      this.handlers = [];
    }
  }

  /**
   * Iterate over all the registered interceptors
   *
   * This method is particularly useful for skipping over any
   * interceptors that may have become `null` calling `eject`.
   *
   * @param {Function} fn The function to call for each interceptor
   *
   * @returns {void}
   */
  forEach(fn) {
    utils$1.forEach(this.handlers, function forEachHandler(h) {
      if (h !== null) {
        fn(h);
      }
    });
  }
}

var transitionalDefaults = {
  silentJSONParsing: true,
  forcedJSONParsing: true,
  clarifyTimeoutError: false,
  legacyInterceptorReqResOrdering: true,
  advertiseZstdAcceptEncoding: false,
};

var URLSearchParams$1 = typeof URLSearchParams !== 'undefined' ? URLSearchParams : AxiosURLSearchParams;

var FormData$1 = typeof FormData !== 'undefined' ? FormData : null;

var Blob$1 = typeof Blob !== 'undefined' ? Blob : null;

var platform$1 = {
  isBrowser: true,
  classes: {
    URLSearchParams: URLSearchParams$1,
    FormData: FormData$1,
    Blob: Blob$1,
  },
  protocols: ['http', 'https', 'file', 'blob', 'url', 'data'],
};

const hasBrowserEnv = typeof window !== 'undefined' && typeof document !== 'undefined';

const _navigator = (typeof navigator === 'object' && navigator) || undefined;

/**
 * Determine if we're running in a standard browser environment
 *
 * This allows axios to run in a web worker, and react-native.
 * Both environments support XMLHttpRequest, but not fully standard globals.
 *
 * web workers:
 *  typeof window -> undefined
 *  typeof document -> undefined
 *
 * react-native:
 *  navigator.product -> 'ReactNative'
 * nativescript
 *  navigator.product -> 'NativeScript' or 'NS'
 *
 * @returns {boolean}
 */
const hasStandardBrowserEnv =
  hasBrowserEnv &&
  (!_navigator || ['ReactNative', 'NativeScript', 'NS'].indexOf(_navigator.product) < 0);

/**
 * Determine if we're running in a standard browser webWorker environment
 *
 * Although the `isStandardBrowserEnv` method indicates that
 * `allows axios to run in a web worker`, the WebWorker will still be
 * filtered out due to its judgment standard
 * `typeof window !== 'undefined' && typeof document !== 'undefined'`.
 * This leads to a problem when axios post `FormData` in webWorker
 */
const hasStandardBrowserWebWorkerEnv = (() => {
  return (
    typeof WorkerGlobalScope !== 'undefined' &&
    // eslint-disable-next-line no-undef
    self instanceof WorkerGlobalScope &&
    typeof self.importScripts === 'function'
  );
})();

const origin = (hasBrowserEnv && window.location.href) || 'http://localhost';

var utils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  hasBrowserEnv: hasBrowserEnv,
  hasStandardBrowserEnv: hasStandardBrowserEnv,
  hasStandardBrowserWebWorkerEnv: hasStandardBrowserWebWorkerEnv,
  navigator: _navigator,
  origin: origin
});

var platform = {
  ...utils,
  ...platform$1,
};

function toURLEncodedForm(data, options) {
  return toFormData$1(data, new platform.classes.URLSearchParams(), {
    visitor: function (value, key, path, helpers) {
      if (platform.isNode && utils$1.isBuffer(value)) {
        this.append(key, value.toString('base64'));
        return false;
      }

      return helpers.defaultVisitor.apply(this, arguments);
    },
    ...options,
  });
}

/**
 * It takes a string like `foo[x][y][z]` and returns an array like `['foo', 'x', 'y', 'z']
 *
 * @param {string} name - The name of the property to get.
 *
 * @returns An array of strings.
 */
function parsePropPath(name) {
  // foo[x][y][z]
  // foo.x.y.z
  // foo-x-y-z
  // foo x y z
  return utils$1.matchAll(/\w+|\[(\w*)]/g, name).map((match) => {
    return match[0] === '[]' ? '' : match[1] || match[0];
  });
}

/**
 * Convert an array to an object.
 *
 * @param {Array<any>} arr - The array to convert to an object.
 *
 * @returns An object with the same keys and values as the array.
 */
function arrayToObject(arr) {
  const obj = {};
  const keys = Object.keys(arr);
  let i;
  const len = keys.length;
  let key;
  for (i = 0; i < len; i++) {
    key = keys[i];
    obj[key] = arr[key];
  }
  return obj;
}

/**
 * It takes a FormData object and returns a JavaScript object
 *
 * @param {string} formData The FormData object to convert to JSON.
 *
 * @returns {Object<string, any> | null} The converted object.
 */
function formDataToJSON(formData) {
  function buildPath(path, value, target, index) {
    let name = path[index++];

    if (name === '__proto__') return true;

    const isNumericKey = Number.isFinite(+name);
    const isLast = index >= path.length;
    name = !name && utils$1.isArray(target) ? target.length : name;

    if (isLast) {
      if (utils$1.hasOwnProp(target, name)) {
        target[name] = utils$1.isArray(target[name])
          ? target[name].concat(value)
          : [target[name], value];
      } else {
        target[name] = value;
      }

      return !isNumericKey;
    }

    if (!utils$1.hasOwnProp(target, name) || !utils$1.isObject(target[name])) {
      target[name] = [];
    }

    const result = buildPath(path, value, target[name], index);

    if (result && utils$1.isArray(target[name])) {
      target[name] = arrayToObject(target[name]);
    }

    return !isNumericKey;
  }

  if (utils$1.isFormData(formData) && utils$1.isFunction(formData.entries)) {
    const obj = {};

    utils$1.forEachEntry(formData, (name, value) => {
      buildPath(parsePropPath(name), value, obj, 0);
    });

    return obj;
  }

  return null;
}

const own = (obj, key) => (obj != null && utils$1.hasOwnProp(obj, key) ? obj[key] : undefined);

/**
 * It takes a string, tries to parse it, and if it fails, it returns the stringified version
 * of the input
 *
 * @param {any} rawValue - The value to be stringified.
 * @param {Function} parser - A function that parses a string into a JavaScript object.
 * @param {Function} encoder - A function that takes a value and returns a string.
 *
 * @returns {string} A stringified version of the rawValue.
 */
function stringifySafely(rawValue, parser, encoder) {
  if (utils$1.isString(rawValue)) {
    try {
      (parser || JSON.parse)(rawValue);
      return utils$1.trim(rawValue);
    } catch (e) {
      if (e.name !== 'SyntaxError') {
        throw e;
      }
    }
  }

  return (encoder || JSON.stringify)(rawValue);
}

const defaults = {
  transitional: transitionalDefaults,

  adapter: ['xhr', 'http', 'fetch'],

  transformRequest: [
    function transformRequest(data, headers) {
      const contentType = headers.getContentType() || '';
      const hasJSONContentType = contentType.indexOf('application/json') > -1;
      const isObjectPayload = utils$1.isObject(data);

      if (isObjectPayload && utils$1.isHTMLForm(data)) {
        data = new FormData(data);
      }

      const isFormData = utils$1.isFormData(data);

      if (isFormData) {
        return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data;
      }

      if (
        utils$1.isArrayBuffer(data) ||
        utils$1.isBuffer(data) ||
        utils$1.isStream(data) ||
        utils$1.isFile(data) ||
        utils$1.isBlob(data) ||
        utils$1.isReadableStream(data)
      ) {
        return data;
      }
      if (utils$1.isArrayBufferView(data)) {
        return data.buffer;
      }
      if (utils$1.isURLSearchParams(data)) {
        headers.setContentType('application/x-www-form-urlencoded;charset=utf-8', false);
        return data.toString();
      }

      let isFileList;

      if (isObjectPayload) {
        const formSerializer = own(this, 'formSerializer');
        if (contentType.indexOf('application/x-www-form-urlencoded') > -1) {
          return toURLEncodedForm(data, formSerializer).toString();
        }

        if (
          (isFileList = utils$1.isFileList(data)) ||
          contentType.indexOf('multipart/form-data') > -1
        ) {
          const env = own(this, 'env');
          const _FormData = env && env.FormData;

          return toFormData$1(
            isFileList ? { 'files[]': data } : data,
            _FormData && new _FormData(),
            formSerializer
          );
        }
      }

      if (isObjectPayload || hasJSONContentType) {
        headers.setContentType('application/json', false);
        return stringifySafely(data);
      }

      return data;
    },
  ],

  transformResponse: [
    function transformResponse(data) {
      const transitional = own(this, 'transitional') || defaults.transitional;
      const forcedJSONParsing = transitional && transitional.forcedJSONParsing;
      const responseType = own(this, 'responseType');
      const JSONRequested = responseType === 'json';

      if (utils$1.isResponse(data) || utils$1.isReadableStream(data)) {
        return data;
      }

      if (
        data &&
        utils$1.isString(data) &&
        ((forcedJSONParsing && !responseType) || JSONRequested)
      ) {
        const silentJSONParsing = transitional && transitional.silentJSONParsing;
        const strictJSONParsing = !silentJSONParsing && JSONRequested;

        try {
          return JSON.parse(data, own(this, 'parseReviver'));
        } catch (e) {
          if (strictJSONParsing) {
            if (e.name === 'SyntaxError') {
              throw AxiosError$1.from(e, AxiosError$1.ERR_BAD_RESPONSE, this, null, own(this, 'response'));
            }
            throw e;
          }
        }
      }

      return data;
    },
  ],

  /**
   * A timeout in milliseconds to abort a request. If set to 0 (default) a
   * timeout is not created.
   */
  timeout: 0,

  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',

  maxContentLength: -1,
  maxBodyLength: -1,

  env: {
    FormData: platform.classes.FormData,
    Blob: platform.classes.Blob,
  },

  validateStatus: function validateStatus(status) {
    return status >= 200 && status < 300;
  },

  headers: {
    common: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': undefined,
    },
  },
};

utils$1.forEach(['delete', 'get', 'head', 'post', 'put', 'patch', 'query'], (method) => {
  defaults.headers[method] = {};
});

/**
 * Transform the data for a request or a response
 *
 * @param {Array|Function} fns A single function or Array of functions
 * @param {?Object} response The response object
 *
 * @returns {*} The resulting transformed data
 */
function transformData(fns, response) {
  const config = this || defaults;
  const context = response || config;
  const headers = AxiosHeaders$1.from(context.headers);
  let data = context.data;

  utils$1.forEach(fns, function transform(fn) {
    data = fn.call(config, data, headers.normalize(), response ? response.status : undefined);
  });

  headers.normalize();

  return data;
}

function isCancel$1(value) {
  return !!(value && value.__CANCEL__);
}

let CanceledError$1 = class CanceledError extends AxiosError$1 {
  /**
   * A `CanceledError` is an object that is thrown when an operation is canceled.
   *
   * @param {string=} message The message.
   * @param {Object=} config The config.
   * @param {Object=} request The request.
   *
   * @returns {CanceledError} The created error.
   */
  constructor(message, config, request) {
    super(message == null ? 'canceled' : message, AxiosError$1.ERR_CANCELED, config, request);
    this.name = 'CanceledError';
    this.__CANCEL__ = true;
  }
};

/**
 * Resolve or reject a Promise based on response status.
 *
 * @param {Function} resolve A function that resolves the promise.
 * @param {Function} reject A function that rejects the promise.
 * @param {object} response The response.
 *
 * @returns {object} The response.
 */
function settle(resolve, reject, response) {
  const validateStatus = response.config.validateStatus;
  if (!response.status || !validateStatus || validateStatus(response.status)) {
    resolve(response);
  } else {
    reject(new AxiosError$1(
      'Request failed with status code ' + response.status,
      response.status >= 400 && response.status < 500 ? AxiosError$1.ERR_BAD_REQUEST : AxiosError$1.ERR_BAD_RESPONSE,
      response.config,
      response.request,
      response
    ));
  }
}

function parseProtocol(url) {
  const match = /^([-+\w]{1,25}):(?:\/\/)?/.exec(url);
  return (match && match[1]) || '';
}

/**
 * Calculate data maxRate
 * @param {Number} [samplesCount= 10]
 * @param {Number} [min= 1000]
 * @returns {Function}
 */
function speedometer(samplesCount, min) {
  samplesCount = samplesCount || 10;
  const bytes = new Array(samplesCount);
  const timestamps = new Array(samplesCount);
  let head = 0;
  let tail = 0;
  let firstSampleTS;

  min = min !== undefined ? min : 1000;

  return function push(chunkLength) {
    const now = Date.now();

    const startedAt = timestamps[tail];

    if (!firstSampleTS) {
      firstSampleTS = now;
    }

    bytes[head] = chunkLength;
    timestamps[head] = now;

    let i = tail;
    let bytesCount = 0;

    while (i !== head) {
      bytesCount += bytes[i++];
      i = i % samplesCount;
    }

    head = (head + 1) % samplesCount;

    if (head === tail) {
      tail = (tail + 1) % samplesCount;
    }

    if (now - firstSampleTS < min) {
      return;
    }

    const passed = startedAt && now - startedAt;

    return passed ? Math.round((bytesCount * 1000) / passed) : undefined;
  };
}

/**
 * Throttle decorator
 * @param {Function} fn
 * @param {Number} freq
 * @return {Function}
 */
function throttle(fn, freq) {
  let timestamp = 0;
  let threshold = 1000 / freq;
  let lastArgs;
  let timer;

  const invoke = (args, now = Date.now()) => {
    timestamp = now;
    lastArgs = null;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    fn(...args);
  };

  const throttled = (...args) => {
    const now = Date.now();
    const passed = now - timestamp;
    if (passed >= threshold) {
      invoke(args, now);
    } else {
      lastArgs = args;
      if (!timer) {
        timer = setTimeout(() => {
          timer = null;
          invoke(lastArgs);
        }, threshold - passed);
      }
    }
  };

  const flush = () => lastArgs && invoke(lastArgs);

  return [throttled, flush];
}

const progressEventReducer = (listener, isDownloadStream, freq = 3) => {
  let bytesNotified = 0;
  const _speedometer = speedometer(50, 250);

  return throttle((e) => {
    if (!e || typeof e.loaded !== 'number') {
      return;
    }
    const rawLoaded = e.loaded;
    const total = e.lengthComputable ? e.total : undefined;
    const loaded = total != null ? Math.min(rawLoaded, total) : rawLoaded;
    const progressBytes = Math.max(0, loaded - bytesNotified);
    const rate = _speedometer(progressBytes);

    bytesNotified = Math.max(bytesNotified, loaded);

    const data = {
      loaded,
      total,
      progress: total ? loaded / total : undefined,
      bytes: progressBytes,
      rate: rate ? rate : undefined,
      estimated: rate && total ? (total - loaded) / rate : undefined,
      event: e,
      lengthComputable: total != null,
      [isDownloadStream ? 'download' : 'upload']: true,
    };

    listener(data);
  }, freq);
};

const progressEventDecorator = (total, throttled) => {
  const lengthComputable = total != null;

  return [
    (loaded) =>
      throttled[0]({
        lengthComputable,
        total,
        loaded,
      }),
    throttled[1],
  ];
};

const asyncDecorator =
  (fn) =>
  (...args) =>
    utils$1.asap(() => fn(...args));

var isURLSameOrigin = platform.hasStandardBrowserEnv
  ? ((origin, isMSIE) => (url) => {
      url = new URL(url, platform.origin);

      return (
        origin.protocol === url.protocol &&
        origin.host === url.host &&
        (isMSIE || origin.port === url.port)
      );
    })(
      new URL(platform.origin),
      platform.navigator && /(msie|trident)/i.test(platform.navigator.userAgent)
    )
  : () => true;

var cookies = platform.hasStandardBrowserEnv
  ? // Standard browser envs support document.cookie
    {
      write(name, value, expires, path, domain, secure, sameSite) {
        if (typeof document === 'undefined') return;

        const cookie = [`${name}=${encodeURIComponent(value)}`];

        if (utils$1.isNumber(expires)) {
          cookie.push(`expires=${new Date(expires).toUTCString()}`);
        }
        if (utils$1.isString(path)) {
          cookie.push(`path=${path}`);
        }
        if (utils$1.isString(domain)) {
          cookie.push(`domain=${domain}`);
        }
        if (secure === true) {
          cookie.push('secure');
        }
        if (utils$1.isString(sameSite)) {
          cookie.push(`SameSite=${sameSite}`);
        }

        document.cookie = cookie.join('; ');
      },

      read(name) {
        if (typeof document === 'undefined') return null;
        // Match name=value by splitting on the semicolon separator instead of building a
        // RegExp from `name` — interpolating an unescaped string into a RegExp would let
        // metacharacters (e.g. `.+?` in an attacker-influenced cookie name) cause ReDoS or
        // match the wrong cookie. Browsers may serialize cookie pairs as either ";" or
        // "; ", so ignore optional whitespace before each cookie name.
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].replace(/^\s+/, '');
          const eq = cookie.indexOf('=');
          if (eq !== -1 && cookie.slice(0, eq) === name) {
            return decodeURIComponent(cookie.slice(eq + 1));
          }
        }
        return null;
      },

      remove(name) {
        this.write(name, '', Date.now() - 86400000, '/');
      },
    }
  : // Non-standard browser env (web workers, react-native) lack needed support.
    {
      write() {},
      read() {
        return null;
      },
      remove() {},
    };

/**
 * Determines whether the specified URL is absolute
 *
 * @param {string} url The URL to test
 *
 * @returns {boolean} True if the specified URL is absolute, otherwise false
 */
function isAbsoluteURL(url) {
  // A URL is considered absolute if it begins with "<scheme>://" or "//" (protocol-relative URL).
  // RFC 3986 defines scheme name as a sequence of characters beginning with a letter and followed
  // by any combination of letters, digits, plus, period, or hyphen.
  if (typeof url !== 'string') {
    return false;
  }

  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

/**
 * Creates a new URL by combining the specified URLs
 *
 * @param {string} baseURL The base URL
 * @param {string} relativeURL The relative URL
 *
 * @returns {string} The combined URL
 */
function combineURLs(baseURL, relativeURL) {
  return relativeURL
    ? baseURL.replace(/\/?\/$/, '') + '/' + relativeURL.replace(/^\/+/, '')
    : baseURL;
}

/**
 * Creates a new URL by combining the baseURL with the requestedURL,
 * only when the requestedURL is not already an absolute URL.
 * If the requestURL is absolute, this function returns the requestedURL untouched.
 *
 * @param {string} baseURL The base URL
 * @param {string} requestedURL Absolute or relative URL to combine
 *
 * @returns {string} The combined full path
 */
function buildFullPath(baseURL, requestedURL, allowAbsoluteUrls) {
  let isRelativeUrl = !isAbsoluteURL(requestedURL);
  if (baseURL && (isRelativeUrl || allowAbsoluteUrls === false)) {
    return combineURLs(baseURL, requestedURL);
  }
  return requestedURL;
}

const headersToObject = (thing) => (thing instanceof AxiosHeaders$1 ? { ...thing } : thing);

/**
 * Config-specific merge-function which creates a new config-object
 * by merging two configuration objects together.
 *
 * @param {Object} config1
 * @param {Object} config2
 *
 * @returns {Object} New object resulting from merging config2 to config1
 */
function mergeConfig$1(config1, config2) {
  // eslint-disable-next-line no-param-reassign
  config2 = config2 || {};

  // Use a null-prototype object so that downstream reads such as `config.auth`
  // or `config.baseURL` cannot inherit polluted values from Object.prototype.
  // `hasOwnProperty` is restored as a non-enumerable own slot to preserve
  // ergonomics for user code that relies on it.
  const config = Object.create(null);
  Object.defineProperty(config, 'hasOwnProperty', {
    // Null-proto descriptor so a polluted Object.prototype.get cannot turn
    // this data descriptor into an accessor descriptor on the way in.
    __proto__: null,
    value: Object.prototype.hasOwnProperty,
    enumerable: false,
    writable: true,
    configurable: true,
  });

  function getMergedValue(target, source, prop, caseless) {
    if (utils$1.isPlainObject(target) && utils$1.isPlainObject(source)) {
      return utils$1.merge.call({ caseless }, target, source);
    } else if (utils$1.isPlainObject(source)) {
      return utils$1.merge({}, source);
    } else if (utils$1.isArray(source)) {
      return source.slice();
    }
    return source;
  }

  function mergeDeepProperties(a, b, prop, caseless) {
    if (!utils$1.isUndefined(b)) {
      return getMergedValue(a, b, prop, caseless);
    } else if (!utils$1.isUndefined(a)) {
      return getMergedValue(undefined, a, prop, caseless);
    }
  }

  // eslint-disable-next-line consistent-return
  function valueFromConfig2(a, b) {
    if (!utils$1.isUndefined(b)) {
      return getMergedValue(undefined, b);
    }
  }

  // eslint-disable-next-line consistent-return
  function defaultToConfig2(a, b) {
    if (!utils$1.isUndefined(b)) {
      return getMergedValue(undefined, b);
    } else if (!utils$1.isUndefined(a)) {
      return getMergedValue(undefined, a);
    }
  }

  // eslint-disable-next-line consistent-return
  function mergeDirectKeys(a, b, prop) {
    if (utils$1.hasOwnProp(config2, prop)) {
      return getMergedValue(a, b);
    } else if (utils$1.hasOwnProp(config1, prop)) {
      return getMergedValue(undefined, a);
    }
  }

  const mergeMap = {
    url: valueFromConfig2,
    method: valueFromConfig2,
    data: valueFromConfig2,
    baseURL: defaultToConfig2,
    transformRequest: defaultToConfig2,
    transformResponse: defaultToConfig2,
    paramsSerializer: defaultToConfig2,
    timeout: defaultToConfig2,
    timeoutMessage: defaultToConfig2,
    withCredentials: defaultToConfig2,
    withXSRFToken: defaultToConfig2,
    adapter: defaultToConfig2,
    responseType: defaultToConfig2,
    xsrfCookieName: defaultToConfig2,
    xsrfHeaderName: defaultToConfig2,
    onUploadProgress: defaultToConfig2,
    onDownloadProgress: defaultToConfig2,
    decompress: defaultToConfig2,
    maxContentLength: defaultToConfig2,
    maxBodyLength: defaultToConfig2,
    beforeRedirect: defaultToConfig2,
    transport: defaultToConfig2,
    httpAgent: defaultToConfig2,
    httpsAgent: defaultToConfig2,
    cancelToken: defaultToConfig2,
    socketPath: defaultToConfig2,
    allowedSocketPaths: defaultToConfig2,
    responseEncoding: defaultToConfig2,
    validateStatus: mergeDirectKeys,
    headers: (a, b, prop) =>
      mergeDeepProperties(headersToObject(a), headersToObject(b), prop, true),
  };

  utils$1.forEach(Object.keys({ ...config1, ...config2 }), function computeConfigValue(prop) {
    if (prop === '__proto__' || prop === 'constructor' || prop === 'prototype') return;
    const merge = utils$1.hasOwnProp(mergeMap, prop) ? mergeMap[prop] : mergeDeepProperties;
    const a = utils$1.hasOwnProp(config1, prop) ? config1[prop] : undefined;
    const b = utils$1.hasOwnProp(config2, prop) ? config2[prop] : undefined;
    const configValue = merge(a, b, prop);
    (utils$1.isUndefined(configValue) && merge !== mergeDirectKeys) || (config[prop] = configValue);
  });

  return config;
}

const FORM_DATA_CONTENT_HEADERS = ['content-type', 'content-length'];

function setFormDataHeaders(headers, formHeaders, policy) {
  if (policy !== 'content-only') {
    headers.set(formHeaders);
    return;
  }

  Object.entries(formHeaders).forEach(([key, val]) => {
    if (FORM_DATA_CONTENT_HEADERS.includes(key.toLowerCase())) {
      headers.set(key, val);
    }
  });
}

/**
 * Encode a UTF-8 string to a Latin-1 byte string for use with btoa().
 * This is a modern replacement for the deprecated unescape(encodeURIComponent(str)) pattern.
 *
 * @param {string} str The string to encode
 *
 * @returns {string} UTF-8 bytes as a Latin-1 string
 */
const encodeUTF8$1 = (str) =>
  encodeURIComponent(str).replace(/%([0-9A-F]{2})/gi, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

function resolveConfig(config) {
  const newConfig = mergeConfig$1({}, config);

  // Read only own properties to prevent prototype pollution gadgets
  // (e.g. Object.prototype.baseURL = 'https://evil.com').
  const own = (key) => (utils$1.hasOwnProp(newConfig, key) ? newConfig[key] : undefined);

  const data = own('data');
  let withXSRFToken = own('withXSRFToken');
  const xsrfHeaderName = own('xsrfHeaderName');
  const xsrfCookieName = own('xsrfCookieName');
  let headers = own('headers');
  const auth = own('auth');
  const baseURL = own('baseURL');
  const allowAbsoluteUrls = own('allowAbsoluteUrls');
  const url = own('url');

  newConfig.headers = headers = AxiosHeaders$1.from(headers);

  newConfig.url = buildURL(
    buildFullPath(baseURL, url, allowAbsoluteUrls),
    own('params'),
    own('paramsSerializer')
  );

  // HTTP basic authentication
  if (auth) {
    headers.set(
      'Authorization',
      'Basic ' +
        btoa((auth.username || '') + ':' + (auth.password ? encodeUTF8$1(auth.password) : ''))
    );
  }

  if (utils$1.isFormData(data)) {
    if (
      platform.hasStandardBrowserEnv ||
      platform.hasStandardBrowserWebWorkerEnv ||
      utils$1.isReactNative(data)
    ) {
      headers.setContentType(undefined); // browser/web worker/RN handles it
    } else if (utils$1.isFunction(data.getHeaders)) {
      // Node.js FormData (like form-data package)
      setFormDataHeaders(headers, data.getHeaders(), own('formDataHeaderPolicy'));
    }
  }

  // Add xsrf header
  // This is only done if running in a standard browser environment.
  // Specifically not if we're in a web worker, or react-native.

  if (platform.hasStandardBrowserEnv) {
    if (utils$1.isFunction(withXSRFToken)) {
      withXSRFToken = withXSRFToken(newConfig);
    }

    // Strict boolean check — prevents proto-pollution gadgets (e.g. Object.prototype.withXSRFToken = 1)
    // and misconfigurations (e.g. "false") from short-circuiting the same-origin check and leaking
    // the XSRF token cross-origin.
    const shouldSendXSRF =
      withXSRFToken === true || (withXSRFToken == null && isURLSameOrigin(newConfig.url));

    if (shouldSendXSRF) {
      const xsrfValue = xsrfHeaderName && xsrfCookieName && cookies.read(xsrfCookieName);

      if (xsrfValue) {
        headers.set(xsrfHeaderName, xsrfValue);
      }
    }
  }

  return newConfig;
}

const isXHRAdapterSupported = typeof XMLHttpRequest !== 'undefined';

var xhrAdapter = isXHRAdapterSupported &&
  function (config) {
    return new Promise(function dispatchXhrRequest(resolve, reject) {
      const _config = resolveConfig(config);
      let requestData = _config.data;
      const requestHeaders = AxiosHeaders$1.from(_config.headers).normalize();
      let { responseType, onUploadProgress, onDownloadProgress } = _config;
      let onCanceled;
      let uploadThrottled, downloadThrottled;
      let flushUpload, flushDownload;

      function done() {
        flushUpload && flushUpload(); // flush events
        flushDownload && flushDownload(); // flush events

        _config.cancelToken && _config.cancelToken.unsubscribe(onCanceled);

        _config.signal && _config.signal.removeEventListener('abort', onCanceled);
      }

      let request = new XMLHttpRequest();

      request.open(_config.method.toUpperCase(), _config.url, true);

      // Set the request timeout in MS
      request.timeout = _config.timeout;

      function onloadend() {
        if (!request) {
          return;
        }
        // Prepare the response
        const responseHeaders = AxiosHeaders$1.from(
          'getAllResponseHeaders' in request && request.getAllResponseHeaders()
        );
        const responseData =
          !responseType || responseType === 'text' || responseType === 'json'
            ? request.responseText
            : request.response;
        const response = {
          data: responseData,
          status: request.status,
          statusText: request.statusText,
          headers: responseHeaders,
          config,
          request,
        };

        settle(
          function _resolve(value) {
            resolve(value);
            done();
          },
          function _reject(err) {
            reject(err);
            done();
          },
          response
        );

        // Clean up request
        request = null;
      }

      if ('onloadend' in request) {
        // Use onloadend if available
        request.onloadend = onloadend;
      } else {
        // Listen for ready state to emulate onloadend
        request.onreadystatechange = function handleLoad() {
          if (!request || request.readyState !== 4) {
            return;
          }

          // The request errored out and we didn't get a response, this will be
          // handled by onerror instead
          // With one exception: request that using file: protocol, most browsers
          // will return status as 0 even though it's a successful request
          if (
            request.status === 0 &&
            !(request.responseURL && request.responseURL.startsWith('file:'))
          ) {
            return;
          }
          // readystate handler is calling before onerror or ontimeout handlers,
          // so we should call onloadend on the next 'tick'
          setTimeout(onloadend);
        };
      }

      // Handle browser request cancellation (as opposed to a manual cancellation)
      request.onabort = function handleAbort() {
        if (!request) {
          return;
        }

        reject(new AxiosError$1('Request aborted', AxiosError$1.ECONNABORTED, config, request));
        done();

        // Clean up request
        request = null;
      };

      // Handle low level network errors
      request.onerror = function handleError(event) {
        // Browsers deliver a ProgressEvent in XHR onerror
        // (message may be empty; when present, surface it)
        // See https://developer.mozilla.org/docs/Web/API/XMLHttpRequest/error_event
        const msg = event && event.message ? event.message : 'Network Error';
        const err = new AxiosError$1(msg, AxiosError$1.ERR_NETWORK, config, request);
        // attach the underlying event for consumers who want details
        err.event = event || null;
        reject(err);
        done();
        request = null;
      };

      // Handle timeout
      request.ontimeout = function handleTimeout() {
        let timeoutErrorMessage = _config.timeout
          ? 'timeout of ' + _config.timeout + 'ms exceeded'
          : 'timeout exceeded';
        const transitional = _config.transitional || transitionalDefaults;
        if (_config.timeoutErrorMessage) {
          timeoutErrorMessage = _config.timeoutErrorMessage;
        }
        reject(
          new AxiosError$1(
            timeoutErrorMessage,
            transitional.clarifyTimeoutError ? AxiosError$1.ETIMEDOUT : AxiosError$1.ECONNABORTED,
            config,
            request
          )
        );
        done();

        // Clean up request
        request = null;
      };

      // Remove Content-Type if data is undefined
      requestData === undefined && requestHeaders.setContentType(null);

      // Add headers to the request
      if ('setRequestHeader' in request) {
        utils$1.forEach(toByteStringHeaderObject(requestHeaders), function setRequestHeader(val, key) {
          request.setRequestHeader(key, val);
        });
      }

      // Add withCredentials to request if needed
      if (!utils$1.isUndefined(_config.withCredentials)) {
        request.withCredentials = !!_config.withCredentials;
      }

      // Add responseType to request if needed
      if (responseType && responseType !== 'json') {
        request.responseType = _config.responseType;
      }

      // Handle progress if needed
      if (onDownloadProgress) {
        [downloadThrottled, flushDownload] = progressEventReducer(onDownloadProgress, true);
        request.addEventListener('progress', downloadThrottled);
      }

      // Not all browsers support upload events
      if (onUploadProgress && request.upload) {
        [uploadThrottled, flushUpload] = progressEventReducer(onUploadProgress);

        request.upload.addEventListener('progress', uploadThrottled);

        request.upload.addEventListener('loadend', flushUpload);
      }

      if (_config.cancelToken || _config.signal) {
        // Handle cancellation
        // eslint-disable-next-line func-names
        onCanceled = (cancel) => {
          if (!request) {
            return;
          }
          reject(!cancel || cancel.type ? new CanceledError$1(null, config, request) : cancel);
          request.abort();
          done();
          request = null;
        };

        _config.cancelToken && _config.cancelToken.subscribe(onCanceled);
        if (_config.signal) {
          _config.signal.aborted
            ? onCanceled()
            : _config.signal.addEventListener('abort', onCanceled);
        }
      }

      const protocol = parseProtocol(_config.url);

      if (protocol && !platform.protocols.includes(protocol)) {
        reject(
          new AxiosError$1(
            'Unsupported protocol ' + protocol + ':',
            AxiosError$1.ERR_BAD_REQUEST,
            config
          )
        );
        return;
      }

      // Send the request
      request.send(requestData || null);
    });
  };

const composeSignals = (signals, timeout) => {
  signals = signals ? signals.filter(Boolean) : [];

  if (!timeout && !signals.length) {
    return;
  }

  const controller = new AbortController();

  let aborted = false;

  const onabort = function (reason) {
    if (!aborted) {
      aborted = true;
      unsubscribe();
      const err = reason instanceof Error ? reason : this.reason;
      controller.abort(
        err instanceof AxiosError$1
          ? err
          : new CanceledError$1(err instanceof Error ? err.message : err)
      );
    }
  };

  let timer =
    timeout &&
    setTimeout(() => {
      timer = null;
      onabort(new AxiosError$1(`timeout of ${timeout}ms exceeded`, AxiosError$1.ETIMEDOUT));
    }, timeout);

  const unsubscribe = () => {
    if (!signals) { return; }
    timer && clearTimeout(timer);
    timer = null;
    signals.forEach((signal) => {
      signal.unsubscribe
        ? signal.unsubscribe(onabort)
        : signal.removeEventListener('abort', onabort);
    });
    signals = null;
  };

  signals.forEach((signal) => signal.addEventListener('abort', onabort));

  const { signal } = controller;

  signal.unsubscribe = () => utils$1.asap(unsubscribe);

  return signal;
};

const streamChunk = function* (chunk, chunkSize) {
  let len = chunk.byteLength;

  if (len < chunkSize) {
    yield chunk;
    return;
  }

  let pos = 0;
  let end;

  while (pos < len) {
    end = pos + chunkSize;
    yield chunk.slice(pos, end);
    pos = end;
  }
};

const readBytes = async function* (iterable, chunkSize) {
  for await (const chunk of readStream(iterable)) {
    yield* streamChunk(chunk, chunkSize);
  }
};

const readStream = async function* (stream) {
  if (stream[Symbol.asyncIterator]) {
    yield* stream;
    return;
  }

  const reader = stream.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      yield value;
    }
  } finally {
    await reader.cancel();
  }
};

const trackStream = (stream, chunkSize, onProgress, onFinish) => {
  const iterator = readBytes(stream, chunkSize);

  let bytes = 0;
  let done;
  let _onFinish = (e) => {
    if (!done) {
      done = true;
      onFinish && onFinish(e);
    }
  };

  return new ReadableStream(
    {
      async pull(controller) {
        try {
          const { done, value } = await iterator.next();

          if (done) {
            _onFinish();
            controller.close();
            return;
          }

          let len = value.byteLength;
          if (onProgress) {
            let loadedBytes = (bytes += len);
            onProgress(loadedBytes);
          }
          controller.enqueue(new Uint8Array(value));
        } catch (err) {
          _onFinish(err);
          throw err;
        }
      },
      cancel(reason) {
        _onFinish(reason);
        return iterator.return();
      },
    },
    {
      highWaterMark: 2,
    }
  );
};

/**
 * Estimate decoded byte length of a data:// URL *without* allocating large buffers.
 * - For base64: compute exact decoded size using length and padding;
 *               handle %XX at the character-count level (no string allocation).
 * - For non-base64: use UTF-8 byteLength of the encoded body as a safe upper bound.
 *
 * @param {string} url
 * @returns {number}
 */
function estimateDataURLDecodedBytes(url) {
  if (!url || typeof url !== 'string') return 0;
  if (!url.startsWith('data:')) return 0;

  const comma = url.indexOf(',');
  if (comma < 0) return 0;

  const meta = url.slice(5, comma);
  const body = url.slice(comma + 1);
  const isBase64 = /;base64/i.test(meta);

  if (isBase64) {
    let effectiveLen = body.length;
    const len = body.length; // cache length

    for (let i = 0; i < len; i++) {
      if (body.charCodeAt(i) === 37 /* '%' */ && i + 2 < len) {
        const a = body.charCodeAt(i + 1);
        const b = body.charCodeAt(i + 2);
        const isHex =
          ((a >= 48 && a <= 57) || (a >= 65 && a <= 70) || (a >= 97 && a <= 102)) &&
          ((b >= 48 && b <= 57) || (b >= 65 && b <= 70) || (b >= 97 && b <= 102));

        if (isHex) {
          effectiveLen -= 2;
          i += 2;
        }
      }
    }

    let pad = 0;
    let idx = len - 1;

    const tailIsPct3D = (j) =>
      j >= 2 &&
      body.charCodeAt(j - 2) === 37 && // '%'
      body.charCodeAt(j - 1) === 51 && // '3'
      (body.charCodeAt(j) === 68 || body.charCodeAt(j) === 100); // 'D' or 'd'

    if (idx >= 0) {
      if (body.charCodeAt(idx) === 61 /* '=' */) {
        pad++;
        idx--;
      } else if (tailIsPct3D(idx)) {
        pad++;
        idx -= 3;
      }
    }

    if (pad === 1 && idx >= 0) {
      if (body.charCodeAt(idx) === 61 /* '=' */) {
        pad++;
      } else if (tailIsPct3D(idx)) {
        pad++;
      }
    }

    const groups = Math.floor(effectiveLen / 4);
    const bytes = groups * 3 - (pad || 0);
    return bytes > 0 ? bytes : 0;
  }

  if (typeof Buffer !== 'undefined' && typeof Buffer.byteLength === 'function') {
    return Buffer.byteLength(body, 'utf8');
  }

  // Compute UTF-8 byte length directly from UTF-16 code units without allocating
  // a byte buffer (TextEncoder.encode would defeat the DoS guard on large bodies).
  // Using body.length here would undercount non-ASCII (e.g. '€' is 1 code unit
  // but 3 UTF-8 bytes).
  let bytes = 0;
  for (let i = 0, len = body.length; i < len; i++) {
    const c = body.charCodeAt(i);
    if (c < 0x80) {
      bytes += 1;
    } else if (c < 0x800) {
      bytes += 2;
    } else if (c >= 0xd800 && c <= 0xdbff && i + 1 < len) {
      const next = body.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i++;
      } else {
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

const VERSION$1 = "1.17.0";

const DEFAULT_CHUNK_SIZE = 64 * 1024;

const { isFunction } = utils$1;

/**
 * Encode a UTF-8 string to a Latin-1 byte string for use with btoa().
 * This is a modern replacement for the deprecated unescape(encodeURIComponent(str)) pattern.
 *
 * @param {string} str The string to encode
 *
 * @returns {string} UTF-8 bytes as a Latin-1 string
 */
const encodeUTF8 = (str) =>
  encodeURIComponent(str).replace(/%([0-9A-F]{2})/gi, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

// Node's WHATWG URL parser returns `username` and `password` percent-encoded.
// Decode before composing the `auth` option so credentials such as
// `my%40email.com:pass` are sent as `my@email.com:pass`. Falls back to the
// original value for malformed input so a bad encoding never throws.
const decodeURIComponentSafe = (value) => {
  if (!utils$1.isString(value)) {
    return value;
  }

  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const test = (fn, ...args) => {
  try {
    return !!fn(...args);
  } catch (e) {
    return false;
  }
};

const maybeWithAuthCredentials = (url) => {
  const protocolIndex = url.indexOf('://');
  let urlToCheck = url;
  if (protocolIndex !== -1) {
    urlToCheck = urlToCheck.slice(protocolIndex + 3);
  }
  return urlToCheck.includes('@') || urlToCheck.includes(':');
};

const factory = (env) => {
  const globalObject =
    utils$1.global !== undefined && utils$1.global !== null
      ? utils$1.global
      : globalThis;
  const { ReadableStream, TextEncoder } = globalObject;

  env = utils$1.merge.call(
    {
      skipUndefined: true,
    },
    {
      Request: globalObject.Request,
      Response: globalObject.Response,
    },
    env
  );

  const { fetch: envFetch, Request, Response } = env;
  const isFetchSupported = envFetch ? isFunction(envFetch) : typeof fetch === 'function';
  const isRequestSupported = isFunction(Request);
  const isResponseSupported = isFunction(Response);

  if (!isFetchSupported) {
    return false;
  }

  const isReadableStreamSupported = isFetchSupported && isFunction(ReadableStream);

  const encodeText =
    isFetchSupported &&
    (typeof TextEncoder === 'function'
      ? (
          (encoder) => (str) =>
            encoder.encode(str)
        )(new TextEncoder())
      : async (str) => new Uint8Array(await new Request(str).arrayBuffer()));

  const supportsRequestStream =
    isRequestSupported &&
    isReadableStreamSupported &&
    test(() => {
      let duplexAccessed = false;

      const request = new Request(platform.origin, {
        body: new ReadableStream(),
        method: 'POST',
        get duplex() {
          duplexAccessed = true;
          return 'half';
        },
      });

      const hasContentType = request.headers.has('Content-Type');

      if (request.body != null) {
        request.body.cancel();
      }

      return duplexAccessed && !hasContentType;
    });

  const supportsResponseStream =
    isResponseSupported &&
    isReadableStreamSupported &&
    test(() => utils$1.isReadableStream(new Response('').body));

  const resolvers = {
    stream: supportsResponseStream && ((res) => res.body),
  };

  isFetchSupported &&
    (() => {
      ['text', 'arrayBuffer', 'blob', 'formData', 'stream'].forEach((type) => {
        !resolvers[type] &&
          (resolvers[type] = (res, config) => {
            let method = res && res[type];

            if (method) {
              return method.call(res);
            }

            throw new AxiosError$1(
              `Response type '${type}' is not supported`,
              AxiosError$1.ERR_NOT_SUPPORT,
              config
            );
          });
      });
    })();

  const getBodyLength = async (body) => {
    if (body == null) {
      return 0;
    }

    if (utils$1.isBlob(body)) {
      return body.size;
    }

    if (utils$1.isSpecCompliantForm(body)) {
      const _request = new Request(platform.origin, {
        method: 'POST',
        body,
      });
      return (await _request.arrayBuffer()).byteLength;
    }

    if (utils$1.isArrayBufferView(body) || utils$1.isArrayBuffer(body)) {
      return body.byteLength;
    }

    if (utils$1.isURLSearchParams(body)) {
      body = body + '';
    }

    if (utils$1.isString(body)) {
      return (await encodeText(body)).byteLength;
    }
  };

  const resolveBodyLength = async (headers, body) => {
    const length = utils$1.toFiniteNumber(headers.getContentLength());

    return length == null ? getBodyLength(body) : length;
  };

  return async (config) => {
    let {
      url,
      method,
      data,
      signal,
      cancelToken,
      timeout,
      onDownloadProgress,
      onUploadProgress,
      responseType,
      headers,
      withCredentials = 'same-origin',
      fetchOptions,
      maxContentLength,
      maxBodyLength,
    } = resolveConfig(config);

    const hasMaxContentLength = utils$1.isNumber(maxContentLength) && maxContentLength > -1;
    const hasMaxBodyLength = utils$1.isNumber(maxBodyLength) && maxBodyLength > -1;
    const own = (key) => (utils$1.hasOwnProp(config, key) ? config[key] : undefined);

    let _fetch = envFetch || fetch;

    responseType = responseType ? (responseType + '').toLowerCase() : 'text';

    let composedSignal = composeSignals(
      [signal, cancelToken && cancelToken.toAbortSignal()],
      timeout
    );

    let request = null;

    const unsubscribe =
      composedSignal &&
      composedSignal.unsubscribe &&
      (() => {
        composedSignal.unsubscribe();
      });

    let requestContentLength;

    try {
      // HTTP basic authentication
      let auth = undefined;
      const configAuth = own('auth');

      if (configAuth) {
        const username = configAuth.username || '';
        const password = configAuth.password || '';
        auth = {
          username,
          password
        };
      }

      if (maybeWithAuthCredentials(url)) {
        const parsedURL = new URL(url, platform.origin);

        if (!auth && (parsedURL.username || parsedURL.password)) {
          const urlUsername = decodeURIComponentSafe(parsedURL.username);
          const urlPassword = decodeURIComponentSafe(parsedURL.password);
          auth = {
            username: urlUsername,
            password: urlPassword
          };
        }

        if (parsedURL.username || parsedURL.password) {
          parsedURL.username = '';
          parsedURL.password = '';
          url = parsedURL.href;
        }
      }

      if (auth) {
        headers.delete('authorization');
        headers.set(
          'Authorization',
          'Basic ' + btoa(encodeUTF8((auth.username || '') + ':' + (auth.password || '')))
        );
      }

      // Enforce maxContentLength for data: URLs up-front so we never materialize
      // an oversized payload. The HTTP adapter applies the same check (see http.js
      // "if (protocol === 'data:')" branch).
      if (hasMaxContentLength && typeof url === 'string' && url.startsWith('data:')) {
        const estimated = estimateDataURLDecodedBytes(url);
        if (estimated > maxContentLength) {
          throw new AxiosError$1(
            'maxContentLength size of ' + maxContentLength + ' exceeded',
            AxiosError$1.ERR_BAD_RESPONSE,
            config,
            request
          );
        }
      }

      // Enforce maxBodyLength against the outbound request body before dispatch.
      // Mirrors http.js behavior (ERR_BAD_REQUEST / 'Request body larger than
      // maxBodyLength limit'). Skip when the body length cannot be determined
      // (e.g. a live ReadableStream supplied by the caller).
      if (hasMaxBodyLength && method !== 'get' && method !== 'head') {
        const outboundLength = await resolveBodyLength(headers, data);
        if (
          typeof outboundLength === 'number' &&
          isFinite(outboundLength) &&
          outboundLength > maxBodyLength
        ) {
          throw new AxiosError$1(
            'Request body larger than maxBodyLength limit',
            AxiosError$1.ERR_BAD_REQUEST,
            config,
            request
          );
        }
      }

      if (
        onUploadProgress &&
        supportsRequestStream &&
        method !== 'get' &&
        method !== 'head' &&
        (requestContentLength = await resolveBodyLength(headers, data)) !== 0
      ) {
        let _request = new Request(url, {
          method: 'POST',
          body: data,
          duplex: 'half',
        });

        let contentTypeHeader;

        if (utils$1.isFormData(data) && (contentTypeHeader = _request.headers.get('content-type'))) {
          headers.setContentType(contentTypeHeader);
        }

        if (_request.body) {
          const [onProgress, flush] = progressEventDecorator(
            requestContentLength,
            progressEventReducer(asyncDecorator(onUploadProgress))
          );

          data = trackStream(_request.body, DEFAULT_CHUNK_SIZE, onProgress, flush);
        }
      }

      if (!utils$1.isString(withCredentials)) {
        withCredentials = withCredentials ? 'include' : 'omit';
      }

      // Cloudflare Workers throws when credentials are defined
      // see https://github.com/cloudflare/workerd/issues/902
      const isCredentialsSupported = isRequestSupported && 'credentials' in Request.prototype;

      // If data is FormData and Content-Type is multipart/form-data without boundary,
      // delete it so fetch can set it correctly with the boundary
      if (utils$1.isFormData(data)) {
        const contentType = headers.getContentType();
        if (
          contentType &&
          /^multipart\/form-data/i.test(contentType) &&
          !/boundary=/i.test(contentType)
        ) {
          headers.delete('content-type');
        }
      }

      // Set User-Agent header if not already set (fetch defaults to 'node' in Node.js)
      headers.set('User-Agent', 'axios/' + VERSION$1, false);

      const resolvedOptions = {
        ...fetchOptions,
        signal: composedSignal,
        method: method.toUpperCase(),
        headers: toByteStringHeaderObject(headers.normalize()),
        body: data,
        duplex: 'half',
        credentials: isCredentialsSupported ? withCredentials : undefined,
      };

      request = isRequestSupported && new Request(url, resolvedOptions);

      let response = await (isRequestSupported
        ? _fetch(request, fetchOptions)
        : _fetch(url, resolvedOptions));

      // Cheap pre-check: if the server honestly declares a content-length that
      // already exceeds the cap, reject before we start streaming.
      if (hasMaxContentLength) {
        const declaredLength = utils$1.toFiniteNumber(response.headers.get('content-length'));
        if (declaredLength != null && declaredLength > maxContentLength) {
          throw new AxiosError$1(
            'maxContentLength size of ' + maxContentLength + ' exceeded',
            AxiosError$1.ERR_BAD_RESPONSE,
            config,
            request
          );
        }
      }

      const isStreamResponse =
        supportsResponseStream && (responseType === 'stream' || responseType === 'response');

      if (
        supportsResponseStream &&
        response.body &&
        (onDownloadProgress || hasMaxContentLength || (isStreamResponse && unsubscribe))
      ) {
        const options = {};

        ['status', 'statusText', 'headers'].forEach((prop) => {
          options[prop] = response[prop];
        });

        const responseContentLength = utils$1.toFiniteNumber(response.headers.get('content-length'));

        const [onProgress, flush] =
          (onDownloadProgress &&
            progressEventDecorator(
              responseContentLength,
              progressEventReducer(asyncDecorator(onDownloadProgress), true)
            )) ||
          [];

        let bytesRead = 0;
        const onChunkProgress = (loadedBytes) => {
          if (hasMaxContentLength) {
            bytesRead = loadedBytes;
            if (bytesRead > maxContentLength) {
              throw new AxiosError$1(
                'maxContentLength size of ' + maxContentLength + ' exceeded',
                AxiosError$1.ERR_BAD_RESPONSE,
                config,
                request
              );
            }
          }
          onProgress && onProgress(loadedBytes);
        };

        response = new Response(
          trackStream(response.body, DEFAULT_CHUNK_SIZE, onChunkProgress, () => {
            flush && flush();
            unsubscribe && unsubscribe();
          }),
          options
        );
      }

      responseType = responseType || 'text';

      let responseData = await resolvers[utils$1.findKey(resolvers, responseType) || 'text'](
        response,
        config
      );

      // Fallback enforcement for environments without ReadableStream support
      // (legacy runtimes). Detect materialized size from typed output; skip
      // streams/Response passthrough since the user will read those themselves.
      if (hasMaxContentLength && !supportsResponseStream && !isStreamResponse) {
        let materializedSize;
        if (responseData != null) {
          if (typeof responseData.byteLength === 'number') {
            materializedSize = responseData.byteLength;
          } else if (typeof responseData.size === 'number') {
            materializedSize = responseData.size;
          } else if (typeof responseData === 'string') {
            materializedSize =
              typeof TextEncoder === 'function'
                ? new TextEncoder().encode(responseData).byteLength
                : responseData.length;
          }
        }
        if (typeof materializedSize === 'number' && materializedSize > maxContentLength) {
          throw new AxiosError$1(
            'maxContentLength size of ' + maxContentLength + ' exceeded',
            AxiosError$1.ERR_BAD_RESPONSE,
            config,
            request
          );
        }
      }

      !isStreamResponse && unsubscribe && unsubscribe();

      return await new Promise((resolve, reject) => {
        settle(resolve, reject, {
          data: responseData,
          headers: AxiosHeaders$1.from(response.headers),
          status: response.status,
          statusText: response.statusText,
          config,
          request,
        });
      });
    } catch (err) {
      unsubscribe && unsubscribe();

      // Safari can surface fetch aborts as a DOMException-like object whose
      // branded getters throw. Prefer our composed signal reason before reading
      // the caught error, preserving timeout vs cancellation semantics.
      if (composedSignal && composedSignal.aborted && composedSignal.reason instanceof AxiosError$1) {
        const canceledError = composedSignal.reason;
        canceledError.config = config;
        request && (canceledError.request = request);
        err !== canceledError && (canceledError.cause = err);
        throw canceledError;
      }

      if (err && err.name === 'TypeError' && /Load failed|fetch/i.test(err.message)) {
        throw Object.assign(
          new AxiosError$1(
            'Network Error',
            AxiosError$1.ERR_NETWORK,
            config,
            request,
            err && err.response
          ),
          {
            cause: err.cause || err,
          }
        );
      }

      throw AxiosError$1.from(err, err && err.code, config, request, err && err.response);
    }
  };
};

const seedCache = new Map();

const getFetch = (config) => {
  let env = (config && config.env) || {};
  const { fetch, Request, Response } = env;
  const seeds = [Request, Response, fetch];

  let len = seeds.length,
    i = len,
    seed,
    target,
    map = seedCache;

  while (i--) {
    seed = seeds[i];
    target = map.get(seed);

    target === undefined && map.set(seed, (target = i ? new Map() : factory(env)));

    map = target;
  }

  return target;
};

getFetch();

/**
 * Known adapters mapping.
 * Provides environment-specific adapters for Axios:
 * - `http` for Node.js
 * - `xhr` for browsers
 * - `fetch` for fetch API-based requests
 *
 * @type {Object<string, Function|Object>}
 */
const knownAdapters = {
  http: httpAdapter,
  xhr: xhrAdapter,
  fetch: {
    get: getFetch,
  },
};

// Assign adapter names for easier debugging and identification
utils$1.forEach(knownAdapters, (fn, value) => {
  if (fn) {
    try {
      // Null-proto descriptors so a polluted Object.prototype.get cannot turn
      // these data descriptors into accessor descriptors on the way in.
      Object.defineProperty(fn, 'name', { __proto__: null, value });
    } catch (e) {
      // eslint-disable-next-line no-empty
    }
    Object.defineProperty(fn, 'adapterName', { __proto__: null, value });
  }
});

/**
 * Render a rejection reason string for unknown or unsupported adapters
 *
 * @param {string} reason
 * @returns {string}
 */
const renderReason = (reason) => `- ${reason}`;

/**
 * Check if the adapter is resolved (function, null, or false)
 *
 * @param {Function|null|false} adapter
 * @returns {boolean}
 */
const isResolvedHandle = (adapter) =>
  utils$1.isFunction(adapter) || adapter === null || adapter === false;

/**
 * Get the first suitable adapter from the provided list.
 * Tries each adapter in order until a supported one is found.
 * Throws an AxiosError if no adapter is suitable.
 *
 * @param {Array<string|Function>|string|Function} adapters - Adapter(s) by name or function.
 * @param {Object} config - Axios request configuration
 * @throws {AxiosError} If no suitable adapter is available
 * @returns {Function} The resolved adapter function
 */
function getAdapter$1(adapters, config) {
  adapters = utils$1.isArray(adapters) ? adapters : [adapters];

  const { length } = adapters;
  let nameOrAdapter;
  let adapter;

  const rejectedReasons = {};

  for (let i = 0; i < length; i++) {
    nameOrAdapter = adapters[i];
    let id;

    adapter = nameOrAdapter;

    if (!isResolvedHandle(nameOrAdapter)) {
      adapter = knownAdapters[(id = String(nameOrAdapter)).toLowerCase()];

      if (adapter === undefined) {
        throw new AxiosError$1(`Unknown adapter '${id}'`);
      }
    }

    if (adapter && (utils$1.isFunction(adapter) || (adapter = adapter.get(config)))) {
      break;
    }

    rejectedReasons[id || '#' + i] = adapter;
  }

  if (!adapter) {
    const reasons = Object.entries(rejectedReasons).map(
      ([id, state]) =>
        `adapter ${id} ` +
        (state === false ? 'is not supported by the environment' : 'is not available in the build')
    );

    let s = length
      ? reasons.length > 1
        ? 'since :\n' + reasons.map(renderReason).join('\n')
        : ' ' + renderReason(reasons[0])
      : 'as no adapter specified';

    throw new AxiosError$1(
      `There is no suitable adapter to dispatch the request ` + s,
      'ERR_NOT_SUPPORT'
    );
  }

  return adapter;
}

/**
 * Exports Axios adapters and utility to resolve an adapter
 */
var adapters = {
  /**
   * Resolve an adapter from a list of adapter names or functions.
   * @type {Function}
   */
  getAdapter: getAdapter$1,

  /**
   * Exposes all known adapters
   * @type {Object<string, Function|Object>}
   */
  adapters: knownAdapters,
};

/**
 * Throws a `CanceledError` if cancellation has been requested.
 *
 * @param {Object} config The config that is to be used for the request
 *
 * @returns {void}
 */
function throwIfCancellationRequested(config) {
  if (config.cancelToken) {
    config.cancelToken.throwIfRequested();
  }

  if (config.signal && config.signal.aborted) {
    throw new CanceledError$1(null, config);
  }
}

/**
 * Dispatch a request to the server using the configured adapter.
 *
 * @param {object} config The config that is to be used for the request
 *
 * @returns {Promise} The Promise to be fulfilled
 */
function dispatchRequest(config) {
  throwIfCancellationRequested(config);

  config.headers = AxiosHeaders$1.from(config.headers);

  // Transform request data
  config.data = transformData.call(config, config.transformRequest);

  if (['post', 'put', 'patch'].indexOf(config.method) !== -1) {
    config.headers.setContentType('application/x-www-form-urlencoded', false);
  }

  const adapter = adapters.getAdapter(config.adapter || defaults.adapter, config);

  return adapter(config).then(
    function onAdapterResolution(response) {
      throwIfCancellationRequested(config);

      // Expose the current response on config so that transformResponse can
      // attach it to any AxiosError it throws (e.g. on JSON parse failure).
      // We clean it up afterwards to avoid polluting the config object.
      config.response = response;
      try {
        response.data = transformData.call(config, config.transformResponse, response);
      } finally {
        delete config.response;
      }

      response.headers = AxiosHeaders$1.from(response.headers);

      return response;
    },
    function onAdapterRejection(reason) {
      if (!isCancel$1(reason)) {
        throwIfCancellationRequested(config);

        // Transform response data
        if (reason && reason.response) {
          config.response = reason.response;
          try {
            reason.response.data = transformData.call(
              config,
              config.transformResponse,
              reason.response
            );
          } finally {
            delete config.response;
          }
          reason.response.headers = AxiosHeaders$1.from(reason.response.headers);
        }
      }

      return Promise.reject(reason);
    }
  );
}

const validators$1 = {};

// eslint-disable-next-line func-names
['object', 'boolean', 'number', 'function', 'string', 'symbol'].forEach((type, i) => {
  validators$1[type] = function validator(thing) {
    return typeof thing === type || 'a' + (i < 1 ? 'n ' : ' ') + type;
  };
});

const deprecatedWarnings = {};

/**
 * Transitional option validator
 *
 * @param {function|boolean?} validator - set to false if the transitional option has been removed
 * @param {string?} version - deprecated version / removed since version
 * @param {string?} message - some message with additional info
 *
 * @returns {function}
 */
validators$1.transitional = function transitional(validator, version, message) {
  function formatMessage(opt, desc) {
    return (
      '[Axios v' +
      VERSION$1 +
      "] Transitional option '" +
      opt +
      "'" +
      desc +
      (message ? '. ' + message : '')
    );
  }

  // eslint-disable-next-line func-names
  return (value, opt, opts) => {
    if (validator === false) {
      throw new AxiosError$1(
        formatMessage(opt, ' has been removed' + (version ? ' in ' + version : '')),
        AxiosError$1.ERR_DEPRECATED
      );
    }

    if (version && !deprecatedWarnings[opt]) {
      deprecatedWarnings[opt] = true;
      // eslint-disable-next-line no-console
      console.warn(
        formatMessage(
          opt,
          ' has been deprecated since v' + version + ' and will be removed in the near future'
        )
      );
    }

    return validator ? validator(value, opt, opts) : true;
  };
};

validators$1.spelling = function spelling(correctSpelling) {
  return (value, opt) => {
    // eslint-disable-next-line no-console
    console.warn(`${opt} is likely a misspelling of ${correctSpelling}`);
    return true;
  };
};

/**
 * Assert object's properties type
 *
 * @param {object} options
 * @param {object} schema
 * @param {boolean?} allowUnknown
 *
 * @returns {object}
 */

function assertOptions(options, schema, allowUnknown) {
  if (typeof options !== 'object') {
    throw new AxiosError$1('options must be an object', AxiosError$1.ERR_BAD_OPTION_VALUE);
  }
  const keys = Object.keys(options);
  let i = keys.length;
  while (i-- > 0) {
    const opt = keys[i];
    // Use hasOwnProperty so a polluted Object.prototype.<opt> cannot supply
    // a non-function validator and cause a TypeError.
    const validator = Object.prototype.hasOwnProperty.call(schema, opt) ? schema[opt] : undefined;
    if (validator) {
      const value = options[opt];
      const result = value === undefined || validator(value, opt, options);
      if (result !== true) {
        throw new AxiosError$1(
          'option ' + opt + ' must be ' + result,
          AxiosError$1.ERR_BAD_OPTION_VALUE
        );
      }
      continue;
    }
    if (allowUnknown !== true) {
      throw new AxiosError$1('Unknown option ' + opt, AxiosError$1.ERR_BAD_OPTION);
    }
  }
}

var validator = {
  assertOptions,
  validators: validators$1,
};

const validators = validator.validators;

/**
 * Create a new instance of Axios
 *
 * @param {Object} instanceConfig The default config for the instance
 *
 * @return {Axios} A new instance of Axios
 */
let Axios$1 = class Axios {
  constructor(instanceConfig) {
    this.defaults = instanceConfig || {};
    this.interceptors = {
      request: new InterceptorManager(),
      response: new InterceptorManager(),
    };
  }

  /**
   * Dispatch a request
   *
   * @param {String|Object} configOrUrl The config specific for this request (merged with this.defaults)
   * @param {?Object} config
   *
   * @returns {Promise} The Promise to be fulfilled
   */
  async request(configOrUrl, config) {
    try {
      return await this._request(configOrUrl, config);
    } catch (err) {
      if (err instanceof Error) {
        let dummy = {};

        Error.captureStackTrace ? Error.captureStackTrace(dummy) : (dummy = new Error());

        // slice off the Error: ... line
        const stack = (() => {
          if (!dummy.stack) {
            return '';
          }

          const firstNewlineIndex = dummy.stack.indexOf('\n');

          return firstNewlineIndex === -1 ? '' : dummy.stack.slice(firstNewlineIndex + 1);
        })();
        try {
          if (!err.stack) {
            err.stack = stack;
            // match without the 2 top stack lines
          } else if (stack) {
            const firstNewlineIndex = stack.indexOf('\n');
            const secondNewlineIndex =
              firstNewlineIndex === -1 ? -1 : stack.indexOf('\n', firstNewlineIndex + 1);
            const stackWithoutTwoTopLines =
              secondNewlineIndex === -1 ? '' : stack.slice(secondNewlineIndex + 1);

            if (!String(err.stack).endsWith(stackWithoutTwoTopLines)) {
              err.stack += '\n' + stack;
            }
          }
        } catch (e) {
          // ignore the case where "stack" is an un-writable property
        }
      }

      throw err;
    }
  }

  _request(configOrUrl, config) {
    /*eslint no-param-reassign:0*/
    // Allow for axios('example/url'[, config]) a la fetch API
    if (typeof configOrUrl === 'string') {
      config = config || {};
      config.url = configOrUrl;
    } else {
      config = configOrUrl || {};
    }

    config = mergeConfig$1(this.defaults, config);

    const { transitional, paramsSerializer, headers } = config;

    if (transitional !== undefined) {
      validator.assertOptions(
        transitional,
        {
          silentJSONParsing: validators.transitional(validators.boolean),
          forcedJSONParsing: validators.transitional(validators.boolean),
          clarifyTimeoutError: validators.transitional(validators.boolean),
          legacyInterceptorReqResOrdering: validators.transitional(validators.boolean),
          advertiseZstdAcceptEncoding: validators.transitional(validators.boolean),
        },
        false
      );
    }

    if (paramsSerializer != null) {
      if (utils$1.isFunction(paramsSerializer)) {
        config.paramsSerializer = {
          serialize: paramsSerializer,
        };
      } else {
        validator.assertOptions(
          paramsSerializer,
          {
            encode: validators.function,
            serialize: validators.function,
          },
          true
        );
      }
    }

    // Set config.allowAbsoluteUrls
    if (config.allowAbsoluteUrls !== undefined) ; else if (this.defaults.allowAbsoluteUrls !== undefined) {
      config.allowAbsoluteUrls = this.defaults.allowAbsoluteUrls;
    } else {
      config.allowAbsoluteUrls = true;
    }

    validator.assertOptions(
      config,
      {
        baseUrl: validators.spelling('baseURL'),
        withXsrfToken: validators.spelling('withXSRFToken'),
      },
      true
    );

    // Set config.method
    config.method = (config.method || this.defaults.method || 'get').toLowerCase();

    // Flatten headers
    let contextHeaders = headers && utils$1.merge(headers.common, headers[config.method]);

    headers &&
      utils$1.forEach(['delete', 'get', 'head', 'post', 'put', 'patch', 'query', 'common'], (method) => {
        delete headers[method];
      });

    config.headers = AxiosHeaders$1.concat(contextHeaders, headers);

    // filter out skipped interceptors
    const requestInterceptorChain = [];
    let synchronousRequestInterceptors = true;
    this.interceptors.request.forEach(function unshiftRequestInterceptors(interceptor) {
      if (typeof interceptor.runWhen === 'function' && interceptor.runWhen(config) === false) {
        return;
      }

      synchronousRequestInterceptors = synchronousRequestInterceptors && interceptor.synchronous;

      const transitional = config.transitional || transitionalDefaults;
      const legacyInterceptorReqResOrdering =
        transitional && transitional.legacyInterceptorReqResOrdering;

      if (legacyInterceptorReqResOrdering) {
        requestInterceptorChain.unshift(interceptor.fulfilled, interceptor.rejected);
      } else {
        requestInterceptorChain.push(interceptor.fulfilled, interceptor.rejected);
      }
    });

    const responseInterceptorChain = [];
    this.interceptors.response.forEach(function pushResponseInterceptors(interceptor) {
      responseInterceptorChain.push(interceptor.fulfilled, interceptor.rejected);
    });

    let promise;
    let i = 0;
    let len;

    if (!synchronousRequestInterceptors) {
      const chain = [dispatchRequest.bind(this), undefined];
      chain.unshift(...requestInterceptorChain);
      chain.push(...responseInterceptorChain);
      len = chain.length;

      promise = Promise.resolve(config);

      while (i < len) {
        promise = promise.then(chain[i++], chain[i++]);
      }

      return promise;
    }

    len = requestInterceptorChain.length;

    let newConfig = config;

    while (i < len) {
      const onFulfilled = requestInterceptorChain[i++];
      const onRejected = requestInterceptorChain[i++];
      try {
        newConfig = onFulfilled(newConfig);
      } catch (error) {
        onRejected.call(this, error);
        break;
      }
    }

    try {
      promise = dispatchRequest.call(this, newConfig);
    } catch (error) {
      return Promise.reject(error);
    }

    i = 0;
    len = responseInterceptorChain.length;

    while (i < len) {
      promise = promise.then(responseInterceptorChain[i++], responseInterceptorChain[i++]);
    }

    return promise;
  }

  getUri(config) {
    config = mergeConfig$1(this.defaults, config);
    const fullPath = buildFullPath(config.baseURL, config.url, config.allowAbsoluteUrls);
    return buildURL(fullPath, config.params, config.paramsSerializer);
  }
};

// Provide aliases for supported request methods
utils$1.forEach(['delete', 'get', 'head', 'options'], function forEachMethodNoData(method) {
  /*eslint func-names:0*/
  Axios$1.prototype[method] = function (url, config) {
    return this.request(
      mergeConfig$1(config || {}, {
        method,
        url,
        data: (config || {}).data,
      })
    );
  };
});

utils$1.forEach(['post', 'put', 'patch', 'query'], function forEachMethodWithData(method) {
  function generateHTTPMethod(isForm) {
    return function httpMethod(url, data, config) {
      return this.request(
        mergeConfig$1(config || {}, {
          method,
          headers: isForm
            ? {
                'Content-Type': 'multipart/form-data',
              }
            : {},
          url,
          data,
        })
      );
    };
  }

  Axios$1.prototype[method] = generateHTTPMethod();

  // QUERY is a safe/idempotent read method; multipart form bodies don't fit
  // its semantics, so no queryForm shorthand is generated.
  if (method !== 'query') {
    Axios$1.prototype[method + 'Form'] = generateHTTPMethod(true);
  }
});

/**
 * A `CancelToken` is an object that can be used to request cancellation of an operation.
 *
 * @param {Function} executor The executor function.
 *
 * @returns {CancelToken}
 */
let CancelToken$1 = class CancelToken {
  constructor(executor) {
    if (typeof executor !== 'function') {
      throw new TypeError('executor must be a function.');
    }

    let resolvePromise;

    this.promise = new Promise(function promiseExecutor(resolve) {
      resolvePromise = resolve;
    });

    const token = this;

    // eslint-disable-next-line func-names
    this.promise.then((cancel) => {
      if (!token._listeners) return;

      let i = token._listeners.length;

      while (i-- > 0) {
        token._listeners[i](cancel);
      }
      token._listeners = null;
    });

    // eslint-disable-next-line func-names
    this.promise.then = (onfulfilled) => {
      let _resolve;
      // eslint-disable-next-line func-names
      const promise = new Promise((resolve) => {
        token.subscribe(resolve);
        _resolve = resolve;
      }).then(onfulfilled);

      promise.cancel = function reject() {
        token.unsubscribe(_resolve);
      };

      return promise;
    };

    executor(function cancel(message, config, request) {
      if (token.reason) {
        // Cancellation has already been requested
        return;
      }

      token.reason = new CanceledError$1(message, config, request);
      resolvePromise(token.reason);
    });
  }

  /**
   * Throws a `CanceledError` if cancellation has been requested.
   */
  throwIfRequested() {
    if (this.reason) {
      throw this.reason;
    }
  }

  /**
   * Subscribe to the cancel signal
   */

  subscribe(listener) {
    if (this.reason) {
      listener(this.reason);
      return;
    }

    if (this._listeners) {
      this._listeners.push(listener);
    } else {
      this._listeners = [listener];
    }
  }

  /**
   * Unsubscribe from the cancel signal
   */

  unsubscribe(listener) {
    if (!this._listeners) {
      return;
    }
    const index = this._listeners.indexOf(listener);
    if (index !== -1) {
      this._listeners.splice(index, 1);
    }
  }

  toAbortSignal() {
    const controller = new AbortController();

    const abort = (err) => {
      controller.abort(err);
    };

    this.subscribe(abort);

    controller.signal.unsubscribe = () => this.unsubscribe(abort);

    return controller.signal;
  }

  /**
   * Returns an object that contains a new `CancelToken` and a function that, when called,
   * cancels the `CancelToken`.
   */
  static source() {
    let cancel;
    const token = new CancelToken(function executor(c) {
      cancel = c;
    });
    return {
      token,
      cancel,
    };
  }
};

/**
 * Syntactic sugar for invoking a function and expanding an array for arguments.
 *
 * Common use case would be to use `Function.prototype.apply`.
 *
 *  ```js
 *  function f(x, y, z) {}
 *  const args = [1, 2, 3];
 *  f.apply(null, args);
 *  ```
 *
 * With `spread` this example can be re-written.
 *
 *  ```js
 *  spread(function(x, y, z) {})([1, 2, 3]);
 *  ```
 *
 * @param {Function} callback
 *
 * @returns {Function}
 */
function spread$1(callback) {
  return function wrap(arr) {
    return callback.apply(null, arr);
  };
}

/**
 * Determines whether the payload is an error thrown by Axios
 *
 * @param {*} payload The value to test
 *
 * @returns {boolean} True if the payload is an error thrown by Axios, otherwise false
 */
function isAxiosError$1(payload) {
  return utils$1.isObject(payload) && payload.isAxiosError === true;
}

const HttpStatusCode$1 = {
  Continue: 100,
  SwitchingProtocols: 101,
  Processing: 102,
  EarlyHints: 103,
  Ok: 200,
  Created: 201,
  Accepted: 202,
  NonAuthoritativeInformation: 203,
  NoContent: 204,
  ResetContent: 205,
  PartialContent: 206,
  MultiStatus: 207,
  AlreadyReported: 208,
  ImUsed: 226,
  MultipleChoices: 300,
  MovedPermanently: 301,
  Found: 302,
  SeeOther: 303,
  NotModified: 304,
  UseProxy: 305,
  Unused: 306,
  TemporaryRedirect: 307,
  PermanentRedirect: 308,
  BadRequest: 400,
  Unauthorized: 401,
  PaymentRequired: 402,
  Forbidden: 403,
  NotFound: 404,
  MethodNotAllowed: 405,
  NotAcceptable: 406,
  ProxyAuthenticationRequired: 407,
  RequestTimeout: 408,
  Conflict: 409,
  Gone: 410,
  LengthRequired: 411,
  PreconditionFailed: 412,
  PayloadTooLarge: 413,
  UriTooLong: 414,
  UnsupportedMediaType: 415,
  RangeNotSatisfiable: 416,
  ExpectationFailed: 417,
  ImATeapot: 418,
  MisdirectedRequest: 421,
  UnprocessableEntity: 422,
  Locked: 423,
  FailedDependency: 424,
  TooEarly: 425,
  UpgradeRequired: 426,
  PreconditionRequired: 428,
  TooManyRequests: 429,
  RequestHeaderFieldsTooLarge: 431,
  UnavailableForLegalReasons: 451,
  InternalServerError: 500,
  NotImplemented: 501,
  BadGateway: 502,
  ServiceUnavailable: 503,
  GatewayTimeout: 504,
  HttpVersionNotSupported: 505,
  VariantAlsoNegotiates: 506,
  InsufficientStorage: 507,
  LoopDetected: 508,
  NotExtended: 510,
  NetworkAuthenticationRequired: 511,
  WebServerIsDown: 521,
  ConnectionTimedOut: 522,
  OriginIsUnreachable: 523,
  TimeoutOccurred: 524,
  SslHandshakeFailed: 525,
  InvalidSslCertificate: 526,
};

Object.entries(HttpStatusCode$1).forEach(([key, value]) => {
  HttpStatusCode$1[value] = key;
});

/**
 * Create an instance of Axios
 *
 * @param {Object} defaultConfig The default config for the instance
 *
 * @returns {Axios} A new instance of Axios
 */
function createInstance(defaultConfig) {
  const context = new Axios$1(defaultConfig);
  const instance = bind(Axios$1.prototype.request, context);

  // Copy axios.prototype to instance
  utils$1.extend(instance, Axios$1.prototype, context, { allOwnKeys: true });

  // Copy context to instance
  utils$1.extend(instance, context, null, { allOwnKeys: true });

  // Factory for creating new instances
  instance.create = function create(instanceConfig) {
    return createInstance(mergeConfig$1(defaultConfig, instanceConfig));
  };

  return instance;
}

// Create the default instance to be exported
const axios = createInstance(defaults);

// Expose Axios class to allow class inheritance
axios.Axios = Axios$1;

// Expose Cancel & CancelToken
axios.CanceledError = CanceledError$1;
axios.CancelToken = CancelToken$1;
axios.isCancel = isCancel$1;
axios.VERSION = VERSION$1;
axios.toFormData = toFormData$1;

// Expose AxiosError class
axios.AxiosError = AxiosError$1;

// alias for CanceledError for backward compatibility
axios.Cancel = axios.CanceledError;

// Expose all/spread
axios.all = function all(promises) {
  return Promise.all(promises);
};

axios.spread = spread$1;

// Expose isAxiosError
axios.isAxiosError = isAxiosError$1;

// Expose mergeConfig
axios.mergeConfig = mergeConfig$1;

axios.AxiosHeaders = AxiosHeaders$1;

axios.formToJSON = (thing) => formDataToJSON(utils$1.isHTMLForm(thing) ? new FormData(thing) : thing);

axios.getAdapter = adapters.getAdapter;

axios.HttpStatusCode = HttpStatusCode$1;

axios.default = axios;

// This module is intended to unwrap Axios default export as named.
// Keep top-level export same with static properties
// so that it can keep same with es module or cjs
const {
  Axios,
  AxiosError,
  CanceledError,
  isCancel,
  CancelToken,
  VERSION,
  all,
  Cancel,
  isAxiosError,
  spread,
  toFormData,
  AxiosHeaders,
  HttpStatusCode,
  formToJSON,
  getAdapter,
  mergeConfig,
  create,
} = axios;

/**
 * Serializes objects
 *
 * @name serialize
 * @param {Object} obj Object to be serialized
 * @param {Any} prefix
 * @returns {String} Serialized object
 * @example
 * digitransit-search-util.serialize(param1, param2);
 * //=true
 */
function serialize(obj, prefix) {
  if (!obj) {
    return '';
  }
  return Object.keys(obj).map(p => {
    const k = prefix || p;
    const v = obj[p];
    return typeof v === 'object' ? serialize(v, k) : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
  }).join('&');
}

/**
 *  Return Promise for a url json get request
 *
 * @name getJson
 * @param {String} url
 * @param {Array} params
 * @returns {Object} response
 * @example
 * digitransit-search-util.getJson(param1, param2);
 * //=response
 */

function getJson(url, params) {
  return axios.get(encodeURI(url) + (params ? (url.search(/\?/) === -1 ? '?' : '&') + serialize(params) : ''), {
    timeout: 10000,
    method: 'GET',
    headers: {
      Accept: 'application/json'
    }
  }).then(res => {
    return res.data;
  });
}

function ownKeys$5(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$5(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$5(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$5(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const DEFAULT_PELIAS_URL = 'https://api.digitransit.fi/geocoding/v1/search';
/**
 * <DESCRIPTION>
 *
 * @name getGeocodingResults
 * @param {String} searchString
 * @param {String} searchParams (Optional) Parameters appended to url, basically box / polygon to restrict search area
 * @param {String} lang (Optional) search language
 * @param {Object} focusPoint (Optional) Own Position (PELIAS API)
 * @param {String} sources (Optional) search sources (e.g OSM, GTFS..)
 * @param {Object} minimalRegexp (Optional) Regexp for testing
 * @param {*} geocodingLayers (Optional) Array of strings that is used to determine which layers is searched, e.g. ['venue', 'address', 'stop']
 * @returns {String} Results in JSON form
 * @example
 * digitransit-search-util.getGeocodingResults("result");
 * //= e.g. {text:"result"}
 */
function getGeocodingResults(searchString, searchParams, lang, focusPoint, sources, peliasUrl, minimalRegexp, geocodingLayers) {
  const text = searchString ? searchString.trim() : null;
  if (text === undefined || text === null || text.length < 1 || minimalRegexp && !minimalRegexp.test(text)) {
    return Promise.resolve([]);
  }
  const PELIAS_URL = peliasUrl || DEFAULT_PELIAS_URL;
  let opts = _objectSpread$5(_objectSpread$5(_objectSpread$5({
    text
  }, searchParams), focusPoint), {}, {
    lang
  });
  if (sources) {
    opts = _objectSpread$5(_objectSpread$5({}, opts), {}, {
      sources
    });
  }
  if (geocodingLayers) {
    const layers = geocodingLayers.toString();
    opts = _objectSpread$5(_objectSpread$5({}, opts), {}, {
      layers
    });
  }
  return getJson(PELIAS_URL, opts).then(res => {
    return res.features;
  });
}

function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function getStopsFromGeocoding(stops, URL_PELIAS_PLACE) {
  if (!stops || stops.length < 1) {
    return Promise.resolve([]);
  }
  let gids = '';
  const stopsWithGids = stops.map(stop => {
    const type = stop.type === 'stop' ? 'stop' : 'station';
    let gid = `gtfs${stop.gtfsId.split(':')[0].toLowerCase()}:${type}:GTFS:${stop.gtfsId}`;
    if (stop.code) {
      gid += `#${stop.code}`;
    }
    gids += `${gid},`;
    return _objectSpread$4(_objectSpread$4({}, stop), {}, {
      gid
    });
  });
  const stopStationMap = stopsWithGids.reduce(function redu(map, stopOrStation) {
    // eslint-disable-next-line no-param-reassign
    map[stopOrStation.gid] = stopOrStation;
    return map;
  }, {});
  return getJson(URL_PELIAS_PLACE, {
    ids: gids.slice(0, -1)
    // lang: context.getLanguage(context), TODO enable this when OTP supports translations
  }).then(res => {
    return res.features.map(stop => {
      const favourite = {
        type: 'FavouriteStop',
        properties: {
          addendum: stop.properties.addendum,
          gid: stopStationMap[stop.properties.gid].gid,
          code: stopStationMap[stop.properties.gid].code,
          gtfsId: stopStationMap[stop.properties.gid].gtfsId,
          lastUpdated: stopStationMap[stop.properties.gid].lastUpdated,
          favouriteId: stopStationMap[stop.properties.gid].favouriteId,
          address: stop.properties.label,
          layer: isStop(stop.properties) ? 'favouriteStop' : 'favouriteStation'
        },
        geometry: stop.geometry
      };
      return favourite;
    });
  });
}
function filterFavouriteLocations(favourites, input) {
  return Promise.resolve(filterMatchingToInput(favourites, input, ['address', 'name']).map(item => ({
    type: 'FavouritePlace',
    properties: _objectSpread$4(_objectSpread$4({}, item), {}, {
      label: item.name,
      layer: 'favouritePlace'
    }),
    geometry: {
      type: 'Point',
      coordinates: [item.lon, item.lat]
    }
  })));
}
function selectPositionFomMap(input) {
  if (typeof input !== 'string' || input.length === 0) {
    return Promise.resolve([{
      type: 'SelectFromMap',
      address: 'SelectFromMap',
      lat: null,
      lon: null,
      properties: {
        labelId: 'select-from-map',
        layer: 'selectFromMap',
        address: 'SelectFromMap',
        lat: null,
        lon: null
      },
      geometry: {
        type: 'Point',
        coordinates: []
      }
    }]);
  }
  return Promise.resolve([]);
}
function getCurrentPositionIfEmpty(input, position) {
  if (typeof input !== 'string' || input.length === 0) {
    return Promise.resolve([{
      type: 'CurrentLocation',
      address: position.address,
      lat: position.lat,
      lon: position.lon,
      properties: {
        labelId: 'use-own-position',
        layer: 'currentPosition',
        address: position.address,
        lat: position.lat,
        lon: position.lon
      },
      geometry: {
        type: 'Point',
        coordinates: [position.lon, position.lat]
      }
    }]);
  }
  return Promise.resolve([]);
}
function selectFromOwnLocations(input) {
  if (typeof input !== 'string' || input.length === 0) {
    return Promise.resolve([{
      type: 'SelectFromOwnLocations',
      address: 'SelectFromOwnLocations',
      lat: null,
      lon: null,
      properties: {
        labelId: 'select-from-own-locations',
        layer: 'ownLocations',
        address: 'selectFromOwnLocations',
        lat: null,
        lon: null
      },
      geometry: {
        type: 'Point',
        coordinates: []
      }
    }]);
  }
  return Promise.resolve([]);
}
function getBackSuggestion() {
  return Promise.resolve([{
    type: 'back',
    address: 'back',
    lat: null,
    lon: null,
    properties: {
      labelId: 'back',
      layer: 'back',
      address: 'back',
      lat: null,
      lon: null
    },
    geometry: {
      type: 'Point',
      coordinates: []
    }
  }]);
}
function filterFavouriteStops(stopsAndStations, input, useStops, useStations) {
  return stopsAndStations.then(stopsStations => {
    const candidates = stopsStations.filter(s => s.type === 'stop' ? useStops : useStations);
    return filterMatchingToInput(candidates, input, ['properties.name', 'properties.name', 'properties.address']);
  });
}
function filterOldSearches(oldSearches, input, dropLayers) {
  let matchingOldSearches = filterMatchingToInput(oldSearches, input, ['properties.name', 'properties.label', 'properties.address', 'properties.shortName', 'properties.longName']);
  if (dropLayers) {
    // don't want these
    matchingOldSearches = matchingOldSearches.filter(item => !dropLayers.includes(item.properties.layer));
  }
  return Promise.resolve(take(matchingOldSearches, 10).map(item => {
    const newItem = _objectSpread$4(_objectSpread$4({}, item), {}, {
      type: 'OldSearch',
      timetableClicked: false,
      // reset latest selection action
      arrowClicked: false
    });
    delete newItem.properties.confidence;
    return newItem;
  }));
}
function hasFavourites(searchContext) {
  const favouriteLocations = searchContext.getFavouriteLocations(searchContext.context);
  if (favouriteLocations?.length > 0) {
    return true;
  }
  const favouriteVehicleRentalStations = searchContext.getFavouriteVehicleRentalStations(searchContext.context);
  if (favouriteVehicleRentalStations?.length > 0) {
    return true;
  }
  const favouriteStops = searchContext.getFavouriteStops(searchContext.context);
  return favouriteStops?.length > 0;
}
const routeLayers = ['route-TRAM', 'route-BUS', 'route-RAIL', 'route-FERRY', 'route-SUBWAY', 'route-AIRPLANE', 'route-FUNICULAR'];
const locationLayers = ['venue', 'address', 'street'];
const parkingLayers = ['carpark', 'bikepark'];
const stopLayers = ['stop', 'station'];

/**
 * Executes the search
 *
 */
function getSearchResults(targets, sources, transportMode, searchContext, filterResults, geocodingSize, {
  input
}, callback, pathOpts, refPoint) {
  const {
    getPositions,
    getFavouriteLocations,
    getOldSearches,
    getFavouriteStops,
    parkingAreaSources,
    getLanguage,
    getStopAndStationsQuery,
    getFavouriteVehicleRentalStationsQuery,
    getFavouriteVehicleRentalStations,
    getFavouriteRoutesQuery,
    getFavouriteRoutes,
    getRoutesQuery,
    context,
    isPeliasLocationAware: locationAware,
    minimalRegexp,
    lineRegexp,
    URL_PELIAS,
    URL_PELIAS_PLACE,
    feedIDs,
    geocodingSearchParams,
    geocodingSources,
    getFutureRoutes,
    cityBikeNetworks
  } = searchContext;
  // if no targets are provided, search them all.
  const allTargets = !targets || targets.length === 0;
  // if no sources are provided, use them all.
  const allSources = !sources || sources.length === 0;
  const position = getPositions(context);
  const searchComponents = [];
  const searches = {
    type: 'all',
    term: input,
    results: []
  };
  const language = getLanguage(context);
  const focusPoint = locationAware && refPoint?.lat && refPoint?.lon ? {
    // Round coordinates to approx 1 km, in order to improve caching
    'focus.point.lat': refPoint.lat.toFixed(3),
    'focus.point.lon': refPoint.lon.toFixed(3)
  } : {};
  const mode = transportMode ? transportMode.split('-')[1] : undefined;
  if (targets.includes('CurrentPosition') && position.status !== 'geolocation-not-supported') {
    searchComponents.push(getCurrentPositionIfEmpty(input, position));
  }
  if (allTargets || targets.includes('MapPosition')) {
    searchComponents.push(selectPositionFomMap(input));
  }
  if (targets.includes('FutureRoutes')) {
    const items = getFutureRoutes(context);
    searchComponents.push(take(items, 3));
  }
  if (targets.includes('SelectFromOwnLocations') && hasFavourites(searchContext)) {
    searchComponents.push(selectFromOwnLocations(input));
  }
  if (allTargets || targets.includes('Locations')) {
    // eslint-disable-next-line prefer-destructuring
    const searchParams = geocodingSearchParams;
    if (sources.includes('Favourite')) {
      const favouriteLocations = getFavouriteLocations(context);
      searchComponents.push(filterFavouriteLocations(favouriteLocations, input));
      if (sources.includes('Back')) {
        searchComponents.push(getBackSuggestion());
      }
    }
    if (allSources || sources.includes('Datasource')) {
      const geocodingLayers = ['venue', 'address', 'street'];
      if (targets.includes('Stations')) {
        geocodingLayers.push('station'); // search stations from OSM
      }
      searchComponents.push(getGeocodingResults(input, searchParams, language, focusPoint, geocodingSources.join(','), URL_PELIAS, minimalRegexp, geocodingLayers));
    }
    if (allSources || sources.includes('History')) {
      const locationHistory = getOldSearches(context, 'endpoint');
      const dropLayers = ['bikestation'];
      dropLayers.push(...stopLayers);
      dropLayers.push(...routeLayers);
      dropLayers.push(...parkingLayers);
      searchComponents.push(filterOldSearches(locationHistory, input, dropLayers));
    }
  }
  if (allTargets || targets.includes('ParkingAreas')) {
    if (allSources || sources.includes('Datasource')) {
      const searchParams = geocodingSearchParams;
      if (geocodingSize && geocodingSize !== 10) {
        searchParams.size = geocodingSize;
      }
      const geocodingLayers = ['carpark', 'bikepark'];
      const feedIds = parkingAreaSources ? parkingAreaSources.join(',') : null;
      searchComponents.push(getGeocodingResults(input, searchParams, language, focusPoint, feedIds, URL_PELIAS, minimalRegexp, geocodingLayers).then(results => {
        if (filterResults) {
          return filterResults(results, mode);
        }
        return results;
      }));
    }
    if (allSources || sources.includes('History')) {
      const history = getOldSearches(context);
      const dropLayers = ['bikestation'];
      dropLayers.push(...stopLayers);
      dropLayers.push(...routeLayers);
      dropLayers.push(...locationLayers);
      searchComponents.push(filterOldSearches(history, input, dropLayers));
    }
  }
  const useStops = targets.includes('Stops');
  const useStations = targets.includes('Stations');
  if (allTargets || useStops || useStations) {
    if (sources.includes('Favourite')) {
      const favouriteStops = getFavouriteStops(context);
      let stopsAndStations;
      if (favouriteStops.every(stop => stop.type === 'station')) {
        stopsAndStations = getStopsFromGeocoding(favouriteStops, URL_PELIAS_PLACE).then(results => {
          if (filterResults) {
            return filterResults(results, mode);
          }
          return results;
        });
      } else {
        stopsAndStations = getStopAndStationsQuery(favouriteStops).then(favourites => getStopsFromGeocoding(favourites, URL_PELIAS_PLACE)).then(results => {
          if (filterResults) {
            return filterResults(results, mode);
          }
          return results;
        });
      }
      searchComponents.push(filterFavouriteStops(stopsAndStations, input, useStops, useStations));
    }
    if (allSources || sources.includes('Datasource')) {
      const geocodingLayers = [];
      if (useStops) {
        geocodingLayers.push('stop');
      }
      if (useStations) {
        geocodingLayers.push('station');
      }
      const searchParams = geocodingSize && geocodingSize !== 10 ? {
        size: geocodingSize
      } : {};
      if (geocodingSearchParams && geocodingSearchParams['boundary.country']) {
        searchParams['boundary.country'] = geocodingSearchParams['boundary.country'];
      }
      // a little hack: when searching location data, automatically dedupe stops
      // this could be a new explicit prop
      if (allTargets || targets.includes('Locations')) {
        searchParams.dedupestops = 1;
      }
      const feeds = feedIDs.map(v => `gtfs${v}`).join(',');
      searchComponents.push(getGeocodingResults(input, searchParams, language, focusPoint, feeds, URL_PELIAS, minimalRegexp, geocodingLayers).then(results => {
        if (filterResults) {
          return filterResults(results, mode);
        }
        return results;
      }));
    }
    if (allSources || sources.includes('History')) {
      const stopHistory = getOldSearches(context).filter(item => {
        if (item.properties.gid) {
          return item.properties.gid.includes('GTFS:');
        }
        return true;
      });
      const dropLayers = ['bikestation'];
      dropLayers.push(...routeLayers);
      dropLayers.push(...locationLayers);
      dropLayers.push(...parkingLayers);
      if (!useStops) {
        dropLayers.push('stop');
      }
      if (!useStations) {
        dropLayers.push('station');
      }
      if (transportMode) {
        searchComponents.push(filterOldSearches(stopHistory, input, dropLayers).then(result => filterResults ? filterResults(result, mode) : result));
      } else {
        searchComponents.push(filterOldSearches(stopHistory, input, dropLayers));
      }
    }
  }
  if (allTargets || targets.includes('Routes')) {
    if (sources.includes('Favourite')) {
      const favouriteRoutes = getFavouriteRoutes(context);
      searchComponents.push(getFavouriteRoutesQuery(favouriteRoutes, input, mode, pathOpts).then(result => filterResults ? filterResults(result, mode, 'Routes') : result));
    }
    searchComponents.push(getRoutesQuery(input, feedIDs, transportMode, pathOpts).then(result => filterResults ? filterResults(result, mode, 'Routes') : result));
    if (allSources || sources.includes('History')) {
      const routeHistory = getOldSearches(context);
      const dropLayers = ['bikestation'];
      if (transportMode) {
        dropLayers.push(...routeLayers.filter(i => !(i === transportMode)));
      }
      dropLayers.push(...stopLayers);
      dropLayers.push(...locationLayers);
      dropLayers.push(...parkingLayers);
      searchComponents.push(filterOldSearches(routeHistory, input, dropLayers).then(results => filterResults ? filterResults(results, mode, 'Routes') : results));
    }
  }
  if (allTargets || targets.includes('VehicleRentalStations')) {
    if (sources.includes('Favourite')) {
      const favouriteVehicleRentalStation = getFavouriteVehicleRentalStations(context);
      searchComponents.push(getFavouriteVehicleRentalStationsQuery(favouriteVehicleRentalStation, input));
    }
    if (allSources || sources.includes('Datasource')) {
      const geocodingLayers = ['bikestation'];
      const searchParams = geocodingSize && geocodingSize !== 10 ? {
        size: geocodingSize
      } : {};
      searchComponents.push(getGeocodingResults(input, searchParams, language, focusPoint, cityBikeNetworks.join(','), URL_PELIAS, minimalRegexp, geocodingLayers).then(results => {
        if (filterResults) {
          return filterResults(results, mode, 'VehicleRentalStations');
        }
        return results;
      }));
    }
    if (allSources || sources.includes('History')) {
      const history = getOldSearches(context);
      const dropLayers = [...stopLayers];
      dropLayers.push(...routeLayers);
      dropLayers.push(...locationLayers);
      dropLayers.push(...parkingLayers);
      searchComponents.push(filterOldSearches(history, input, dropLayers));
    }
  }
  const searchResultsPromise = Promise.all(searchComponents).then(flatten).then(results => {
    searches.results = results;
  }).catch(err => {
    searches.error = err;
  });
  searchResultsPromise.then(() => {
    callback(_objectSpread$4(_objectSpread$4({}, searches), {}, {
      results: sortSearchResults(lineRegexp, searches.results, input)
    }));
  });
}
const debouncedSearch = debounce(getSearchResults, 300, {
  leading: true
});
const executeSearch = (targets, sources, transportMode, searchContext, filterResults, geocodingSize, data, callback, pathOpts, refPoint) => {
  callback(null); // This means 'we are searching'
  debouncedSearch(targets, sources, transportMode, searchContext, filterResults, geocodingSize, data, callback, pathOpts, refPoint);
};

/* eslint sort-keys: "error" */
const translations = {
  de: {
    translation: {
      'add-via-button-label': 'Füge Zwischenziel hinzu',
      'add-via-duration-button-label': 'Set stop duration at via point {{index}}',
      'add-via-duration-button-label-close': 'Close stop duration selector at via point {{index}}',
      'add-via-duration-button-label-open': 'Open stop duration selector at via point {{index}}',
      address: 'Adresse',
      'address-place-or-business': 'Adress, Ort or Geschäft',
      airplane: 'Flug',
      arrival: 'Ankunft',
      back: 'Zurück',
      bikerentalstation: 'Leihrad-Station',
      bus: 'Bus',
      cancel: 'Abbrechen',
      'clear-button-label': 'Löschen',
      'clear-search-history': 'Suchhistorie löschen',
      currentposition: 'Aktuelle Position',
      delete: 'Löschen',
      'delete-old-searches-header': 'Wollen Sie die Such-Historie löschen?',
      departure: 'Abfahrt',
      destination: 'Ziel',
      favourite: 'Favoriten',
      ferry: 'Fähre',
      'future-route': 'Zukünftige Verbindung',
      'give-destination': 'Ziel eingeben.',
      'give-origin': 'Start eingeben.',
      loading: 'Laden...',
      'minute-short': 'min',
      origin: 'Start',
      'origin-stop-near-you': 'Start',
      ownlocations: 'Eigene Favoriten',
      place: 'Ort',
      rail: 'Zug',
      search: 'Suche',
      'search-address-or-place': 'Suche anhand von Adresse oder Ortsangabe',
      'search-autosuggest-label': 'Venue, place and stopsearch.',
      'search-autosuggest-label-instructions-desktop': 'Navigate list with arrow keys and select with enter key.',
      'search-autosuggest-label-instructions-mobile': 'Enter the location and navigate in the list by swiping.',
      'search-autosuggest-label-move-to-destination': 'After selecting the starting location you are automatically moved to the destination field.',
      'search-autosuggest-len': 'There is {{count}} suggestion available',
      'search-autosuggest-len_plural': 'There are {{count}} suggestions available',
      'search-button-label': 'Suche',
      'search-current-suggestion': 'Aktuelle Auswahl: {{selection}}',
      'search-destination': 'Suche Zielort, Linie or Haltestelle',
      'search-destination-index': 'Zielort eingeben',
      'search-destination-results-but-no-search': 'Ergebnisse in “Zielort”-Reiter anzeigen',
      'search-no-results': 'Kein Ort gefunden.',
      'search-origin': 'Suche Startort, Linie oder Haltestelle',
      'search-origin-index': 'Startort eingeben',
      'search-position': 'Standort ermitteln',
      'select-from-map': 'Wähle Position anhand Karte',
      'select-from-own-locations': 'Aus eigenen Favoriten auswählen',
      station: 'Station',
      stop: 'Haltestelle',
      'stop-near-you': 'Suche Haltestellen und Linien in der Nähe',
      'stop-near-you-bus': 'Suche Buslinien or Haltestellen',
      'stop-near-you-citybike': 'Suche Leihrad-Stationen',
      'stop-near-you-rail': 'Suche Zugverbindungen oder Bahnhöfe',
      'stop-near-you-subway': 'Suche U-Bahn-Linien oder Haltestellen',
      'stop-near-you-tram': 'Suche Stadtbahn-Haltestellen-Suche',
      street: 'Straße',
      subway: 'U-Bahn',
      'today-at': 'Heute um',
      'tomorrow-at': 'Morgen um',
      tram: 'Straßenbahn',
      'use-own-position': 'Benutze aktuellen Standort',
      venue: 'Venue',
      'via-point': 'Zwischenziel',
      where: 'Wohin?'
    }
  },
  en: {
    translation: {
      'add-via-button-label': 'Add via point',
      'add-via-duration-button-label': 'Set stop duration at via point {{index}}',
      'add-via-duration-button-label-close': 'Close stop duration selector at via point {{index}}',
      'add-via-duration-button-label-open': 'Open stop duration selector at via point {{index}}',
      address: 'Address',
      'address-place-or-business': 'Address, place or business',
      airplane: 'Flight',
      arrival: 'Arrival',
      back: 'Back',
      bikepark: 'Park & Ride for bikes',
      bikerentalstation: 'Bike station',
      bus: 'bus',
      cancel: 'Cancel',
      carpark: 'Park & Ride',
      'clear-button-label': 'Clear',
      'clear-search-history': 'Clear search history',
      currentposition: 'Current position',
      delete: 'Delete',
      'delete-old-searches-header': 'Do you want to clear the search history?',
      departure: 'Departure',
      destination: 'Destination',
      favourite: 'Favourite',
      ferry: 'Ferry',
      'future-route': 'Future route',
      'give-destination': 'Enter your destination',
      'give-origin': 'Enter your origin.',
      loading: 'Loading...',
      'minute-short': 'm',
      origin: 'Start',
      'origin-stop-near-you': 'Start',
      ownlocations: 'Own locations',
      place: 'Location',
      platform: 'Platform',
      rail: 'Train',
      required: 'required field',
      search: 'Search',
      'search-address-or-place': 'Search by address or place name',
      'search-autosuggest-label': 'Venue, place and stopsearch.',
      'search-autosuggest-label-instructions-desktop': 'Navigate list with arrow keys and select with enter key.',
      'search-autosuggest-label-instructions-mobile': 'Enter the location and navigate in the list by swiping.',
      'search-autosuggest-label-move-to-destination': 'After selecting the starting location you are automatically moved to the destination field.',
      'search-autosuggest-len': 'There is {{count}} suggestion available',
      'search-autosuggest-len_plural': 'There are {{count}} suggestions available',
      'search-button-label': 'Search',
      'search-current-suggestion': 'Current selection: {{selection}}',
      'search-destination': 'Search destination, route or stop',
      'search-destination-index': 'Enter destination',
      'search-destination-results-but-no-search': 'View results in the adjacent “Destination” tab',
      'search-no-results': 'No location',
      'search-origin': 'Search origin, route or stop',
      'search-origin-index': 'Enter origin',
      'search-position': 'Detect location',
      'select-from-map': 'Select position from map',
      'select-from-own-locations': 'Select from own locations',
      station: 'Station',
      stop: 'Stop',
      'stop-near-you': 'Search stops and routes',
      'stop-near-you-airplane': 'Search flights or airports',
      'stop-near-you-bikepark': 'Search bike parks',
      'stop-near-you-bus': 'Search bus routes or stops',
      'stop-near-you-carpark': 'Search car parks',
      'stop-near-you-citybike': 'Search city bike stations',
      'stop-near-you-rail': 'Search train routes or stations',
      'stop-near-you-subway': 'Search metro routes or stations ',
      'stop-near-you-tram': 'Search tram routes or stops',
      'stop-route-station': 'Bus and stop search',
      street: 'Street',
      subway: 'Metro',
      'today-at': 'today at',
      'tomorrow-at': 'tomorrow at',
      track: 'Track',
      tram: 'Tram',
      'use-own-position': 'Use current location',
      venue: 'Venue',
      'via-point': 'Via point',
      where: 'Where to?'
    }
  },
  fi: {
    translation: {
      'add-via-button-label': 'Lisää välipiste',
      'add-via-duration-button-label': 'Aseta välipisteelle {{index}} pysähdyksen kesto',
      'add-via-duration-button-label-close': 'Avaa välipisteen {{index}} pysähdyksen kesto valitsin',
      'add-via-duration-button-label-open': 'Sulje välipisteen {{index}} pysähdyksen kesto valitsin',
      address: 'Osoite',
      'address-place-or-business': 'Osoite, paikka tai yritys',
      airplane: 'Lentokone',
      arrival: 'Perillä',
      back: 'Takaisin',
      bikepark: 'Liityntäpysäköinti',
      bikerentalstation: 'Pyöräasema',
      bus: 'bussi',
      cancel: 'Peruuta',
      carpark: 'Liityntäpysäköinti',
      'clear-button-label': 'Tyhjennä',
      'clear-search-history': 'Tyhjennä hakuhistoria',
      currentposition: 'Tämänhetkinen sijainti',
      delete: 'Poista',
      'delete-old-searches-header': 'Haluatko tyhjentää hakuhistorian?',
      departure: 'Lähtöaika',
      destination: 'Määränpää',
      favourite: 'Suosikki',
      ferry: 'Lautta',
      'future-route': 'Tuleva reitti',
      'give-destination': 'Määränpää',
      'give-origin': 'Lähtöpaikka',
      loading: 'Ladataan...',
      'minute-short': 'min',
      origin: 'Lähtöpaikka',
      'origin-stop-near-you': 'Lähtöpaikka',
      ownlocations: 'Omat paikat',
      place: 'Paikka',
      platform: 'Laituri',
      rail: 'Juna',
      required: 'pakollinen kenttä',
      search: 'Etsi',
      'search-address-or-place': 'Hae osoitteella tai paikan nimellä',
      'search-autosuggest-label': 'Paikka, linja ja pysäkkihaku.',
      'search-autosuggest-label-instructions-desktop': 'Navigoi listassa nuolinäppäimillä ja valitse enterillä.',
      'search-autosuggest-label-instructions-mobile': 'Syötä sijainti ja navigoi listassa pyyhkäisemällä.',
      'search-autosuggest-label-move-to-destination': 'Valittuasi lähtöpaikan siirrytään suoraan määränpää-kenttään.',
      'search-autosuggest-len': ' Löydettiin {{count}} ehdotus',
      'search-autosuggest-len_plural': ' Löydettiin {{count}} ehdotusta',
      'search-button-label': 'Hae',
      'search-current-suggestion': 'Tämänhetkinen valinta: {{selection}}',
      'search-destination': 'Hae määränpäätä, linjaa tai pysäkkiä',
      'search-destination-index': 'Syötä määränpää',
      'search-destination-results-but-no-search': 'Katso tulokset viereiseltä Määränpää-välilehdeltä',
      'search-no-results': 'Ei tuloksia',
      'search-origin': 'Hae lähtöpaikkaa, linjaa tai pysäkkiä',
      'search-origin-index': 'Syötä lähtöpaikka',
      'search-position': 'Paikanna',
      'select-from-map': 'Valitse sijainti kartalta',
      'select-from-own-locations': 'Valitse omista paikoista',
      station: 'Asema',
      stop: 'Pysäkki',
      'stop-near-you': 'Linja, pysäkki tai asema',
      'stop-near-you-airplane': 'Hae lentoja tai lentoasemaa',
      'stop-near-you-bikepark': 'Hae pyöräparkkia',
      'stop-near-you-bus': 'Hae bussilinjaa tai pysäkkiä',
      'stop-near-you-carpark': 'Hae autoparkkia',
      'stop-near-you-citybike': 'Hae kaupunkipyöräasemaa',
      'stop-near-you-rail': 'Hae lähijunaa tai asemaa',
      'stop-near-you-subway': 'Hae metrolinjaa tai asemaa',
      'stop-near-you-tram': 'Hae raitiolinjaa tai pysäkkiä',
      'stop-route-station': 'Linja- ja pysäkkihaku',
      street: 'Katu',
      subway: 'Metro',
      'today-at': 'tänään klo',
      'tomorrow-at': 'huomenna klo',
      track: 'Raide',
      tram: 'Raitiovaunu',
      'use-own-position': 'Käytä nykyistä sijaintia',
      venue: 'Paikka',
      'via-point': 'Välipiste',
      where: 'Minne mennään?'
    }
  },
  pl: {
    translation: {
      'add-via-button-label': 'Dodaj punkt pośredni',
      'add-via-duration-button-label': 'Wybierz czas postoju dla punktu {{index}}',
      'add-via-duration-button-label-close': 'Zamknij wybornik czasu postoju w punkcie {{index}}',
      'add-via-duration-button-label-open': 'Otwórz wybornik czasu postoju w punkcie {{index}}',
      address: 'Adres',
      'address-place-or-business': 'Adres, miejsce lub firma',
      airplane: 'Lot',
      arrival: 'Przyjazd',
      back: 'Wróć',
      bikepark: 'Parkuj i Jedź dla rowerów',
      bikerentalstation: 'Stacja rowerowa',
      bus: 'autobus',
      cancel: 'Anuluj',
      carpark: 'Parkuj i Jedź',
      'clear-button-label': 'Wyczyść',
      'clear-search-history': 'Wyczyść historię wyszukiwania',
      currentposition: 'Obecne położenie',
      delete: 'Usuń',
      'delete-old-searches-header': 'Czy na pewno chcesz wyczyścić historię wyszukiwania?',
      departure: 'Odjazd',
      destination: 'Cel',
      favourite: 'Ulubione',
      ferry: 'Prom',
      'future-route': 'Przyszła trasa',
      'give-destination': 'Wprowadź swój cel',
      'give-origin': 'Wprowadź swój punkt początkowy.',
      loading: 'Ładowanie…',
      'minute-short': 'min.',
      origin: 'Początek',
      'origin-stop-near-you': 'Początek',
      ownlocations: 'Własne miejsca',
      place: 'Miejsce',
      platform: 'Peron',
      rail: 'Pociąg',
      required: 'wymagane pole',
      search: 'Szukaj',
      'search-address-or-place': 'Szukaj po adresie lub nazwie miejsca',
      'search-autosuggest-label': 'Miejsce, lokalizacja i przystanek.',
      'search-autosuggest-label-instructions-desktop': 'Nawiguj po liście używając strzałek i wybieraj klawiszem Enter.',
      'search-autosuggest-label-instructions-mobile': 'Wprowadź lokalizację i nawiguj po liście gestami przesuwania.',
      'search-autosuggest-label-move-to-destination': 'Po wybraniu lokalizacji początkowej, automatycznie przejdziesz do pola miejsca docelowego.',
      'search-autosuggest-len': 'Dostępna jest {{count}} propozycja',
      'search-autosuggest-len_few': 'Dostępne są {{count}} propozycje',
      'search-autosuggest-len_other': 'Dostępne jest {{count}} propozycji',
      'search-button-label': 'Szukaj',
      'search-current-suggestion': 'Obecny wybór: {{selection}}',
      'search-destination': 'Szukaj celu, trasy lub przystanku',
      'search-destination-index': 'Wprowadź punkt docelowy',
      'search-destination-results-but-no-search': 'Wyświetl wyniki w sąsiedniej karcie „Cel”',
      'search-no-results': 'Brak lokalizacji',
      'search-origin': 'Wyszukaj początek, koniec lub punkt pośredni',
      'search-origin-index': 'Wprowadź punkt początkowy',
      'search-position': 'Wykryj lokalizację',
      'select-from-map': 'Wybierz miejsce na mapie',
      'select-from-own-locations': 'Wybierz z własnych miejsc',
      station: 'Stacja',
      stop: 'Przystanek',
      'stop-near-you': 'Szukaj przystanków i tras',
      'stop-near-you-airplane': 'Szukaj lotów i lotnisk',
      'stop-near-you-bus': 'Szukaj tras autobusowych i przystanków',
      'stop-near-you-citybike': 'Szukaj stacji rowerów miejskich',
      'stop-near-you-rail': 'Szukaj tras kolejowych i przystanków',
      'stop-near-you-subway': 'Szukaj tras metra i przystanków ',
      'stop-near-you-tram': 'Szukaj tras tramwajowych i przystanków',
      'stop-route-station': 'Wyszukiwanie autobusów i przystanków',
      street: 'Ulica',
      subway: 'Metro',
      'today-at': 'dziś o',
      'tomorrow-at': 'jutro o',
      track: 'Tor',
      tram: 'Tramwaj',
      'use-own-position': 'Użyj obecnej lokalizacji',
      venue: 'Miejsce',
      'via-point': 'Przez punkt',
      where: 'Dokąd?'
    }
  },
  sv: {
    translation: {
      'add-via-button-label': 'Lägg till viapunkt',
      'add-via-duration-button-label': 'Ställ in varaktighet för viapunkten {{index}}',
      'add-via-duration-button-label-close': 'Stäng väljaren för varaktighet vid via-punkten {{index}}',
      'add-via-duration-button-label-open': 'Öppna väljaren för varaktighet vid via-punkten {{index}}',
      address: 'Adress',
      'address-place-or-business': 'Adress, plats eller företag',
      airplane: 'Flyg',
      arrival: 'Anländer',
      back: 'Tillbaka',
      bikepark: 'Anslutningsparkering för cyklar',
      bikerentalstation: 'Stadscykelstation',
      bus: 'Bussi',
      cancel: 'Avbryt',
      carpark: 'Infartsparkering',
      'clear-button-label': 'Töm',
      'clear-search-history': 'Töm sökhistoriken',
      currentposition: 'Nuvarande position',
      delete: 'Ta bort',
      'delete-old-searches-header': 'Vill du tömma sökhistoriken?',
      departure: 'Avgångstid',
      destination: 'Destination',
      favourite: 'Favorit',
      ferry: 'Färja',
      'future-route': 'Framtida rutt',
      'give-destination': 'Skriv in din destination',
      'give-origin': 'Skriv in din avfärdsplats',
      loading: 'Laddar...',
      'minute-short': 'min',
      origin: 'Avfärdsplats',
      'origin-stop-near-you': 'Avfärdsplats',
      ownlocations: 'Egna platser',
      place: 'Plats',
      platform: 'Plattform',
      rail: 'Tåg',
      required: 'obligatoriskt fält',
      search: 'Sök',
      'search-address-or-place': 'Sök med adressen eller platsens namn',
      'search-autosuggest-label': 'Plats, linje och hållplatssökning.',
      'search-autosuggest-label-instructions-desktop': 'Navigera listan med piltangenterna och välj med Enter-tangeten.',
      'search-autosuggest-label-instructions-mobile': 'Ange platsen och navigera i listan genom att svepa.',
      'search-autosuggest-label-move-to-destination': 'Efter att du valt avgångsplatsen tas du direkt till Destination-fältet.',
      'search-autosuggest-len': 'Hittade {{count}} förslag',
      'search-autosuggest-len_plural': 'Hittade {{count}} förslag',
      'search-button-label': 'Sök',
      'search-current-suggestion': 'Nuvarande val: {{selection}}',
      'search-destination': 'Sök destination, linje eller hållplats',
      'search-destination-index': 'Skriv destination',
      'search-destination-results-but-no-search': 'Se resultaten under fliken Destination',
      'search-no-results': 'Inga sökresultat',
      'search-origin': 'Sök avfärdsplats, linje eller hållplats',
      'search-origin-index': 'Skriv avfärdsplats',
      'search-position': 'Sök position',
      'select-from-map': 'Välj position från kartan',
      'select-from-own-locations': 'Välj från egna platser',
      station: 'Station',
      stop: 'Hållplats',
      'stop-near-you': 'Sök hållplatser eller linjer',
      'stop-near-you-airplane': 'Sök flyg eller flygplatser',
      'stop-near-you-bikepark': 'Sök cykelparkering',
      'stop-near-you-bus': 'Sök busslinjer eller hållplatser',
      'stop-near-you-carpark': 'Sök anslutningsparkering',
      'stop-near-you-citybike': 'Sök stadscykelstationer',
      'stop-near-you-rail': 'Sök närtåger eller stationer',
      'stop-near-you-subway': 'Sök metrolinjer eller stationer',
      'stop-near-you-tram': 'Sök spårvagnlinjer eller hållplatser',
      'stop-route-station': 'Buss och stopp sökningen',
      street: 'Gata',
      subway: 'Metro',
      'today-at': 'i dag kl',
      'tomorrow-at': 'i morgon',
      track: 'Spår',
      tram: 'Spårvagn',
      'use-own-position': 'Använd min position',
      venue: 'Mötesplats',
      'via-point': 'Via punkt',
      where: 'Vart vill du åka?'
    }
  }
};

const i18n = createInstance$1({
  fallbackLng: 'fi',
  // debug: true,
  resources: translations,
  defaultNS: 'translation',
  react: {
    useSuspense: true
  }
});
i18n.init();

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$2 = "input[type=text] {\n  margin: 0;\n}\n\n.styles_container__YKW96 {\n  display: flex;\n  width: 100%;\n}\n.styles_container__YKW96 .styles_input__--GRS.styles_favourite__VJm6c {\n  padding-left: 47px;\n}\n.styles_container__YKW96 .styles_input__--GRS.styles_thin__dDVH9 {\n  height: 34px;\n}\n.styles_container__YKW96 .styles_input__--GRS {\n  font-size: 16px;\n  font-family: inherit;\n  display: flex;\n  width: 100%;\n  height: 44px;\n  border-radius: 5px;\n  border: solid 1px #888;\n  background-color: #fff;\n  padding-left: 50px;\n  padding-right: 44px;\n  line-height: 1.2;\n  letter-spacing: -0.5px;\n  color: #333;\n  box-sizing: border-box;\n  appearance: none;\n}\n.styles_container__YKW96 .styles_input__--GRS:focus {\n  background: #fff;\n  outline: none;\n  border: solid 1px var(--color);\n  box-shadow: 0 0 2px 1px var(--hover-color);\n}\n@media screen and (min-width: 768px) {\n  .styles_container__YKW96 .styles_input__--GRS {\n    font-size: 15px;\n    height: 44px;\n    padding-left: 49px;\n  }\n}\n.styles_container__YKW96 .styles_input__--GRS::placeholder {\n  color: #666;\n  opacity: 1;\n  font-size: 15px;\n}\n\n.styles_hasValue__JCbDo {\n  color: #333;\n}\n\n.styles_suggestionsContainerOpen__ARBAD {\n  width: 100%;\n  max-height: 44vh;\n  border-radius: 5px;\n  box-shadow: 0 2px 0 0 rgba(0, 0, 0, 0.06);\n  border: solid 1px rgba(0, 0, 0, 0.1);\n  overflow-x: hidden;\n  overflow-y: scroll;\n  position: absolute;\n  background-color: white;\n  top: calc(2.625rem + 9px);\n  z-index: 20;\n}\n\n.styles_suggestionsContainer__FCLTy {\n  background-color: white;\n  z-index: 20;\n}\n.styles_suggestionsContainer__FCLTy .styles_suggestionsList__uyd8R {\n  margin: 0;\n  padding: 0;\n  box-sizing: border-box;\n  background: white;\n}\n.styles_suggestionsContainer__FCLTy .styles_suggestionsList__uyd8R li:hover {\n  background: #f2f5f7;\n}\n\n.styles_suggestion__yZzst {\n  margin: 0;\n  padding: 0;\n  box-sizing: border-box;\n  cursor: pointer;\n  list-style: none;\n  display: block;\n  overflow-x: hidden;\n  overflow-y: hidden;\n  white-space: nowrap;\n  z-index: 20;\n}\n\n.styles_suggestionHighlighted__4-yqj {\n  background-color: #f2f5f7;\n}\n\n.styles_autosuggest-input-container__d7zo4 {\n  position: relative;\n  width: 100%;\n}\n.styles_autosuggest-input-container__d7zo4.styles_origin__3BSEn {\n  z-index: 16;\n}\n.styles_autosuggest-input-container__d7zo4.styles_destination__VSQ-a {\n  z-index: 10;\n}\n.styles_autosuggest-input-container__d7zo4.styles_stop-route-station__Gcetf {\n  z-index: 9;\n}\n\n.styles_autosuggest-input-icon__4ww0f {\n  display: flex;\n  position: absolute;\n  align-items: center;\n  width: 16px;\n  height: 100%;\n  margin-top: 0;\n  margin-left: 19px;\n}\n.styles_autosuggest-input-icon__4ww0f svg {\n  width: 16px;\n  height: 24px;\n}\n.styles_autosuggest-input-icon__4ww0f.styles_origin__3BSEn svg {\n  color: #4ea700;\n  fill: #4ea700;\n  width: 16px;\n  height: 16px;\n}\n.styles_autosuggest-input-icon__4ww0f.styles_destination__VSQ-a svg {\n  fill: #ec5188;\n  width: 16px;\n  height: 24px;\n}\n.styles_autosuggest-input-icon__4ww0f.styles_stop-route-station__Gcetf svg, .styles_autosuggest-input-icon__4ww0f.styles_favourite__VJm6c svg {\n  fill: #888;\n  width: 16px;\n  height: 16px;\n}\n.styles_autosuggest-input-icon__4ww0f.styles_origin-stop-near-you__NhZ-N svg {\n  fill: #888;\n  width: 16px;\n  height: 16px;\n}\n\nbutton.styles_clear-input__CYTYl {\n  display: flex;\n  position: absolute;\n  padding: 14px 0;\n  right: 0;\n  width: 44px;\n  height: 100%;\n  color: var(--color);\n  outline: none;\n  border: 0;\n  margin: 0;\n  justify-content: center;\n  text-align: left;\n  background: none;\n  border-style: none;\n  border-width: 0;\n  cursor: pointer;\n  font-family: inherit;\n  background-color: transparent;\n  transition: none;\n}\nbutton.styles_clear-input__CYTYl svg {\n  fill: var(--color);\n  width: 16px;\n  height: 16px;\n}\nbutton.styles_clear-input__CYTYl:focus {\n  background-color: transparent;\n  color: inherit;\n  /* First apply outline that is used in Edge and Firefox */\n  /* Then override outline for webkit browsers like Chrome, this has no effect on other browsers */\n  outline: 5px auto -webkit-focus-ring-color;\n}\nbutton.styles_clear-input__CYTYl:active {\n  background-color: transparent;\n  color: inherit;\n}\nbutton.styles_clear-input__CYTYl:hover {\n  background-color: transparent;\n  color: inherit;\n}\n\n.styles_sr-only__N8Ha-:not(:focus, :active) {\n  clip: rect(0 0 0 0);\n  clip-path: inset(50%);\n  height: 1px;\n  overflow: hidden;\n  position: absolute;\n  white-space: nowrap;\n  width: 1px;\n}\n\n.styles_hidden__iNw4s {\n  visibility: hidden;\n}";
var styles = {"container":"styles_container__YKW96","input":"styles_input__--GRS","favourite":"styles_favourite__VJm6c","thin":"styles_thin__dDVH9","hasValue":"styles_hasValue__JCbDo","suggestionsContainerOpen":"styles_suggestionsContainerOpen__ARBAD","suggestionsContainer":"styles_suggestionsContainer__FCLTy","suggestionsList":"styles_suggestionsList__uyd8R","suggestion":"styles_suggestion__yZzst","suggestionHighlighted":"styles_suggestionHighlighted__4-yqj","autosuggest-input-container":"styles_autosuggest-input-container__d7zo4","origin":"styles_origin__3BSEn","destination":"styles_destination__VSQ-a","stop-route-station":"styles_stop-route-station__Gcetf","autosuggest-input-icon":"styles_autosuggest-input-icon__4ww0f","origin-stop-near-you":"styles_origin-stop-near-you__NhZ-N","clear-input":"styles_clear-input__CYTYl","sr-only":"styles_sr-only__N8Ha-","hidden":"styles_hidden__iNw4s"};
styleInject(css_248z$2);

/**
 * Location properties.
 * @typedef {LocationProperties}
 * @property {string} layer Suggestion layer.
 * @property {lat} [number]
 * @property {lon} [number]
 * @property {string} address Address.
 */

/**
 * Returns localadmin (city name) for suggestions
 *
 * @name getLocaladmin
 * @param {Object} suggestion suggestion's properties from geocoding or a favourite stop/station.
 * Expects last part of an address (after ',') to contain the city name of the suggestion location.
 * @returns {String}  City name or empty string
 */
const getLocaladmin = suggestion => suggestion.localadmin || suggestion.localadmin || suggestion.address && suggestion.address.lastIndexOf(',') < suggestion.address.length - 2 && suggestion.address.substring(suggestion.address.lastIndexOf(',') + 2) || '';
const getStopCode = ({
  id,
  code
}) => {
  if (code) {
    return code;
  }
  if (id === undefined || typeof id.indexOf === 'undefined' || id.indexOf('#') === -1) {
    return undefined;
  }
  // id from pelias
  return id.substring(id.indexOf('#') + 1);
};
const formatFavouritePlaceLabel = (name, address) => [name || address && address.split(',')[0], typeof address === 'string' ? address.replace(new RegExp(`${escapeRegExp(name)}([ ,]|$)+`), '') : ''];

/**
 * Format suggestion-pair [name, address] by `suggestion.layer`.
 *
 * @name getNameLabel
 * @param {LocationProperties} suggestion
 * @param {boolean} [plain]
 *
 * @returns {Array.<string>} Formatted label.
 */
const getNameLabel = memoize((suggestion, plain = false) => {
  switch (suggestion.layer) {
    case 'currentPosition':
      return [suggestion.labelId, suggestion.address];
    case 'selectFromMap':
    case 'ownLocations':
    case 'back':
      return [suggestion.labelId];
    case 'favouritePlace':
      return formatFavouritePlaceLabel(suggestion.name, suggestion.address);
    case 'favouriteVehicleRentalStation':
    case 'bikestation':
      return [suggestion.name];
    case 'favouriteRoute':
    case 'route-BUS':
    case 'route-TRAM':
    case 'route-RAIL':
    case 'route-SUBWAY':
    case 'route-FERRY':
    case 'route-FUNICULAR':
    case 'route-AIRPLANE':
      return !plain && suggestion.shortName ? [suggestion.gtfsId, suggestion.mode.toLowerCase(), suggestion.longName] : [suggestion.shortName, suggestion.longName, suggestion.agency ? suggestion.agency.name : undefined];
    case 'venue':
    case 'address':
    case 'street':
      return [suggestion.name, suggestion.label.replace(new RegExp(`${escapeRegExp(suggestion.name)}(,)?( )?`), '')];
    case 'favouriteStop':
    case 'stop':
      return plain ? [suggestion.name || suggestion.label || suggestion.address && suggestion.address.split(',')[0], getLocaladmin(suggestion)] : [suggestion.name, suggestion.id, getStopCode(suggestion), getLocaladmin(suggestion)];
    case 'favouriteStation':
    case 'station':
    default:
      return [suggestion.name || suggestion.label || suggestion.address && suggestion.address.split(',')[0], getLocaladmin(suggestion)];
  }
}, (item, plain) => {
  const i = cloneDeep(item);
  i.plain = plain;
  return i;
});

/**
 * Returns label for properties
 *
 * @name getLabel
 * @param {*} properties object
 * @returns {Boolean} true/false
 */
function getLabel(properties) {
  const parts = getNameLabel(properties, true);
  switch (properties.layer) {
    case 'selectFromMap':
    case 'currentPosition':
    case 'ownLocations':
    case 'back':
      return parts[1] || parts[0];
    case 'favouriteVehicleRentalStation':
    case 'favouritePlace':
      return parts[0];
    default:
      return parts.length > 1 && parts[1] !== '' ? parts.join(', ') : parts[1] || parts[0];
  }
}

const isKeyboardSelectionEvent = event => {
  const space = [13, ' ', 'Spacebar'];
  const enter = [32, 'Enter'];
  const key = event && (event.key || event.which || event.keyCode) || '';
  if (!key || !space.concat(enter).includes(key)) {
    return false;
  }
  event.preventDefault();
  return true;
};
const isPOISearch = id => id === 'origin' || id === 'destination' || id === 'via-point' || id === 'origin-stop-near-you';
const getPlatform = (addendum, lng, t) => {
  // check if i81n is initialized
  if (!t) {
    return undefined;
  }
  if (!addendum || !addendum.GTFS.platform) {
    return undefined;
  }
  const {
    modes,
    platform
  } = addendum.GTFS;
  const type = modes && modes[0] === 'RAIL' ? t('track', {
    lng
  }) : t('platform', {
    lng
  });
  return [type, platform];
};
function getSuggestionContent(item, lng, t) {
  // check if i81n is initialized
  if (!t) {
    return undefined;
  }
  if (item.type !== 'FutureRoute') {
    if (item.type === 'SelectFromMap') {
      return ['', t('select-from-map', {
        lng
      })];
    }
    if (item.type === 'CurrentLocation') {
      return ['', t('use-own-position', {
        lng
      })];
    }
    if (item.type === 'SelectFromOwnLocations') {
      return ['', t('select-from-own-locations', {
        lng
      })];
    }
    /* eslint-disable-next-line prefer-const */
    let [name, label] = getNameLabel(item.properties, true);
    let suggestionType;
    if (item.properties.layer.toLowerCase().includes('bikerental') || item.properties.layer.toLowerCase().includes('bikestation')) {
      suggestionType = t('vehiclerentalstation', {
        lng
      });
      const stopCode = item.properties.labelId;
      return [suggestionType, name, undefined, stopCode];
    }
    if (item.properties.layer === 'bikepark') {
      suggestionType = t('bikepark', {
        lng
      });
      return [suggestionType, name, undefined, undefined];
    }
    if (item.properties.layer === 'carpark') {
      suggestionType = t('carpark', {
        lng
      });
      return [suggestionType, name, undefined, undefined];
    }
    if (item.properties.mode) {
      suggestionType = t(item.properties.mode.toLowerCase().replace('favourite', ''), {
        lng
      });
    } else {
      const layer = item.properties.layer.replace('route-', '').toLowerCase().replace('favourite', '');
      suggestionType = t(layer, {
        lng
      });
    }
    if (item.properties.id && (item.properties.layer === 'stop' || item.properties.layer === 'station')) {
      const stopCode = getStopCode(item.properties);
      const mode = item.properties.addendum?.GTFS.modes;
      const platform = getPlatform(item.properties.addendum, lng, t);
      return [suggestionType, getStopName(name, stopCode), label, stopCode, mode, platform];
    }
    if (item.properties.layer === 'favouriteStop' || item.properties.layer === 'favouriteStation') {
      const {
        address,
        code
      } = item.properties;
      const stoName = address ? getStopName(address.split(',')[0], code) : name;
      const platform = getPlatform(item.properties.addendum, lng);
      return [suggestionType, stoName, label, code, undefined, platform];
    }
    return [suggestionType, name, label];
  }
  const {
    origin,
    destination
  } = item.properties;
  const tail1 = origin.locality ? `, ${origin.locality} foobar` : '';
  const tail2 = destination.locality ? `, ${destination.locality}` : '';
  const name1 = origin.name;
  const name2 = destination.name;
  return [t('future-route', {
    lng
  }), `${t('origin', {
    lng
  })} ${name1}${tail1} ${t('destination', {
    lng
  })} ${name2}${tail2}`, item.translatedText];
}
function translateFutureRouteSuggestionTime(item, lng, t) {
  // check if i81n is initialized
  if (!t) {
    return undefined;
  }
  const time = DateTime.fromSeconds(Number(item.properties.time));
  const now = DateTime.now();
  let str = item.properties.arriveBy ? t('arrival', {
    lng
  }) : t('departure', {
    lng
  });
  if (time.hasSame(now, 'day')) {
    str = `${str} ${t('today-at')}`;
  } else if (time.hasSame(now.plus({
    days: 1
  }), 'day')) {
    str = `${str} ${t('tomorrow-at', {
      lng
    })}`;
  } else {
    str = `${str} ${time.toFormat('ccc d.L.')}`;
  }
  str = `${str} ${time.toFormat('HH:mm')}`;
  return str;
}
const getSuggestionValue = suggestion => {
  if (suggestion.type === 'SelectFromOwnLocations' || suggestion.type === 'back') {
    return '';
  }
  return getLabel(suggestion.properties);
};
const suggestionAsAriaContent = ({
  suggestions,
  t,
  lng
}) => {
  let label = [];
  const firstSuggestion = suggestions[0];
  if (firstSuggestion) {
    if (firstSuggestion.type && firstSuggestion.type.includes('Favourite')) {
      label.push(t('favourite', {
        lng
      }));
    }
    label = label.concat(getSuggestionContent(suggestions[0], lng));
  }
  return [...new Set(label)].join(' - ');
};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var lib = {exports: {}};

/*! For license information please see index.js.LICENSE.txt */

var hasRequiredLib;

function requireLib () {
	if (hasRequiredLib) return lib.exports;
	hasRequiredLib = 1;
	(function (module, exports) {
		!function(e,t){module.exports=t(React);}("undefined"!=typeof self?self:this,(function(e){return function(){var t={888:function(e,t,n){var o,r;r=void 0!==n.g?n.g:"undefined"!=typeof window?window:this,o=function(){return function(e){var t={ignore:"[data-scroll-ignore]",header:null,topOnEmptyHash:true,speed:500,speedAsDuration:false,durationMax:null,durationMin:null,clip:true,offset:0,easing:"easeInOutCubic",customEasing:null,updateURL:true,popstate:true,emitEvents:true},n=function(){var e={};return Array.prototype.forEach.call(arguments,(function(t){for(var n in t){if(!t.hasOwnProperty(n))return;e[n]=t[n];}})),e},o=function(e){"#"===e.charAt(0)&&(e=e.substr(1));for(var t,n=String(e),o=n.length,r=-1,u="",i=n.charCodeAt(0);++r<o;){if(0===(t=n.charCodeAt(r)))throw new InvalidCharacterError("Invalid character: the input contains U+0000.");u+=t>=1&&t<=31||127==t||0===r&&t>=48&&t<=57||1===r&&t>=48&&t<=57&&45===i?"\\"+t.toString(16)+" ":t>=128||45===t||95===t||t>=48&&t<=57||t>=65&&t<=90||t>=97&&t<=122?n.charAt(r):"\\"+n.charAt(r);}return "#"+u},r=function(){return Math.max(document.body.scrollHeight,document.documentElement.scrollHeight,document.body.offsetHeight,document.documentElement.offsetHeight,document.body.clientHeight,document.documentElement.clientHeight)},u=function(t){return t?(n=t,parseInt(e.getComputedStyle(n).height,10)+t.offsetTop):0;var n;},i=function(t,n,o){0===t&&document.body.focus(),o||(t.focus(),document.activeElement!==t&&(t.setAttribute("tabindex","-1"),t.focus(),t.style.outline="none"),e.scrollTo(0,n));},s=function(t,n,o,r){if(n.emitEvents&&"function"==typeof e.CustomEvent){var u=new CustomEvent(t,{bubbles:true,detail:{anchor:o,toggle:r}});document.dispatchEvent(u);}};return function(c,a){var l,f,d,v,p={cancelScroll:function(e){cancelAnimationFrame(v),v=null,e||s("scrollCancel",l);},animateScroll:function(o,c,a){p.cancelScroll();var f=n(l||t,a||{}),h="[object Number]"===Object.prototype.toString.call(o),m=h||!o.tagName?null:o;if(h||m){var y=e.pageYOffset;f.header&&!d&&(d=document.querySelector(f.header));var b,g,w,E=u(d),O=h?o:function(t,n,o,u){var i=0;if(t.offsetParent)do{i+=t.offsetTop,t=t.offsetParent;}while(t);return i=Math.max(i-n-o,0),u&&(i=Math.min(i,r()-e.innerHeight)),i}(m,E,parseInt("function"==typeof f.offset?f.offset(o,c):f.offset,10),f.clip),S=O-y,_=r(),I=0,M=function(e,t){var n=t.speedAsDuration?t.speed:Math.abs(e/1e3*t.speed);return t.durationMax&&n>t.durationMax?t.durationMax:t.durationMin&&n<t.durationMin?t.durationMin:parseInt(n,10)}(S,f),L=function(t){b||(b=t),I+=t-b,w=y+S*function(e,t){var n;return "easeInQuad"===e.easing&&(n=t*t),"easeOutQuad"===e.easing&&(n=t*(2-t)),"easeInOutQuad"===e.easing&&(n=t<.5?2*t*t:(4-2*t)*t-1),"easeInCubic"===e.easing&&(n=t*t*t),"easeOutCubic"===e.easing&&(n=--t*t*t+1),"easeInOutCubic"===e.easing&&(n=t<.5?4*t*t*t:(t-1)*(2*t-2)*(2*t-2)+1),"easeInQuart"===e.easing&&(n=t*t*t*t),"easeOutQuart"===e.easing&&(n=1- --t*t*t*t),"easeInOutQuart"===e.easing&&(n=t<.5?8*t*t*t*t:1-8*--t*t*t*t),"easeInQuint"===e.easing&&(n=t*t*t*t*t),"easeOutQuint"===e.easing&&(n=1+--t*t*t*t*t),"easeInOutQuint"===e.easing&&(n=t<.5?16*t*t*t*t*t:1+16*--t*t*t*t*t),e.customEasing&&(n=e.customEasing(t)),n||t}(f,g=(g=0===M?0:I/M)>1?1:g),e.scrollTo(0,Math.floor(w)),function(t,n){var r=e.pageYOffset;if(t==n||r==n||(y<n&&e.innerHeight+r)>=_)return p.cancelScroll(true),i(o,n,h),s("scrollStop",f,o,c),b=null,v=null,true}(w,O)||(v=e.requestAnimationFrame(L),b=t);};0===e.pageYOffset&&e.scrollTo(0,0),function(e,t,n){t||history.pushState&&n.updateURL&&history.pushState({smoothScroll:JSON.stringify(n),anchor:e.id},document.title,e===document.documentElement?"#top":"#"+e.id);}(o,h,f),"matchMedia"in e&&e.matchMedia("(prefers-reduced-motion)").matches?i(o,Math.floor(O),false):(s("scrollStart",f,o,c),p.cancelScroll(true),e.requestAnimationFrame(L));}}},h=function(t){if(!t.defaultPrevented&&!(0!==t.button||t.metaKey||t.ctrlKey||t.shiftKey)&&"closest"in t.target&&(f=t.target.closest(c))&&"a"===f.tagName.toLowerCase()&&!t.target.closest(l.ignore)&&f.hostname===e.location.hostname&&f.pathname===e.location.pathname&&/#/.test(f.href)){var n,r;try{n=o(decodeURIComponent(f.hash));}catch(e){n=o(f.hash);}if("#"===n){if(!l.topOnEmptyHash)return;r=document.documentElement;}else r=document.querySelector(n);(r=r||"#top"!==n?r:document.documentElement)&&(t.preventDefault(),function(t){if(history.replaceState&&t.updateURL&&!history.state){var n=e.location.hash;n=n||"",history.replaceState({smoothScroll:JSON.stringify(t),anchor:n||e.pageYOffset},document.title,n||e.location.href);}}(l),p.animateScroll(r,f));}},m=function(e){if(null!==history.state&&history.state.smoothScroll&&history.state.smoothScroll===JSON.stringify(l)){var t=history.state.anchor;"string"==typeof t&&t&&!(t=document.querySelector(o(history.state.anchor)))||p.animateScroll(t,null,{updateURL:false});}};return p.destroy=function(){l&&(document.removeEventListener("click",h,false),e.removeEventListener("popstate",m,false),p.cancelScroll(),l=null,f=null,d=null,v=null);},function(){if(!("querySelector"in document&&"addEventListener"in e&&"requestAnimationFrame"in e&&"closest"in e.Element.prototype))throw "Smooth Scroll: This browser does not support the required JavaScript methods and browser APIs.";p.destroy(),l=n(t,a||{}),d=l.header?document.querySelector(l.header):null,document.addEventListener("click",h,false),l.updateURL&&l.popstate&&e.addEventListener("popstate",m,false);}(),p}}(r)}.apply(t,[]),void 0===o||(e.exports=o);},432:function(e,t,n){var o=this&&this.__createBinding||(Object.create?function(e,t,n,o){ void 0===o&&(o=n),Object.defineProperty(e,o,{enumerable:true,get:function(){return t[n]}});}:function(e,t,n,o){ void 0===o&&(o=n),e[o]=t[n];}),r=this&&this.__exportStar||function(e,t){for(var n in e)"default"===n||Object.prototype.hasOwnProperty.call(t,n)||o(t,e,n);};Object.defineProperty(t,"__esModule",{value:true}),r(n(47),t),r(n(926),t),r(n(349),t),r(n(137),t),r(n(729),t),r(n(383),t),r(n(450),t),r(n(358),t),r(n(753),t),r(n(869),t),r(n(352),t),r(n(446),t),r(n(164),t),r(n(305),t),r(n(502),t);},349:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useArrowNavigation=void 0;var o=n(156);t.useArrowNavigation=function(e){var t=e.listItems,n=e.onArrowUpOrDown,r=(0, o.useState)(0),u=r[0],i=r[1],s=(0, o.useCallback)((function(e){i(e);}),[t]),c=function(e){var o=e.key;if(null==t?void 0:t.length)if("ArrowUp"===o)n&&n(),e.preventDefault(),s(u>0?u-1:t.length-1);else {if("ArrowDown"!==o)return;n&&n(),e.preventDefault(),u<t.length-1?s(u+1):s(0);}};return (0, o.useEffect)((function(){return window.addEventListener("keydown",c),function(){return window.removeEventListener("keydown",c)}})),(0, o.useEffect)((function(){s(0);}),[t]),{selectedIndex:u}};},446:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useDebounce=void 0;var o=n(156);t.useDebounce=function(e,t){ void 0===t&&(t=300);var n=(0, o.useState)(e),r=n[0],u=n[1];return (0, o.useEffect)((function(){var n=setTimeout((function(){u(e);}),t);return function(){clearTimeout(n);}}),[e,t]),r};},164:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useElementSize=void 0;var o=n(156);t.useElementSize=function(){var e,t,n=(0, o.useRef)(null);return [n,{width:null===(e=null==n?void 0:n.current)||void 0===e?void 0:e.clientWidth,height:null===(t=null==n?void 0:n.current)||void 0===t?void 0:t.clientHeight}]},t.default=t.useElementSize;},137:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useIntersectionObserver=void 0;var o=n(156);t.useIntersectionObserver=function(e){var t=(0, o.useRef)(null),n=(0, o.useState)(false),r=n[0],u=n[1],i=function(e){var t=e[0];u(t.isIntersecting);};return (0, o.useEffect)((function(){if(window&&window.IntersectionObserver){var n=new IntersectionObserver(i,e),o=t.current;return t.current&&n.observe(t.current),function(){o&&n.unobserve(o);}}u(false);}),[t,e]),{containerRef:t,isVisible:r}},t.default=t.useIntersectionObserver;},729:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useIsBreakpoint=void 0;var o=n(156),r=n(432),u={xs:1,mobileSmall:320,mobile:375,tablet:768,tabletLarge:920,desktopSmall:1024,desktop:1260,desktopLarge:1440};t.useIsBreakpoint=function(e){var t="string"==typeof e?u[e]:e,n=(0, o.useState)(null),i=n[0],s=n[1],c=(0, o.useCallback)((function(){s(window.innerWidth);}),[]);return (0, o.useEffect)((function(){c();}),[]),(0, r.useOnResize)(c),i>=t},t.default=t.useIsBreakpoint;},450:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useIsLoadingAfterError=void 0;var o=n(156);t.useIsLoadingAfterError=function(e,t){var n=(0, o.useState)(t),r=n[0],u=n[1];return (0, o.useEffect)((function(){e||u(t);}),[e,t]),e&&r};},305:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useIsMobile=void 0;var o=n(729);t.useIsMobile=function(){return !(0, o.useIsBreakpoint)("tablet")},t.default=t.useIsMobile;},383:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useIsomorphicLayoutEffect=void 0;var o=n(156);t.useIsomorphicLayoutEffect="undefined"!=typeof window?o.useLayoutEffect:o.useEffect,t.default=t.useIsomorphicLayoutEffect;},753:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useOnLoseFocusWithin=void 0;var o=n(156);t.useOnLoseFocusWithin=function(e){var t=e.ref,n=e.callback,r=e.watch;(0, o.useEffect)((function(){var e=function(){var e;if(r)return (null===(e=t.current)||void 0===e?void 0:e.matches(":focus-within"))?void 0:n()};return document.addEventListener("keyup",e),function(){return document.removeEventListener("keyup",e)}}),[n,t,r]);},t.default=t.useOnLoseFocusWithin;},502:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useOnResize=void 0;var o=n(156);t.useOnResize=function(e){(0, o.useEffect)((function(){var t=function(){e(window.innerWidth);};return window.addEventListener("resize",t,{passive:true}),function(){return window.removeEventListener("resize",t)}}),[]);},t.default=t.useOnResize;},352:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useOnWindowFocus=void 0;var o=n(156);t.useOnWindowFocus=function(e){(0, o.useEffect)((function(){var t=function(){e&&"visible"===document.visibilityState&&e();};return window.addEventListener("visibilitychange",t,false),function(){return window.removeEventListener("visibilitychange",t)}}),[e]);};},47:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useOutsideReacter=void 0;var o=n(156);t.useOutsideReacter=function(e,t){function n(n){e.current&&!e.current.contains(n.target)&&t();}(0, o.useEffect)((function(){return document.addEventListener("mousedown",n),function(){document.removeEventListener("mousedown",n);}}));};},869:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useRunAfterUpdate=void 0;var o=n(156);t.useRunAfterUpdate=function(){var e=(0, o.useRef)(null);return (0, o.useEffect)((function(){e.current&&(e.current(),e.current=null);})),function(t){e.current=t;}},t.default=t.useRunAfterUpdate;},926:function(e,t,n){Object.defineProperty(t,"__esModule",{value:true}),t.useScrollLock=void 0;var o=n(156);t.useScrollLock=function(){return {lock:(0, o.useCallback)((function(){var e=window.scrollY+"px",t=document.body;t.style.position="fixed",t.style.top="-"+e;}),[]),unlock:(0, o.useCallback)((function(){var e=document.body,t=e.style.top,n=-1*parseInt(t||"0");e.style.position="",e.style.top="",n&&window.scrollTo(0,-1*parseInt(t||"0"));}),[])}};},358:function(e,t,n){var o=this&&this.__importDefault||function(e){return e&&e.__esModule?e:{default:e}};Object.defineProperty(t,"__esModule",{value:true}),t.useSmoothScroll=void 0;var r=n(156),u=o(n(888));t.useSmoothScroll=function(e){var t=(0, r.useRef)(null),n=e.ref,o=e.offset,i=void 0===o?0:o,s=e.speed,c=void 0===s?500:s;return (0, r.useEffect)((function(){t.current=new u.default(null,{speed:c,speedAsDuration:true});}),[]),(0, r.useCallback)((function(){n&&t.current&&t.current.animateScroll(n.current,null,{offset:function(){return i},updateURL:false});}),[i,n])};},156:function(t){t.exports=e;}},n={};function o(e){var r=n[e];if(void 0!==r)return r.exports;var u=n[e]={exports:{}};return t[e].call(u.exports,u,u.exports,o),u.exports}return o.g=function(){if("object"==typeof globalThis)return globalThis;try{return this||new Function("return this")()}catch(e){if("object"==typeof window)return window}}(),o(432)}()}));
		
	} (lib));
	return lib.exports;
}

var libExports = requireLib();
var hooks = /*@__PURE__*/getDefaultExportFromCjs(libExports);

var css_248z$1 = "input[type=text] {\n  margin: 0;\n  padding: 0;\n}\n\n.MobileSearch_fullscreen-root__-fKuG {\n  display: flex;\n  flex-direction: column;\n  position: fixed;\n  width: 100%;\n  height: 100%;\n  top: 0;\n  left: 0;\n  background: #fff;\n  z-index: 1000;\n}\n\n.MobileSearch_combobox-container__UApoj {\n  margin: 15px 10px 0;\n  border: solid #888 1px;\n  display: flex;\n  align-items: center;\n  border-radius: 5px;\n  height: 60px;\n  justify-content: space-between;\n}\n.MobileSearch_combobox-container__UApoj .MobileSearch_right-column__n9ied {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  height: 100%;\n  padding-top: 9px;\n}\n\n.MobileSearch_combobox-icon__91hHs {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 100%;\n  width: 52px;\n  border: 0;\n  background: none;\n}\n.MobileSearch_combobox-icon__91hHs svg {\n  width: 14px;\n  height: 21px;\n  transform: rotate(-180deg);\n}\n\n.MobileSearch_combobox-label__TVrz1 {\n  position: relative;\n  top: -2px;\n  font-size: 12px;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: -0.2px;\n  color: #333;\n}\n\n.MobileSearch_container__fxGZ7 {\n  display: flex;\n  align-items: center;\n  width: 100%;\n}\n.MobileSearch_container__fxGZ7 .MobileSearch_input__bUFhg {\n  font-family: inherit;\n  width: 100%;\n  position: relative;\n  padding-bottom: 7px;\n  top: 2px;\n  border: none;\n  height: 26px;\n  font-weight: normal;\n  box-shadow: none;\n  font-size: 16px;\n  letter-spacing: -0.5px;\n  color: #666;\n  appearance: none;\n}\n.MobileSearch_container__fxGZ7 .MobileSearch_input__bUFhg:focus {\n  outline: none;\n  border: none;\n  box-shadow: none;\n  background: #fff;\n}\n\nbutton.MobileSearch_clear-input__AaH3X {\n  display: flex;\n  align-items: center;\n  right: 0;\n  padding: 0 15px 7px 0;\n  height: 26px;\n  color: var(--color);\n  border: 0;\n  margin: 0;\n  justify-content: center;\n  text-align: left;\n  background: none;\n  border-style: none;\n  border-width: 0;\n  cursor: pointer;\n  font-family: inherit;\n  background-color: transparent;\n  transition: none;\n}\nbutton.MobileSearch_clear-input__AaH3X svg {\n  fill: var(--color);\n  width: 16px;\n  height: 16px;\n}\nbutton.MobileSearch_clear-input__AaH3X:focus {\n  background-color: transparent;\n  color: inherit;\n}\nbutton.MobileSearch_clear-input__AaH3X:active {\n  background-color: transparent;\n  color: inherit;\n}\nbutton.MobileSearch_clear-input__AaH3X:hover {\n  background-color: transparent;\n  color: inherit;\n}\n\n.MobileSearch_hasValue__1Txox {\n  color: #333;\n}\n\n.MobileSearch_suggestion__v2-T8 {\n  font-size: 15px;\n  min-height: 50px;\n  letter-spacing: -0.6px;\n  color: #333;\n  font-weight: 500;\n  background: #fff;\n  margin: 0;\n  padding: 0;\n}\n\n.MobileSearch_suggestionHighlighted__laMbA {\n  background-color: #f2f5f7;\n}\n\n.MobileSearch_suggestionsContainerOpen__-ZF8G {\n  display: flex;\n  position: absolute;\n  margin-bottom: auto;\n  z-index: 20;\n  width: 100vw;\n  max-height: calc(100% - 75px);\n  height: 100%;\n  top: 75px;\n  left: 0;\n  overflow-x: hidden;\n  overflow-y: scroll;\n}\n.MobileSearch_suggestionsContainerOpen__-ZF8G .MobileSearch_suggestionsList__zRJY4 {\n  width: 100vw;\n  list-style: none;\n  padding-left: 0;\n  margin: 0;\n  background: #f2f5f7;\n}\n\n.MobileSearch_clear-search-history__XpFfN {\n  display: flex;\n  font-family: inherit;\n  margin-top: auto;\n  align-items: center;\n  justify-content: center;\n  width: 100%;\n  height: 62px;\n  background: #f2f5f7;\n  color: var(--accessible-primary-color);\n  font-size: 15px;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: -0.42px;\n  border: none;\n  border-top: 1px solid #ddd;\n}\n.MobileSearch_suggestionHighlighted__laMbA .MobileSearch_clear-search-history__XpFfN {\n  background-color: transparent;\n  color: inherit;\n}\n\n.MobileSearch_mobile-modal-overlay__BDcbD {\n  z-index: 9999;\n  top: 0;\n  left: 0;\n  right: unset;\n  bottom: unset;\n  margin: 0;\n  padding: 0;\n  width: 100%;\n  height: 100%;\n  background-color: white;\n  position: fixed;\n}\n\n.MobileSearch_mobile-modal__-gEAQ {\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n}\n.MobileSearch_mobile-modal__-gEAQ.ReactModal__Content--after-open > div {\n  transform: translateY(0);\n}\n.MobileSearch_mobile-modal__-gEAQ.ReactModal__Content--before-close > div {\n  transform: translateY(10%);\n}\n\n.MobileSearch_mobile-modal-content__mUUFh {\n  position: fixed;\n  width: 100%;\n  height: 100%;\n  outline: none;\n  background-color: #fff;\n  transition: transform 0.25s;\n  transform: translateY(10%);\n}";
var mobileStyles = {"fullscreen-root":"MobileSearch_fullscreen-root__-fKuG","combobox-container":"MobileSearch_combobox-container__UApoj","right-column":"MobileSearch_right-column__n9ied","combobox-icon":"MobileSearch_combobox-icon__91hHs","combobox-label":"MobileSearch_combobox-label__TVrz1","container":"MobileSearch_container__fxGZ7","input":"MobileSearch_input__bUFhg","clear-input":"MobileSearch_clear-input__AaH3X","hasValue":"MobileSearch_hasValue__1Txox","suggestion":"MobileSearch_suggestion__v2-T8","suggestionHighlighted":"MobileSearch_suggestionHighlighted__laMbA","suggestionsContainerOpen":"MobileSearch_suggestionsContainerOpen__-ZF8G","suggestionsList":"MobileSearch_suggestionsList__zRJY4","clear-search-history":"MobileSearch_clear-search-history__XpFfN","mobile-modal-overlay":"MobileSearch_mobile-modal-overlay__BDcbD","mobile-modal":"MobileSearch_mobile-modal__-gEAQ","mobile-modal-content":"MobileSearch_mobile-modal-content__mUUFh"};
styleInject(css_248z$1);

var css_248z = "input[type=text] {\n  margin: 0;\n  padding: 0;\n}\n\n.MobileNoScroll_fullscreen-root__z0ny4 {\n  display: flex;\n  flex-direction: column;\n  position: fixed;\n  width: 100%;\n  height: 100%;\n  top: 0;\n  left: 0;\n  background: #fff;\n  z-index: 1000;\n}\n\n.MobileNoScroll_combobox-container__TTtSg {\n  margin: 15px 10px 0;\n  border: solid #888 1px;\n  display: flex;\n  align-items: center;\n  border-radius: 5px;\n  height: 60px;\n  justify-content: space-between;\n}\n.MobileNoScroll_combobox-container__TTtSg .MobileNoScroll_right-column__TS4N7 {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  height: 100%;\n  padding-top: 9px;\n}\n\n.MobileNoScroll_combobox-icon__-r8Qf {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 100%;\n  width: 52px;\n  border: 0;\n  background: none;\n}\n.MobileNoScroll_combobox-icon__-r8Qf svg {\n  width: 14px;\n  height: 21px;\n  transform: rotate(-180deg);\n}\n\n.MobileNoScroll_combobox-label__rQ8p- {\n  position: relative;\n  top: -2px;\n  font-size: 12px;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: -0.2px;\n  color: #333;\n}\n\n.MobileNoScroll_container__XXfFb {\n  display: flex;\n  align-items: center;\n  width: 100%;\n}\n.MobileNoScroll_container__XXfFb .MobileNoScroll_input__Xaj1- {\n  font-family: inherit;\n  width: 100%;\n  position: relative;\n  padding-bottom: 7px;\n  top: 2px;\n  border: none;\n  height: 26px;\n  font-weight: normal;\n  box-shadow: none;\n  font-size: 16px;\n  letter-spacing: -0.5px;\n  color: #666;\n  appearance: none;\n}\n.MobileNoScroll_container__XXfFb .MobileNoScroll_input__Xaj1-:focus {\n  outline: none;\n  border: none;\n  box-shadow: none;\n  background: #fff;\n}\n\nbutton.MobileNoScroll_clear-input__Jz4Xv {\n  display: flex;\n  align-items: center;\n  right: 0;\n  padding: 0 15px 7px 0;\n  height: 26px;\n  color: var(--color);\n  border: 0;\n  margin: 0;\n  justify-content: center;\n  text-align: left;\n  background: none;\n  border-style: none;\n  border-width: 0;\n  cursor: pointer;\n  font-family: inherit;\n  background-color: transparent;\n  transition: none;\n}\nbutton.MobileNoScroll_clear-input__Jz4Xv svg {\n  fill: var(--color);\n  width: 16px;\n  height: 16px;\n}\nbutton.MobileNoScroll_clear-input__Jz4Xv:focus {\n  background-color: transparent;\n  color: inherit;\n}\nbutton.MobileNoScroll_clear-input__Jz4Xv:active {\n  background-color: transparent;\n  color: inherit;\n}\nbutton.MobileNoScroll_clear-input__Jz4Xv:hover {\n  background-color: transparent;\n  color: inherit;\n}\n\n.MobileNoScroll_hasValue__ftyO6 {\n  color: #333;\n}\n\n.MobileNoScroll_suggestion__7L2kI {\n  font-size: 15px;\n  min-height: 50px;\n  letter-spacing: -0.6px;\n  color: #333;\n  font-weight: 500;\n  background: #fff;\n  margin: 0;\n  padding: 0;\n}\n\n.MobileNoScroll_suggestionHighlighted__IKy2t {\n  background-color: #f2f5f7;\n}\n\n.MobileNoScroll_suggestionsContainerOpen__ePksf {\n  display: flex;\n  position: absolute;\n  margin-bottom: auto;\n  z-index: 20;\n  width: 100vw;\n  max-height: calc(100% - 75px);\n  height: 100%;\n  top: 75px;\n  left: 0;\n  overflow-x: hidden;\n  overflow-y: scroll;\n}\n.MobileNoScroll_suggestionsContainerOpen__ePksf .MobileNoScroll_suggestionsList__aeQlJ {\n  width: 100vw;\n  list-style: none;\n  padding-left: 0;\n  margin: 0;\n  background: #f2f5f7;\n}\n\n.MobileNoScroll_clear-search-history__WrE0K {\n  display: flex;\n  font-family: inherit;\n  margin-top: auto;\n  align-items: center;\n  justify-content: center;\n  width: 100%;\n  height: 62px;\n  background: #f2f5f7;\n  color: var(--accessible-primary-color);\n  font-size: 15px;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: -0.42px;\n  border: none;\n  border-top: 1px solid #ddd;\n}\n.MobileNoScroll_suggestionHighlighted__IKy2t .MobileNoScroll_clear-search-history__WrE0K {\n  background-color: transparent;\n  color: inherit;\n}\n\n.MobileNoScroll_mobile-modal-overlay__k1JdU {\n  z-index: 9999;\n  top: 0;\n  left: 0;\n  right: unset;\n  bottom: unset;\n  margin: 0;\n  padding: 0;\n  width: 100%;\n  height: 100%;\n  background-color: white;\n  position: fixed;\n}\n\n.MobileNoScroll_mobile-modal__x5kKM {\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n}\n.MobileNoScroll_mobile-modal__x5kKM.ReactModal__Content--after-open > div {\n  transform: translateY(0);\n}\n.MobileNoScroll_mobile-modal__x5kKM.ReactModal__Content--before-close > div {\n  transform: translateY(10%);\n}\n\n.MobileNoScroll_mobile-modal-content__os7v8 {\n  position: fixed;\n  width: 100%;\n  height: 100%;\n  outline: none;\n  background-color: #fff;\n  transition: transform 0.25s;\n  transform: translateY(10%);\n}\n\n.MobileNoScroll_suggestionsContainerOpen__ePksf {\n  -ms-overflow-style: none; /* Internet Explorer 10+ */\n  scrollbar-width: none; /* Firefox */\n}\n.MobileNoScroll_suggestionsContainerOpen__ePksf::-webkit-scrollbar {\n  display: none; /* Safari and Chrome */\n}";
var mobileNoScrollStyles = {"fullscreen-root":"MobileNoScroll_fullscreen-root__z0ny4","combobox-container":"MobileNoScroll_combobox-container__TTtSg","right-column":"MobileNoScroll_right-column__TS4N7","combobox-icon":"MobileNoScroll_combobox-icon__-r8Qf","combobox-label":"MobileNoScroll_combobox-label__rQ8p-","container":"MobileNoScroll_container__XXfFb","input":"MobileNoScroll_input__Xaj1-","clear-input":"MobileNoScroll_clear-input__Jz4Xv","hasValue":"MobileNoScroll_hasValue__ftyO6","suggestion":"MobileNoScroll_suggestion__7L2kI","suggestionHighlighted":"MobileNoScroll_suggestionHighlighted__IKy2t","suggestionsContainerOpen":"MobileNoScroll_suggestionsContainerOpen__ePksf","suggestionsList":"MobileNoScroll_suggestionsList__aeQlJ","clear-search-history":"MobileNoScroll_clear-search-history__WrE0K","mobile-modal-overlay":"MobileNoScroll_mobile-modal-overlay__k1JdU","mobile-modal":"MobileNoScroll_mobile-modal__x5kKM","mobile-modal-content":"MobileNoScroll_mobile-modal-content__os7v8"};
styleInject(css_248z);

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}

const _excluded = ["item", "lng", "getItemProps", "highlightedIndex", "itemIndex", "styles"],
  _excluded2 = ["suggestions", "getMenuProps", "getItemProps", "lng", "styles", "hidden", "renderClearHistoryButton", "handleClearHistory"];
function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const Suggestion = /*#__PURE__*/memo(function Suggestion(_ref) {
  let {
      item,
      lng,
      getItemProps,
      highlightedIndex,
      itemIndex,
      styles
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [t] = useTranslation();
  const newItem = item.type === 'FutureRoute' ? _objectSpread$3(_objectSpread$3({}, item), {}, {
    translatedText: translateFutureRouteSuggestionTime(item, lng, t)
  }) : item;
  const content = getSuggestionContent(item, lng, t);
  return /*#__PURE__*/React.createElement("li", Object.assign({
    className: cx(styles.suggestion, highlightedIndex === itemIndex && styles.suggestionHighlighted)
  }, getItemProps({
    index: itemIndex
  })), /*#__PURE__*/React.createElement(SuggestionItem, Object.assign({
    item: newItem,
    content: content,
    lng: lng
  }, rest)));
});
const itemShape = process.env.NODE_ENV !== "production" ? PropTypes.shape({
  name: PropTypes.string,
  type: PropTypes.string,
  address: PropTypes.string,
  selectedIconId: PropTypes.string,
  iconColor: PropTypes.string,
  translatedText: PropTypes.string,
  properties: PropTypes.shape({
    layer: PropTypes.string,
    color: PropTypes.string,
    localadmin: PropTypes.string,
    mode: PropTypes.string,
    id: PropTypes.string,
    source: PropTypes.string,
    arrowClicked: PropTypes.bool,
    destination: PropTypes.shape({
      name: PropTypes.string,
      localadmin: PropTypes.string
    }),
    origin: PropTypes.shape({
      name: PropTypes.string,
      localadmin: PropTypes.string
    })
  })
}) : {};
process.env.NODE_ENV !== "production" ? Suggestion.propTypes = {
  item: itemShape.isRequired,
  lng: PropTypes.string.isRequired,
  getItemProps: PropTypes.func.isRequired,
  highlightedIndex: PropTypes.number.isRequired,
  itemIndex: PropTypes.number.isRequired,
  styles: PropTypes.objectOf(PropTypes.string).isRequired
} : void 0;
const Suggestions = /*#__PURE__*/memo(function Suggestions(_ref2) {
  let {
      suggestions,
      getMenuProps,
      getItemProps,
      lng,
      styles,
      hidden,
      renderClearHistoryButton = false,
      handleClearHistory = () => undefined
    } = _ref2,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  const [t] = useTranslation();
  return /*#__PURE__*/React.createElement("div", {
    className: cx(styles.suggestionsContainerOpen, styles.suggestionsContainer, hidden && styles.hidden)
  }, /*#__PURE__*/React.createElement("ul", Object.assign({
    className: styles.suggestionsList
  }, getMenuProps()), suggestions.map((suggestion, i) => {
    return /*#__PURE__*/React.createElement(Suggestion, Object.assign({
      key: `${suggestion.type}-${suggestion.properties.gid || suggestion.properties.gtfsId || JSON.stringify(suggestion.properties)}`,
      getItemProps: getItemProps,
      itemIndex: i,
      item: suggestion,
      lng: lng,
      styles: styles
    }, rest));
  }), renderClearHistoryButton &&
  /*#__PURE__*/
  // Not a selectable downshift item, so getItemProps is intentionally
  // not used here (its index would always be out of the items bounds).
  React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
    onClick: handleClearHistory,
    type: "button",
    className: styles['clear-search-history']
  }, t('clear-search-history', {
    lng
  })))));
});
process.env.NODE_ENV !== "production" ? Suggestions.propTypes = {
  suggestions: PropTypes.arrayOf(itemShape).isRequired,
  getMenuProps: PropTypes.func.isRequired,
  getItemProps: PropTypes.func.isRequired,
  lng: PropTypes.string.isRequired,
  styles: PropTypes.objectOf(PropTypes.string).isRequired,
  hidden: PropTypes.bool.isRequired,
  renderClearHistoryButton: PropTypes.bool,
  handleClearHistory: PropTypes.func
} : void 0;

/**
 * Input clear button element
 * @typedef {object} ClearButtonProps
 * @property {string} color // hex color string
 * @property {string} lng // language, eg. 'fi'
 * @property {() => void} clearInput
 * @property {() => void} [onKeyDown]
 * @param {ClearButtonProps} props
 * @returns {JSX.Element}
 */
const ClearButton = ({
  color,
  lng,
  clearInput,
  onKeyDown,
  styles
}) => {
  const [t] = useTranslation();
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: styles['clear-input'],
    onClick: clearInput,
    "aria-label": t('clear-button-label', {
      lng
    }),
    onKeyDown: onKeyDown
  }, /*#__PURE__*/React.createElement(Icon, {
    img: "close",
    color: color
  }));
};
process.env.NODE_ENV !== "production" ? ClearButton.propTypes = {
  color: PropTypes.string.isRequired,
  lng: PropTypes.string.isRequired,
  clearInput: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  styles: PropTypes.objectOf(PropTypes.string).isRequired
} : void 0;

function Input({
  id,
  lng,
  value,
  placeholder,
  required,
  getInputProps,
  getLabelProps,
  clearInput,
  ariaLabel,
  inputRef,
  styles,
  renderLabel = false,
  isMobile,
  inputClassName,
  transportMode,
  clearButtonColor,
  autoFocus = false,
  inputOnBlur = () => {},
  onValueChange
}) {
  const handleChange = e => {
    // Downshift reports the new value only in an effect, one render later. Until
    // then it feeds the stale controlled inputValue back into the DOM node, which
    // wipes the keystroke and desyncs Android keyboards' composing range.
    onValueChange(e.target.value);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, renderLabel && /*#__PURE__*/React.createElement("label", getLabelProps({
    className: styles['sr-only'],
    htmlFor: id
  }), ariaLabel), /*#__PURE__*/React.createElement("input", Object.assign({
    "aria-label": ariaLabel
    // eslint-disable-next-line jsx-a11y/no-autofocus
    ,
    autoFocus: autoFocus,
    placeholder: placeholder,
    required: required,
    type: "text",
    className: cx(styles.input, styles[id], inputClassName, isMobile && transportMode && styles.thin, value && styles.hasValue),
    id: id
  }, getInputProps({
    ref: inputRef,
    onBlur: inputOnBlur,
    onChange: handleChange
  }))), value && /*#__PURE__*/React.createElement(ClearButton, {
    lng: lng,
    clearInput: () => clearInput(inputRef),
    styles: styles,
    color: clearButtonColor,
    onKeyDown: e => isKeyboardSelectionEvent(e) && clearInput(inputRef)
  }));
}
process.env.NODE_ENV !== "production" ? Input.propTypes = {
  id: PropTypes.string.isRequired,
  lng: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  getInputProps: PropTypes.func.isRequired,
  getLabelProps: PropTypes.func.isRequired,
  clearInput: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  inputRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  }).isRequired,
  styles: PropTypes.objectOf(PropTypes.string).isRequired,
  renderLabel: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  inputClassName: PropTypes.string.isRequired,
  transportMode: PropTypes.string,
  clearButtonColor: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  inputOnBlur: PropTypes.func,
  onValueChange: PropTypes.func.isRequired
} : void 0;

function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

/**
 * @typedef AutosuggestState
 * @property {array} suggestions
 * @property {boolean} renderMobile
 *
 * @typedef MobileViewProps
 * @property {string} appElement
 * @property {string} id
 * @property {string} placeholder
 * @property {function} clearInput
 * @property {function} onSelectedItemChange
 * @property {object} fontWeights
 * @property {function} clearOldSearches
 * @property {object} suggestionProps
 * @property {object} colors
 * @property {string} lng
 * @property {object} ariaProps
 * @property {string} value
 * @property {string} inputClassName
 * @property {boolean} required
 * @property {string} [mobileLabel]
 * @property {boolean} [showScroll]
 * @property {AutosuggestState} state
 * @property {function} dispatch
 * @property {Object} pendingEnterRef ref tracking whether a selection was
 *   requested (click/enter) while suggestions were still loading; shared
 *   with the parent DTAutosuggest component
 *
 * @param {MobileViewProps} props
 * @returns {JSX.Element}
 */
const MobileView = ({
  appElement,
  id,
  placeholder,
  clearInput,
  onSelectedItemChange,
  fontWeights,
  clearOldSearches,
  suggestionProps,
  lng,
  ariaProps,
  value,
  inputClassName,
  required,
  mobileLabel,
  showScroll,
  state,
  dispatch,
  pendingEnterRef,
  colors
}) => {
  const [t] = useTranslation();
  const {
    lock,
    unlock
  } = hooks.useScrollLock();
  const styles = showScroll ? mobileStyles : mobileNoScrollStyles;
  const inputId = `${id}-input`;
  const labelId = `${id}-label`;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const inputRef = React.useRef();
  useEffect(() => {
    ReactModal.setAppElement(appElement);
  }, []);
  useEffect(() => {
    if (state.renderMobile) {
      lock();
    } else {
      unlock();
    }
  }, [state.renderMobile]);

  /**
   * independent hooks in mobile view.
   * inputValue and suggestion states are kept in the parent
   */
  const {
    highlightedIndex,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    setHighlightedIndex
  } = useCombobox({
    items: state.suggestions,
    inputValue: value,
    isOpen: true,
    onSelectedItemChange: changes => {
      onSelectedItemChange(changes);
      dispatch({
        type: 'TOGGLE_MENU',
        isMobile: true
      });
    },
    inputId,
    labelId,
    selectedItem: null,
    defaultHighlightedIndex: -1,
    onInputValueChange: ({
      inputValue,
      type
    }) => state.renderMobile &&
    // prevent updating value when the search is not open
    type !== useCombobox.stateChangeTypes.InputChange &&
    // typing is dispatched synchronously by Input
    dispatch({
      type: 'INPUT_CHANGE',
      value: inputValue
    }),
    stateReducer: (oldState, {
      changes,
      type
    }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          {
            setHighlightedIndex(-1);
            dispatch({
              type: 'TOGGLE_MENU',
              isMobile: true
            });
            return oldState;
          }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (state.loading) {
            // Refs are designed to be mutated directly; this signals the
            // parent's "pending enter" effect once suggestions have loaded.
            // eslint-disable-next-line no-param-reassign
            pendingEnterRef.current = true;
            return oldState;
          }
          // select the first item if none is highlighted
          if (oldState.highlightedIndex === -1) {
            return _objectSpread$2(_objectSpread$2({}, changes), {}, {
              selectedItem: state.suggestions[0]
            });
          }
          return changes;
        // allows tabbing out of input field without closing
        case useCombobox.stateChangeTypes.InputBlur:
          {
            return oldState;
          }
        default:
          return changes;
      }
    },
    itemToString(suggestion) {
      return suggestion ? getSuggestionValue(suggestion) : '';
    }
  });
  // call to suppress ref errors from downshift
  getMenuProps({}, {
    suppressRefError: true
  });
  getInputProps({}, {
    suppressRefError: true
  });
  const handleValueChange = useCallback(newValue => state.renderMobile && dispatch({
    type: 'INPUT_CHANGE',
    value: newValue
  }), [state.renderMobile, dispatch]);
  const handleClearHistory = useCallback(() => setDialogOpen(true), []);
  const {
    ariaRequiredText,
    SearchBarId,
    ariaCurrentSuggestion
  } = ariaProps;
  const ariaLabel = ariaRequiredText.concat(' ').concat(SearchBarId).concat(' ').concat(ariaCurrentSuggestion);
  return /*#__PURE__*/React.createElement(ReactModal, {
    isOpen: state.renderMobile,
    className: styles['mobile-modal'],
    overlayClassName: styles['mobile-modal-overlay'],
    shouldReturnFocusAfterClose: false,
    shouldCloseOnEsc: true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['mobile-modal-content'],
    style: {
      '--color': colors.primary,
      '--accessible-primary-color': colors.accessiblePrimary,
      '--hover-color': colors.hover,
      '--font-weight-medium': fontWeights.medium
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['combobox-container'],
    htmlFor: inputId
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: styles['combobox-icon'],
    onClick: () => {
      setHighlightedIndex(-1);
      dispatch({
        type: 'TOGGLE_MENU',
        isMobile: true
      });
    },
    onKeyDown: e => {
      if (isKeyboardSelectionEvent(e)) {
        setHighlightedIndex(-1);
        dispatch({
          type: 'TOGGLE_MENU',
          isMobile: true
        });
      }
    },
    "aria-label": t('cancel', {
      lng
    }),
    tabIndex: 0
  }, /*#__PURE__*/React.createElement(Icon, {
    img: "arrow",
    color: colors.primary
  })), /*#__PURE__*/React.createElement("span", {
    className: styles['right-column']
  }, /*#__PURE__*/React.createElement("span", Object.assign({
    className: styles['combobox-label'],
    id: labelId
  }, getLabelProps()), mobileLabel), /*#__PURE__*/React.createElement(Input, {
    placeholder: isPOISearch(id) ? t('address-place-or-business', {
      lng
    }) : placeholder,
    id: id,
    lng: lng,
    value: value,
    getInputProps: getInputProps,
    getLabelProps: getLabelProps,
    clearInput: () => clearInput(inputRef),
    ariaLabel: ariaLabel,
    inputRef: inputRef,
    styles: styles,
    renderLabel: false,
    clearButtonColor: colors.primary,
    autoFocus: true,
    inputClassName: inputClassName,
    required: required,
    isMobile: true,
    onValueChange: handleValueChange
  }), /*#__PURE__*/React.createElement(Suggestions, Object.assign({
    suggestions: state.suggestions,
    getMenuProps: getMenuProps,
    getItemProps: getItemProps,
    highlightedIndex: highlightedIndex,
    isOpen: true // when mobile view is open we always want to show suggestions
    ,
    hidden: false,
    lng: lng,
    styles: styles,
    renderClearHistoryButton: true,
    handleClearHistory: handleClearHistory
  }, suggestionProps)))), /*#__PURE__*/React.createElement(DialogModal, {
    isModalOpen: isDialogOpen,
    handleClose: () => setDialogOpen(false),
    headerText: t('delete-old-searches-header', {
      lng
    }),
    primaryButtonText: t('delete', {
      lng
    }),
    secondaryButtonText: t('cancel', {
      lng
    }),
    primaryButtonOnClick: () => {
      clearOldSearches();
      setDialogOpen(false);
    },
    secondaryButtonOnClick: () => setDialogOpen(false),
    lang: lng
  })));
};
process.env.NODE_ENV !== "production" ? MobileView.propTypes = {
  appElement: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  clearInput: PropTypes.func.isRequired,
  clearOldSearches: PropTypes.func.isRequired,
  onSelectedItemChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  colors: PropTypes.objectOf(PropTypes.string).isRequired,
  fontWeights: PropTypes.shape({
    medium: PropTypes.number.isRequired
  }).isRequired,
  suggestionProps: PropTypes.shape({}).isRequired,
  ariaProps: PropTypes.shape({
    ariaRequiredText: PropTypes.string.isRequired,
    SearchBarId: PropTypes.string.isRequired,
    ariaCurrentSuggestion: PropTypes.string.isRequired
  }).isRequired,
  inputClassName: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  mobileLabel: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  showScroll: PropTypes.bool.isRequired,
  lng: PropTypes.string.isRequired,
  state: PropTypes.shape({
    suggestions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    renderMobile: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  pendingEnterRef: PropTypes.shape({
    current: PropTypes.bool
  }).isRequired
} : void 0;

function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const searchReducer = (state, action) => {
  switch (action.type) {
    case 'INPUT_CHANGE':
      return _objectSpread$1(_objectSpread$1({}, state), {}, {
        value: action.value
      });
    case 'FETCH_SUGGESTIONS':
      return action.loading === state.loading && !action.suggestions ? state : _objectSpread$1(_objectSpread$1({}, state), {}, {
        loading: action.loading,
        suggestions: action.suggestions || state.suggestions
      });
    case 'CLEAR_SUGGESTIONS':
      return _objectSpread$1(_objectSpread$1({}, state), {}, {
        suggestions: []
      });
    case 'RESET_SOURCES':
      return _objectSpread$1(_objectSpread$1({}, state), {}, {
        sources: action.sources
      });
    case 'SET_SOURCES':
      return _objectSpread$1(_objectSpread$1({}, state), {}, {
        showOwnPlaces: action.showOwnPlaces,
        sources: action.sources,
        pendingSelection: action.pendingSelection
      });
    case 'CLEAR':
      return _objectSpread$1(_objectSpread$1({}, state), {}, {
        value: '',
        isCleared: true,
        suggestions: []
      });
    case 'INPUT_BLUR':
      {
        return _objectSpread$1(_objectSpread$1({}, state), {}, {
          value: action.value || state.value,
          isCleared: action.isMobile ? false : state.isCleared,
          pendingSelection: null,
          showOwnPlaces: false
        });
      }
    case 'RESET':
      {
        return action.initialState;
      }
    case 'TOGGLE_MENU':
      {
        if (action.isMobile) {
          if (!state.renderMobile) {
            return _objectSpread$1(_objectSpread$1({}, state), {}, {
              renderMobile: true,
              value: state.isCleared ? '' : state.value
            });
          }
          return _objectSpread$1(_objectSpread$1({}, state), {}, {
            renderMobile: false,
            isCleared: false
          });
        }
        return _objectSpread$1(_objectSpread$1({}, state), {}, {
          isMenuOpen: !state.isMenuOpen,
          renderMobile: action.isMobile && !state.renderMobile
        });
      }
    default:
      return state;
  }
};

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

// Hoisted so these defaults are referentially stable across renders. Using
// inline object/array literals as parameter defaults (e.g. `sources = []`)
// creates a brand-new reference on every render in which the caller omits
// the prop, which in turn invalidates every hook (useEffect/useCallback/
// useMemo) that depends on them - e.g. causing `fetchSuggestions` to be
// recreated and refetch on every render, and the `RESET_SOURCES` effect to
// fire in a loop.
const DEFAULT_SOURCES = [];
const DEFAULT_PATH_OPTS = {
  routesPrefix: 'linjat',
  stopsPrefix: 'pysakit'
};
const DEFAULT_REF_POINT = {};
const DEFAULT_FONT_WEIGHTS = {
  medium: 500
};
const getAriaProps = ({
  id,
  ariaLabel,
  lng,
  t,
  isMobile,
  suggestions,
  value,
  required
}) => {
  const ariaBarId = id.replace('searchfield-', '');
  const SearchBarId = ariaLabel || t(ariaBarId, {
    lng
  }).replace('searchfield-', '').concat('.'); // Full stop makes screen reader speech clearer.
  const ariaRequiredText = required ? `${t('required', {
    lng
  })}.` : '';
  const ariaLabelInstructions = isMobile ? t('search-autosuggest-label-instructions-mobile', {
    lng
  }) : t('search-autosuggest-label-instructions-desktop', {
    lng
  });
  const movingToDestinationFieldText = id === 'origin' ? t('search-autosuggest-label-move-to-destination', {
    lng
  }) : '';
  const ariaLabelText = ariaLabelInstructions.concat(' ').concat(movingToDestinationFieldText);
  const ariaSuggestionLen = t('search-autosuggest-len', {
    count: suggestions.length,
    lng
  });
  const ariaCurrentSuggestion = suggestionAsAriaContent({
    suggestions,
    t,
    lng
  }) || value ? t('search-current-suggestion', {
    lng,
    selection: suggestionAsAriaContent({
      suggestions,
      t,
      lng
    }).toLowerCase() || value
  }) : '';
  return {
    SearchBarId,
    ariaRequiredText,
    ariaLabelText,
    ariaSuggestionLen,
    ariaCurrentSuggestion
  };
};
const positions = ['Valittu sijainti', 'Nykyinen sijaintisi', 'Current position', 'Selected location', 'Vald position', 'Använd min position', 'Min position', 'Käytä nykyistä sijaintia', 'Use current location', 'Your current location', 'Wybrane miejsce'];

/**
 * Takes the targets and modifies them based on ownPlaces and isLocationSearch
 * @param {object} props
 * @param {string[]} props.targets
 * @param {boolean} props.isLocationSearch
 * @param {boolean} props.isMobile
 * @param {boolean} props.ownPlaces
 * @param {string[]} props.sources
 * @returns {string[]} newTargets
 */
const getNewTargets = ({
  targets,
  isLocationSearch,
  isMobile,
  ownPlaces,
  sources
}) => {
  const useAll = !targets?.length;
  // Default: neither the ownPlaces nor the "explicit, non-empty targets"
  // case applies (e.g. the common "leave targets empty to search
  // everything" usage) - pass targets through unchanged rather than
  // leaving newTargets undefined, which would crash downstream calls like
  // `targets.includes(...)` in getSearchResults.
  let newTargets = targets;
  if (ownPlaces) {
    newTargets = ['Locations'];
    if (useAll || targets.includes('Stops')) {
      newTargets.push('Stops');
    }
    if (useAll || targets.includes('Stations')) {
      newTargets.push('Stations');
    }
    if (useAll || targets.includes('VehicleRentalStations')) {
      newTargets.push('VehicleRentalStations');
    }
  } else if (!useAll) {
    newTargets = [...targets];
    // in desktop, favorites are accessed via sub search
    if (isLocationSearch && !isMobile && (!sources.length || sources.includes('Favourite'))) {
      newTargets.push('SelectFromOwnLocations');
    }
  }
  return newTargets;
};

/**
 * An autosuggest search input for finding locations, stops, stations, routes and vehicle rental stations.
 *
 * @example
 * const searchContext = {
 *   isPeliasLocationAware: false // true / false does Let Pelias suggest based on current user location
 *   minimalRegexp: undefined // used for testing min. regexp. For example: new RegExp('.{2,}'),
 *   lineRegexp: undefined //  identify searches for route numbers/labels: bus | train | metro. For example: new RegExp(
 *    //   '(^[0-9]+[a-z]?$|^[yuleapinkrtdz]$|(^m[12]?b?$))',
 *    //  'i',
 *    //  ),
 *   URL_PELIAS: '' // url for pelias searches
 *   feedIDs: ['HSL', 'HSLLautta'] // FeedId's like  [HSL, HSLLautta]
 *   geocodingSources: ['oa','osm','nlsfi']  // sources for geocoding
 *   geocodingSearchParams; {}  // Searchparmas fro geocoding
 *   getFavouriteLocations: () => ({}),    // Function that returns array of favourite locations.
 *   getFavouriteStops: () => ({}),        // Function that returns array of favourite stops.
 *   getLanguage: () => ({}),              // Function that returns current language.
 *   getFavouriteRoutes: () => ({}),       // Function that returns array of favourite routes.
 *   getPositions: () => ({}),             // Function that returns user's geolocation.
 *   getRoutesQuery: () => ({}),           // Function that returns query for fetching routes.
 *   getStopAndStationsQuery: () => ({}),  // Function that fetches favourite stops and stations from graphql API.
 *   getFavouriteRoutesQuery: () => ({}),  // Function that returns query for fetching favourite routes.
 *   getFavouriteVehicleRentalStations: () => ({}),  // Function that returns favourite bike rental station.
 *   getFavouriteVehicleRentalStationsQuery: () => ({}), // Function that returns query for fetching favourite bike rental stations.
 *   startLocationWatch: () => ({}),       // Function that locates users geolocation.
 *   saveSearch: () => ({}),               // Function that saves search to old searches store.
 *   clearOldSearches: () => ({}),         // Function that clears old searches store.
 *   getFutureRoutes: () => ({}),          // Function that return future routes
 *   saveFutureRoute: () => ({}),          // Function that saves a future route
 *   clearFutureRoutes: () => ({}),        // Function that clears future routes
 * };
 * const lang = 'fi'; // en, fi or sv
 * const onSelect = (item, id) => {
 *    // Funtionality when user selects a suggesions. No default implementation is given.
 *    return null;
 * };
 * const onClear = id => {
 *    // Called  when user clicks the clear search string button. No default implementation.
 *    return null;
 * };
 * const getAutoSuggestIcons: {
 *   // Called for every city bike station rendered as a search suggestion. Should return the icon and
 *   // color used for that station. Two icons are available, 'citybike-stop-digitransit' anditybike-stop-digitransit-secondary'.
 *   citybikes: station => {
 *      return ['citybike-stop-digitransit', '#f2b62d'];
 *   }
 * }
 * const transportMode = undefined;
 * const placeholder = "stop-near-you";
 * const targets = ['Locations', 'Stops', 'Routes']; // Defines what you are searching. Options are Locations, Stops, Stations, Routes, VehicleRentalStations, FutureRoutes, MapPosition and CurrentPosition. Leave empty to search all targets.
 * const sources = ['Favourite', 'History', 'Datasource'] // Defines where you are searching. all available are: Favourite, History (previously searched searches) and Datasource. Leave empty to use all sources.
 * return (
 *  <DTAutosuggest
 *    appElement={appElement} // Required. Root element's id. Needed for react-modal component.
 *    searchContext={searchContext}
 *    icon="origin" // Optional String for icon that is shown left of searchfield. used with Icon library
 *    id="origin" // used for style props and info for component.
 *    placeholder={placeholder} // String that is showns initally in search field
 *    value="" // e.g. user typed string that is shown in search field
 *    onSelect={onSelect}
 *    onClear={onClear}
 *    lang={lang}
 *    getAutoSuggestIcons={getAutoSuggestIcons}
 *    transportMode={transportMode} // transportmode with which we filter the routes, e.g. route-BUS
 *    geocodingSize={10} // defines how many stops and stations to fetch from geocoding. Useful if you want to filter the results and still get a reasonable amount of suggestions.
 *    filterResults={results => return results} // Optional filtering function for routes and stops
 *    handelViaPoints={() => return null } // Optional Via point handling logic. This is currently managed with DTAutosuggestpanel by default, but if DTAutosuggest is used seperatelly own implementation must be provided.
 *    focusChange={() => return null} // When suggestion is selected, handle changing focus. This is currently managed with DTAutosuggestpanel by default, but if DTAutosuggest is used seperatelly own implementation must be provided.
 *    storeRef={() => return null} // Functionality to store refs. Currenlty managed with DTAutosuggestpanel by default, but if DTAutosuggest is used seperatelly own implementation must be provided.
 *    sources={sources}
 *    targets={targets}
 *    isMobile  // Optional. Defaults to false. Whether to use mobile search.
 *    mobileLabel="Custom label" // Optional. Custom label text for autosuggest field on mobile.
 *    inputClassName="" // Optional. Custom classname applied to the input element of the component for providing CSS styles.
 *    translatedPlaceholder= // Optional. Custon translated placeholder text for autosuggest field.
 *
 * @typedef DTAutosuggestProps
 * @property {string} appElement
 * @property {string} id
 * @property {string} placeholder
 * @property {function} onSelect
 * @property {string} [icon]
 * @property {string} [value]
 * @property {function} [onClear]
 * @property {string} [lang]
 * @property {Object} [getAutoSuggestIcons]
 * @property {function} [handleViaPoints]
 * @property {function} [focusChange]
 * @property {function} [storeRef]
 * @property {boolean} [isMobile]
 * @property {string} [mobileLabel]
 * @property {string} [inputClassName]
 * @property {string} [translatedPlaceholder]
 * @property {boolean} [required]
 * @property {Object} [fontWeights]
 * @property {Object} [colors]
 * @property {string} [modeSet]
 * @property {boolean} [showScroll]
 * @property {boolean} [isEmbedded]
 * Geocoding related props
 * @property {Object} searchContext
 * @property {string} [transportMode]
 * @property {string[]} [targets]
 * @property {string[]} [sources]
 * @property {number} [geocodingSize]
 * @property {function} [filterResults]
 * @property {Object} [pathOpts]
 * @property {Object} [refPoint]
 * @param {DTAutosuggestProps} props
 * @param {string[]} [props.sources=[]]
 * @param {Object} [props.pathOpts={routesPrefix: 'linjat', stopsPrefix: 'pysakit'}]
 * @param {Object} [props.refPoint={}]
 * @param {Object} [props.fontWeights={medium: 500}]
 * @returns {JSX.Element}
 */
function DTAutosuggest({
  appElement,
  id,
  placeholder,
  onSelect,
  icon,
  value = '',
  onClear,
  lang: lng = 'fi',
  getAutoSuggestIcons,
  handleViaPoints,
  focusChange,
  storeRef,
  isMobile = false,
  mobileLabel,
  inputClassName = '',
  translatedPlaceholder,
  required = false,
  ariaLabel,
  fontWeights = DEFAULT_FONT_WEIGHTS,
  colors = defaultColors,
  modeSet,
  showScroll = false,
  isEmbedded = false,
  transportMode,
  targets,
  sources = DEFAULT_SOURCES,
  geocodingSize,
  filterResults,
  searchContext,
  pathOpts = DEFAULT_PATH_OPTS,
  refPoint = DEFAULT_REF_POINT
}) {
  const [t] = useTranslation();
  const initialState = {
    suggestions: [],
    loading: false,
    sources,
    showOwnPlaces: false,
    pendingSelection: null,
    isCleared: false,
    renderMobile: false,
    value,
    isMenuOpen: false
  };
  const [state, dispatch] = useReducer(searchReducer, initialState);
  // Reset the state when value or sources change, this also triggers a new search
  useEffect(() => dispatch({
    type: 'RESET',
    initialState
  }), [value]);
  useEffect(() => dispatch({
    type: 'RESET_SOURCES',
    sources
  }), [sources]);
  // create and store input ref in the parent if storeRef is provided
  const inputRef = useRef(id);
  // Tracks whether the user pressed enter/clicked an item while suggestions
  // were still loading; a ref (rather than reducer state) is used because
  // setting it is a pure bookkeeping side effect of the stateReducer below,
  // not something that should itself trigger a render or round-trip through
  // another dispatch.
  const pendingEnterRef = useRef(false);
  useEffect(() => {
    if (storeRef) {
      storeRef(inputRef.current);
    }
  }, [inputRef.current]);
  const fetchSuggestions = useCallback(() => {
    const useAll = !targets?.length;
    const isLocationSearch = useAll || targets.includes('Locations');
    const newTargets = getNewTargets({
      targets,
      isLocationSearch,
      isMobile,
      ownPlaces: state.showOwnPlaces,
      sources: state.sources
    });
    // remove  location favourites in desktop search (collection item replaces it in target array)
    const newSources = state.sources ? state.sources.filter(s => !isLocationSearch || !s === 'Favourite' || state.showOwnPlaces || isMobile) : state.sources;
    executeSearch(newTargets, newSources, transportMode, searchContext, filterResults, geocodingSize, {
      input: state.value || ''
    }, searchResult => {
      if (searchResult == null) {
        dispatch({
          type: 'FETCH_SUGGESTIONS',
          loading: true
        });
        return;
      }
      const newSuggestions = (searchResult.results || []).filter(suggestion => suggestion.type !== 'FutureRoute' || suggestion.type === 'FutureRoute' && suggestion.properties.time > Date.now() / 1000).map(suggestion => {
        if (suggestion.type === 'CurrentLocation' || suggestion.type === 'SelectFromMap' || suggestion.type === 'SelectFromOwnLocations' || suggestion.type === 'back') {
          const translatedSuggestion = _objectSpread({}, suggestion);
          translatedSuggestion.properties.labelId = t(suggestion.properties.labelId, {
            lng
          });
          return translatedSuggestion;
        }
        return suggestion;
      });
      dispatch({
        type: 'FETCH_SUGGESTIONS',
        loading: false,
        suggestions: newSuggestions
      });
    }, pathOpts, refPoint);
  }, [state.value, state.sources, state.showOwnPlaces, targets, transportMode, searchContext, filterResults, geocodingSize, isMobile, lng, pathOpts, refPoint, id]);
  const selectSuggestion = useCallback((suggestion, index) => {
    if (!suggestion) {
      return;
    }
    if (handleViaPoints) {
      handleViaPoints(suggestion, index);
    } else {
      onSelect(suggestion, id);
    }
    if (focusChange && (!isMobile || isEmbedded)) {
      focusChange();
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }, [isMobile]);
  const onSelectedItemChange = changes => {
    const {
      selectedItem
    } = changes;
    if (!selectedItem) {
      return;
    }
    // These are sentinel navigation items, not real suggestions: react to
    // them here (the designated place for side effects) instead of from
    // inside the stateReducer, which should stay a pure function computing
    // downshift's next state.
    if (selectedItem.type === 'SelectFromOwnLocations') {
      dispatch({
        type: 'SET_SOURCES',
        sources: ['Favourite', 'Back'],
        showOwnPlaces: true,
        pendingSelection: selectedItem.type
      });
      return;
    }
    if (selectedItem.type === 'back') {
      dispatch({
        type: 'SET_SOURCES',
        sources,
        showOwnPlaces: false,
        pendingSelection: null
      });
      return;
    }
    selectSuggestion(selectedItem, changes.highlightedIndex);
  };
  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    selectItem,
    openMenu,
    closeMenu
  } = useCombobox({
    inputId: id,
    inputValue: state.value,
    defaultHighlightedIndex: 0,
    // Typing is dispatched synchronously by Input. Downshift reports it from an
    // effect a render late, which would overwrite newer keystrokes.
    onInputValueChange: ({
      inputValue,
      type
    }) => type !== useCombobox.stateChangeTypes.InputChange && dispatch({
      type: 'INPUT_CHANGE',
      value: inputValue
    }),
    stateReducer: useCallback((oldState, {
      type,
      changes
    }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          {
            // If suggestions are still loading, remember that a selection was
            // requested and let the effect below make it once loading
            // finishes - see the "pending enter" effect near closeMenu().
            if (state.loading) {
              pendingEnterRef.current = true;
              return oldState;
            }
            if (!changes.selectedItem) {
              return changes;
            }
            if (changes.selectedItem.type === 'SelectFromOwnLocations' || changes.selectedItem.type === 'back') {
              // Keep the menu open for these sentinel "navigation" items
              // instead of closing/committing a normal selection. The
              // source-switching side effect itself runs in
              // onSelectedItemChange once downshift commits this change.
              return _objectSpread(_objectSpread({}, changes), {}, {
                isOpen: true
              });
            }
            return changes;
          }
        case useCombobox.stateChangeTypes.InputClick:
          {
            // clear input if current position or selected location is shown
            if (positions.includes(value)) {
              return _objectSpread(_objectSpread({}, changes), {}, {
                inputValue: '',
                isOpen: true
              });
            }
            return _objectSpread(_objectSpread({}, changes), {}, {
              isOpen: true
            });
          }
        case useCombobox.stateChangeTypes.InputBlur:
          {
            if (changes.selectedItem) {
              return _objectSpread(_objectSpread({}, changes), {}, {
                selectedItem: oldState.selectedItem,
                inputValue: oldState.inputValue
              });
            }
            return changes;
          }
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          {
            return _objectSpread(_objectSpread({}, changes), {}, {
              inputValue: ''
            });
          }
        default:
          {
            return changes;
          }
      }
    }, [state.loading, value, isMobile]),
    items: state.suggestions,
    itemToString(suggestion) {
      return suggestion ? getSuggestionValue(suggestion) : '';
    },
    onSelectedItemChange,
    onIsOpenChange: changes => {
      if (changes.isOpen && !state.renderMobile) {
        dispatch({
          type: 'TOGGLE_MENU',
          isMobile
        });
      }
    }
  });
  const inputOnBlur = () => {
    dispatch({
      isMobile,
      value: !isMobile && value,
      type: 'INPUT_BLUR'
    });
    if (!isMobile) {
      dispatch({
        type: 'RESET',
        initialState
      });
    }
  };
  const clearInput = ref => {
    dispatch({
      type: 'CLEAR'
    });
    if (onClear) {
      onClear(id);
    }
    if (ref.current) {
      ref.current.focus();
    }
    openMenu();
  };
  const handleValueChange = useCallback(newValue => dispatch({
    type: 'INPUT_CHANGE',
    value: newValue
  }), []);

  // Fetch suggestions when isOpen, value, or fetchSuggestions dependencies change
  useEffect(() => {
    // Don't search when the search field (state.value) contains position strings that were given as a prop (value),
    // because they are UI labels not real search terms. Fetching suggestions with them caused a quickly flashing set of
    // irrelevant results, e.g. 'Käytä nykyistä sijaintia' -> 'City-käytävä'.
    if ((isOpen || state.renderMobile) && !(state.value === value && positions.includes(value))) {
      fetchSuggestions(state.value);
    }
  }, [isOpen, state.renderMobile, state.value, fetchSuggestions]);

  // this effect handles selecting the suggestion when enter was pressed but suggestions were still loading
  useEffect(() => {
    if (pendingEnterRef.current && !state.loading) {
      pendingEnterRef.current = false;
      selectSuggestion(state.suggestions[0], 0);
      closeMenu();
    }
  }, [state.loading, state.suggestions]);
  const baseItemProps = useMemo(() => ({
    loading: state.loading,
    isMobile,
    ariaFavouriteString: t('favourite', {
      lng
    }),
    fontWeights,
    getAutoSuggestIcons,
    colors,
    modeSet
  }), [state.loading, isMobile, t, lng, fontWeights, getAutoSuggestIcons, colors, modeSet]);
  const {
    ariaCurrentSuggestion,
    ariaRequiredText,
    SearchBarId,
    ariaLabelText
  } = getAriaProps({
    id,
    lng,
    t,
    isMobile,
    ariaLabel,
    suggestions: state.suggestions,
    value: state.value,
    required
  });
  const mobileClearOldSearches = () => {
    const {
      context,
      clearOldSearches,
      clearFutureRoutes
    } = searchContext;
    if (context && clearOldSearches) {
      clearOldSearches(context);
      if (clearFutureRoutes) {
        clearFutureRoutes(context);
      }
      fetchSuggestions(state.value);
    }
  };
  useEffect(() => {
    if (!state.renderMobile && !isOpen) {
      dispatch({
        type: 'RESET',
        initialState
      });
    }
  }, [state.renderMobile, isOpen]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isMobile && /*#__PURE__*/React.createElement(MobileView, {
    placeholder: t(placeholder, {
      lng
    }),
    fontWeights: fontWeights,
    clearOldSearches: mobileClearOldSearches,
    appElement: appElement,
    mobileLabel: mobileLabel || t(id, {
      lng
    }),
    ariaProps: {
      ariaCurrentSuggestion,
      SearchBarId,
      ariaRequiredText
    },
    id: id,
    lng: lng,
    onSelectedItemChange: onSelectedItemChange,
    value: state.value,
    clearInput: clearInput,
    suggestionProps: baseItemProps,
    showScroll: !!showScroll,
    colors: colors,
    inputClassName: inputClassName,
    required: required,
    state: state,
    dispatch: dispatch,
    pendingEnterRef: pendingEnterRef
  }), /*#__PURE__*/React.createElement("div", {
    className: cx(styles['autosuggest-input-container'], styles[id], state.renderMobile && 'hidden'),
    style: {
      '--color': colors.primary,
      '--hover-color': colors.hover
    }
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: cx(styles['autosuggest-input-icon'], styles[id], inputClassName && styles[`${inputClassName}-input-icon`]),
    "aria-label": ariaRequiredText.concat(' ').concat(SearchBarId).concat(' ').concat(t('search-autosuggest-label', {
      lng
    }))
  }, /*#__PURE__*/React.createElement(Icon, {
    img: `${icon}`
  })), /*#__PURE__*/React.createElement(Input, {
    inputClassName: inputClassName,
    ariaLabel: ariaCurrentSuggestion.concat(' ').concat(ariaRequiredText).concat(' ').concat(SearchBarId).concat(' ').concat(ariaLabelText),
    id: id,
    lng: lng,
    getInputProps: getInputProps,
    getLabelProps: getLabelProps,
    selectItem: selectItem,
    value: state.value,
    clearInput: clearInput,
    inputRef: inputRef,
    styles: styles,
    clearButtonColor: colors.primary,
    placeholder: translatedPlaceholder || t(placeholder, {
      lng
    }),
    required: required,
    transportMode: transportMode,
    isMobile: isMobile,
    inputOnBlur: inputOnBlur,
    onValueChange: handleValueChange
  }), /*#__PURE__*/React.createElement(Suggestions, Object.assign({
    hidden: !isOpen || !state.suggestions.length || isMobile,
    highlightedIndex: highlightedIndex,
    getItemProps: getItemProps,
    getMenuProps: getMenuProps,
    suggestions: state.suggestions,
    lng: lng,
    styles: styles
  }, baseItemProps))));
}
process.env.NODE_ENV !== "production" ? DTAutosuggest.propTypes = {
  appElement: PropTypes.string.isRequired,
  icon: PropTypes.string,
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  translatedPlaceholder: PropTypes.string,
  value: PropTypes.string,
  transportMode: PropTypes.string,
  geocodingSize: PropTypes.number,
  filterResults: PropTypes.func,
  searchContext: PropTypes.shape({
    URL_PELIAS: PropTypes.string,
    // eslint-disable-next-line
    context: PropTypes.object,
    clearOldSearches: PropTypes.func,
    clearFutureRoutes: PropTypes.func
  }).isRequired,
  sources: PropTypes.arrayOf(PropTypes.string),
  targets: PropTypes.arrayOf(PropTypes.string),
  ariaLabel: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  storeRef: PropTypes.func,
  handleViaPoints: PropTypes.func,
  focusChange: PropTypes.func,
  lang: PropTypes.string,
  isMobile: PropTypes.bool,
  pathOpts: PropTypes.shape({
    routesPrefix: PropTypes.string,
    stopsPrefix: PropTypes.string
  }),
  mobileLabel: PropTypes.string,
  refPoint: PropTypes.shape({
    address: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number
  }),
  inputClassName: PropTypes.string,
  fontWeights: PropTypes.shape({
    medium: PropTypes.number
  }),
  colors: PropTypes.objectOf(PropTypes.string),
  getAutoSuggestIcons: PropTypes.objectOf(PropTypes.func),
  required: PropTypes.bool,
  modeSet: PropTypes.string,
  showScroll: PropTypes.bool,
  isEmbedded: PropTypes.bool
} : void 0;
var index = props => {
  return /*#__PURE__*/React.createElement(I18nextProvider, {
    i18n: i18n
  }, /*#__PURE__*/React.createElement(DTAutosuggest, props));
};

export { index as default };
//# sourceMappingURL=index.js.map
